import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactDOM from 'react-dom/client';
import landingExample from './data/landing-example.json';
import { Search, MapPin, Store, Building2, Flame, Target, AlertTriangle, Download, FileText, Info, X, LogOut, User as UserIcon, Mail, Loader2, Wallet, Sparkles, ChevronDown, PlusCircle, MinusCircle, ThumbsUp, ThumbsDown, Send, CheckCircle2, Printer, Eye, EyeOff, Layers, Satellite, RefreshCw, Lightbulb, Clock } from 'lucide-react';
import { initAnalytics, setAnalyticsUserId, trackAmplitudeEvent } from './analytics';
import { isAuditBrowser } from './auditBrowser';
import {
  chatGPTAttribution,
  trackChatGPTRegistration,
  trackChatGPTSubscription,
} from './chatgptAds';
import {
  GOOGLE_CONSENT_OPEN_EVENT,
  GOOGLE_CONSENT_STORAGE_KEY,
  type GoogleConsentChoice,
  clearGoogleUserData,
  ensureGoogleTags,
  sendPurchasePageView,
  setGoogleUserData,
  trackGooglePurchase,
  trackGoogleSignUp,
  updateGoogleConsent,
} from './googleTag';
import type { MarketReportInput, ReportPlace } from './reportPdf';
import { EvaluateLocalStart, useEvalConfig } from './evaluateLocalStart';
import type { EvalDeps, EvalStartContext } from './evaluateLocal';
import { ToolDisclaimerAccept } from './toolDisclaimer';
import './styles.css';

const EvaluateLocalFlow = React.lazy(() => import('./evaluateLocal').then(mod => ({ default: mod.EvaluateLocalFlow })));
const SharedEvaluationPage = React.lazy(() => import('./evaluateLocal').then(mod => ({ default: mod.SharedEvaluationPage })));
const OutcomeSurvey = React.lazy(() => import('./evaluateLocal').then(mod => ({ default: mod.OutcomeSurvey })));

if (typeof window !== 'undefined') {
  const amplitudeWindow = window as Window & { __RADARBI_AMPLITUDE_INITIALIZED__?: boolean };
  if (!amplitudeWindow.__RADARBI_AMPLITUDE_INITIALIZED__) {
    amplitudeWindow.__RADARBI_AMPLITUDE_INITIALIZED__ = true;
    initAnalytics();
  }
}

// ============================================================
// AUTH TYPES & HELPERS
// ============================================================
type AuthUser = { id: string; email: string; emailVerified: boolean; name?: string; avatarUrl?: string; role: string; createdAt?: string; updatedAt?: string };
type AuthResponse = {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  accessTokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
  user: AuthUser;
  isNewUser?: boolean;
  chatgptEventId?: string;
};
type AuthLoginMethod = 'email' | 'google';

const ACCESS_TOKEN_KEY = 'radarbi_token';
const REFRESH_TOKEN_KEY = 'radarbi_refresh_token';
const ACCESS_TOKEN_EXPIRES_KEY = 'radarbi_token_expires_at';
const REFRESH_TOKEN_EXPIRES_KEY = 'radarbi_refresh_token_expires_at';
const TOKEN_EXPIRY_SKEW_MS = 30_000;
const SIGNUP_OFFER_CREDITS = 100;
const SIGNUP_OFFER_PROPS = {
  offer_credits: SIGNUP_OFFER_CREDITS,
  product_layers: 'competition,who_brings_customers,peak_hour',
  audience: 'open_local,another_location,about_to_sign',
} as const;
let refreshSessionPromise: Promise<AuthResponse | null> | null = null;

function isStoredTokenExpired(key: string): boolean {
  const value = localStorage.getItem(key);
  if (!value) return false;
  const expiresAt = Date.parse(value);
  if (Number.isNaN(expiresAt)) return false;
  return Date.now() + TOKEN_EXPIRY_SKEW_MS >= expiresAt;
}

function getAuthToken(): string | null {
  if (isStoredTokenExpired(ACCESS_TOKEN_EXPIRES_KEY)) return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function getRawAuthToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function getRefreshToken(): string | null {
  if (isStoredTokenExpired(REFRESH_TOKEN_EXPIRES_KEY)) {
    clearAuthToken();
    return null;
  }
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function setAuthTokens(accessToken: string, refreshToken?: string, accessTokenExpiresAt?: string, refreshTokenExpiresAt?: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (accessTokenExpiresAt) localStorage.setItem(ACCESS_TOKEN_EXPIRES_KEY, accessTokenExpiresAt);
  if (refreshTokenExpiresAt) localStorage.setItem(REFRESH_TOKEN_EXPIRES_KEY, refreshTokenExpiresAt);
}

function persistAuthResponse(data: AuthResponse) {
  const accessToken = data.accessToken || data.token;
  if (!accessToken) throw new Error('No se recibió token de sesión');
  setAuthTokens(accessToken, data.refreshToken, data.accessTokenExpiresAt, data.refreshTokenExpiresAt);
}

function clearAuthToken() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_EXPIRES_KEY);
  localStorage.removeItem(REFRESH_TOKEN_EXPIRES_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  if (token) return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  return { 'Content-Type': 'application/json' };
}

async function refreshAccessToken(): Promise<boolean> {
  return Boolean(await refreshAuthSession());
}

function isAuthRejected(status: number) {
  return status === 401 || status === 403;
}

async function refreshAuthSession(): Promise<AuthResponse | null> {
  if (refreshSessionPromise) return refreshSessionPromise;

  refreshSessionPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refreshToken })
      });
    } catch {
      return null;
    }
    if (!res.ok) {
      if (isAuthRejected(res.status)) clearAuthToken();
      return null;
    }
    const data: AuthResponse = await res.json();
    persistAuthResponse(data);
    return data;
  })();

  try {
    return await refreshSessionPromise;
  } finally {
    refreshSessionPromise = null;
  }
}

async function apiFetch(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers = { ...authHeaders(), ...(init.headers as Record<string, string> | undefined) };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });
  if (res.status === 401 && retry && await refreshAccessToken()) {
    return apiFetch(path, init, false);
  }
  return res;
}

// Google Sign-In script loader
function loadGoogleScript(): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById('google-signin-script')) { resolve(); return; }
    const s = document.createElement('script');
    s.id = 'google-signin-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

// ============================================================
// FLOATING LOGIN CARD (top-right, minimizable, non-blocking)
// ============================================================
const OTP_RESEND_SECONDS = 30;

function FloatingLoginCard({
  onLogin,
  onClose,
  expanded,
  attentionKey,
}: {
  onLogin: (user: AuthUser, token: string, method: AuthLoginMethod, options?: { isNewUser?: boolean; loginEventId?: string; registrationEventId?: string; chatgptEventId?: string }) => void;
  onClose: () => void;
  expanded: boolean;
  attentionKey: number;
}) {
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [attention, setAttention] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const googleClientId = (window as any).__RADARBI_GOOGLE_CLIENT_ID;
    if (!googleClientId || !expanded) return;
    loadGoogleScript().then(() => {
      const google = (window as any).google;
      if (!google?.accounts?.id) return;
      google.accounts.id.initialize({ client_id: googleClientId, callback: handleGoogleResponse });
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        google.accounts.id.renderButton(googleBtnRef.current, { theme: 'outline', size: 'large', width: 300, text: 'continue_with', shape: 'pill' });
      }
    });
  }, [expanded]);

  useEffect(() => {
    if (!expanded || attentionKey === 0) return;
    setAttention(false);
    const frame = window.requestAnimationFrame(() => setAttention(true));
    const timer = window.setTimeout(() => setAttention(false), 900);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [attentionKey, expanded]);

  async function handleGoogleResponse(response: any) {
    setLoading(true); setError('');
    try {
      const loginEventId = createTikTokEventId('login', 'google');
      const registrationEventId = createTikTokEventId('registration', 'google');
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: response.credential, tiktokLoginEventId: loginEventId, tiktokRegistrationEventId: registrationEventId, ...chatGPTAttribution() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Google auth failed');
      persistAuthResponse(data);
      onLogin(data.user, data.token, 'google', { isNewUser: data.isNewUser, loginEventId, registrationEventId, chatgptEventId: data.chatgptEventId || '' });
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  async function sendOTP(resend = false) {
    if (!email || !email.includes('@')) { setError('Ingresa un email válido'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/otp/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code');
      setStep('otp'); setOtp(''); setResendIn(OTP_RESEND_SECONDS);
      setSuccessMsg(resend
        ? 'Te enviamos un código nuevo. Usa el último que llegue; revisa también spam o promociones.'
        : 'Código enviado. Verifica tu correo para continuar.');
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function verifyOTP() {
    if (!otp || otp.length < 6) { setError('Ingresa el código de 6 dígitos'); return; }
    setLoading(true); setError('');
    try {
      const loginEventId = createTikTokEventId('login', 'email');
      const registrationEventId = createTikTokEventId('registration', 'email');
      const res = await fetch(`${API_BASE}/api/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: otp, tiktokLoginEventId: loginEventId, tiktokRegistrationEventId: registrationEventId, ...chatGPTAttribution() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      persistAuthResponse(data);
      onLogin(data.user, data.token, 'email', { isNewUser: data.isNewUser, loginEventId, registrationEventId, chatgptEventId: data.chatgptEventId || '' });
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  if (!expanded) return null;

  return (
    <div className={`floating-login-card ${attention ? 'floating-login-card-attention' : ''}`}>
      {/* Close button */}
      <button type="button" onClick={onClose} aria-label="Cerrar inicio de sesión" style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: '4px', height: 'auto' }}><X size={16} aria-hidden="true" /></button>

      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <img src="/logo.webp" alt="RadarBI" width={66} height={44} decoding="async" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
        {attention && <div className="login-attention-pill">Inicia sesión para continuar</div>}
        <p className="login-credit-offer">Regístrate y gana 100 créditos gratis</p>
        <p style={{ color: '#64748b', fontSize: '12px', margin: '4px 0 0' }}>Entra con tu correo. Si eres nuevo, crearemos tu cuenta.</p>
      </div>

      {step === 'email' ? (
        <>
          <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0' }}>
            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            <span style={{ color: '#475569', fontSize: '11px' }}>o con email</span>
            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
          </div>
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <Mail size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input type="email" id="login-email" name="email" autoComplete="email" aria-label="Correo electrónico" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendOTP()} placeholder="tu@email.com"
              style={{ width: '100%', height: '40px', border: '1.5px solid #e2e8f0', borderRadius: '10px', paddingLeft: '34px', fontSize: '14px', background: '#f8fafc', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <button onClick={() => sendOTP()} disabled={loading || !email}
            style={{ width: '100%', height: '40px', background: 'linear-gradient(135deg, #0a2540, #1e3a5f)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: loading || !email ? 0.7 : 1 }}>
            {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Mail size={14} />}
            {loading ? 'Enviando...' : 'Continuar'}
          </button>
        </>
      ) : (
        <>
          <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 10px', textAlign: 'center' }}>
            Verifica tu correo para continuar:<br/><strong>{email}</strong>
          </p>
          <input type="text" id="login-otp" name="one-time-code" inputMode="numeric" autoComplete="one-time-code" aria-label="Código de verificación de 6 dígitos" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={e => e.key === 'Enter' && verifyOTP()} placeholder="000000" maxLength={6}
            style={{ width: '100%', height: '48px', border: '1.5px solid #e2e8f0', borderRadius: '10px', textAlign: 'center', fontSize: '24px', fontWeight: 800, letterSpacing: '6px', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', marginBottom: '8px' }} />
          <button onClick={verifyOTP} disabled={loading || otp.length < 6}
            style={{ width: '100%', height: '40px', background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: loading || otp.length < 6 ? 0.7 : 1 }}>
            {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
            {loading ? 'Verificando...' : 'Verificar código'}
          </button>
          <p style={{ color: '#64748b', fontSize: '12px', margin: '10px 0 0', textAlign: 'center' }}>
            ¿No te llegó?{' '}
            <button type="button" onClick={() => sendOTP(true)} disabled={loading || resendIn > 0}
              style={{ background: 'none', border: 'none', padding: 0, height: 'auto', color: resendIn > 0 ? '#94a3b8' : '#0ea5e9', cursor: resendIn > 0 ? 'default' : 'pointer', fontSize: '12px', fontWeight: 700, textDecoration: resendIn > 0 ? 'none' : 'underline', display: 'inline' }}>
              {resendIn > 0 ? `Reenviar código en ${resendIn} s` : 'Reenviar código'}
            </button>
          </p>
          <button onClick={() => { setStep('email'); setOtp(''); setError(''); setSuccessMsg(''); setResendIn(0); }}
            style={{ background: 'none', border: 'none', color: '#0ea5e9', cursor: 'pointer', fontSize: '12px', marginTop: '6px', textDecoration: 'underline', width: '100%', textAlign: 'center' }}>Cambiar email</button>
        </>
      )}
      {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: '8px 10px', borderRadius: '8px', fontSize: '12px', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}><AlertTriangle size={12} />{error}</div>}
      {successMsg && !error && <div style={{ background: '#f0fdf4', color: '#166534', padding: '8px 10px', borderRadius: '8px', fontSize: '12px', marginTop: '10px' }}>{successMsg}</div>}
    </div>
  );
}

// ============================================================
// AUTH WRAPPER (non-blocking — app always visible, login floats)
// ============================================================
function AuthGate({ children }: { children: (user: AuthUser | null, logout: () => void, requireAuth: () => boolean) => React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(() => Boolean(getRawAuthToken() || localStorage.getItem(REFRESH_TOKEN_KEY)));
  const [showLogin, setShowLogin] = useState(false);
  const [loginAttentionKey, setLoginAttentionKey] = useState(0);

  useEffect(() => {
    setAnalyticsUserId(user?.id);
    // Enhanced conversions need the account email on the page before any conversion
    // hit, and the checkout can start on the very next click.
    if (user?.email) setGoogleUserData({ email: user.email });
    else clearGoogleUserData();
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const token = getAuthToken();
        const rawToken = getRawAuthToken();

        if (token) {
          let res: Response | null = null;
          try {
            res = await apiFetch('/api/auth/me', {}, false);
          } catch {
            return;
          }
          if (res.ok) {
            const data = await res.json();
            if (!cancelled) setUser(data.user);
            return;
          }
          if (!isAuthRejected(res.status)) return;
        }

        if (rawToken || getRefreshToken()) {
          const refreshed = await refreshAuthSession();
          if (refreshed?.user && !cancelled) {
            setUser(refreshed.user);
            return;
          }
          if (getRawAuthToken() || localStorage.getItem(REFRESH_TOKEN_KEY)) return;
        }

        clearAuthToken();
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleLogin(u: AuthUser, _token: string, method: AuthLoginMethod, options?: { isNewUser?: boolean; loginEventId?: string; registrationEventId?: string; chatgptEventId?: string }) {
    setAnalyticsUserId(u.id);
    setUser(u);
    setShowLogin(false);
    trackAmplitudeEvent(options?.isNewUser ? 'Registration Completed' : 'Login Completed', options?.isNewUser
      ? { method, ...SIGNUP_OFFER_PROPS }
      : { method });
    trackTikTokAuthSuccess(u, method, options);
    if (options?.isNewUser && options.registrationEventId) {
      trackGoogleSignUp({
        userId: u.id,
        method,
        email: u.email,
        offerCredits: SIGNUP_OFFER_CREDITS,
      });
    }
    if (options?.isNewUser && options.chatgptEventId) {
      void trackChatGPTRegistration(options.chatgptEventId, { email: u.email, userId: u.id });
    }
  }

  function handleLogout() {
    const token = getAuthToken();
    const refreshToken = getRefreshToken();
    if (token || refreshToken) {
      apiFetch('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken })
      }).catch(() => {});
    }
    clearAuthToken();
    setUser(null);
    trackAmplitudeEvent('Logout Completed');
  }

  function openLogin(attention = false) {
    setShowLogin(true);
    if (attention) {
      setLoginAttentionKey(key => key + 1);
    }
  }

  // Call this before any protected action. Returns true if authenticated, false if not (and opens login).
  function requireAuth(): boolean {
    if (user) return true;
    openLogin(true);
    return false;
  }

  if (checking) {
    return (
      <div className="boot-screen">
        <img src="/logo.webp" alt="RadarBI" width={150} height={100} fetchPriority="high" decoding="async" />
        <div className="loader-spinner" />
        <p>Cargando tu radar comercial...</p>
      </div>
    );
  }

  return (
    <>
      {children(user, handleLogout, requireAuth)}
      {!user && showLogin && <div className="auth-focus-backdrop" aria-hidden="true" />}
      {!user && <FloatingLoginCard onLogin={handleLogin} onClose={() => setShowLogin(false)} expanded={showLogin} attentionKey={loginAttentionKey} />}
      {/* Small auth actions when not logged in and card is closed */}
      {!user && !showLogin && (
        <div className="auth-actions">
          <button
            onClick={() => openLogin(true)}
            className="auth-action auth-action-primary"
          >
            <UserIcon size={14} /> Iniciar sesión
          </button>
          <button
            onClick={() => {
              trackAmplitudeEvent('Signup Offer Clicked', { source: 'auth_actions', ...SIGNUP_OFFER_PROPS });
              trackTikTokEvent('ClickButton', {
                description: 'Regístrate y gana 100 créditos gratis',
                content_name: 'signup_offer',
                content_category: 'account',
                ...SIGNUP_OFFER_PROPS,
              });
              trackMarketingDataLayer('radarbi_signup_offer_clicked', { source: 'auth_actions', ...SIGNUP_OFFER_PROPS });
              openLogin(true);
            }}
            className="auth-action auth-action-secondary"
          >
            <Mail size={14} /> Registrarse
          </button>
        </div>
      )}
    </>
  );
}

type Place = { id:string; name:string; source:string; category:string; lat:number; lng:number; address?:string; rating?:number; reviews?:number; estimatedTrafficScore?:number; buildingType?:string; buildingLevels?:number };
type RentalProperty = { title: string; price: string; area: string; source: string; url: string; lat?: number; lng?: number };
type Zone = { id:string; name:string; district:string; lat:number; lng:number; score:number; competitorCount:number; partnerCount:number; demandScore:number; notes:string[]; purchasingPower?:string; commercialRent?:string; recommendation?:string; populationDensity?:string; availableRentals?:RentalProperty[] };
type PopulationStats = { residentialBuildings:number; apartmentBuildings:number; houses:number; otherResidential:number; floorsKnown:number; totalKnownFloors:number; averageKnownFloors:number };
type FootTrafficTimeSlot = { label:string; score:number; level:'alta'|'media'|'baja'|string };
type FootTrafficSignalMix = { residential:number; commercial:number; transit:number; pedestrian:number; knownFloors:number; residentialCapacity:number };
type FootTrafficSegment = { id:string; name:string; path:{lat:number; lng:number}[]; score:number; level:'alta'|'media'|'baja'|string; reason:string; peakHours?:string[]; timeProfile?:FootTrafficTimeSlot[]; signalMix?:FootTrafficSignalMix; dominantPattern?:string; nearbyResidential:number; nearbyCommercial:number; nearbyTransit:number; nearbyPedestrian:number; knownFloorsNearby:number; residentialCapacity:number };
type FootTrafficResponse = { available?: boolean; segments: FootTrafficSegment[]; populationData?: Place[]; populationStats?: PopulationStats; summary: { high:number; medium:number; low:number; averageScore:number; busiestWindow?:string; peakHours?:string[]; dominantPattern?:string; residentialSignals:number; commercialSignals:number; transitSignals:number; pedestrianSignals:number }; warnings?: string[] };
type ApiResponse = { places: Place[]; partners: Place[]; populationData?: Place[]; populationStats?: PopulationStats; opportunityZones: Zone[]; summary: { totalCompetitors:number; totalPartners:number; avgRating:number; avgReviews:number; bestDistricts:string[]; saturatedDistricts:string[] }; warnings?: string[] };
type MapLayerKey = 'competitors' | 'partnerHeat' | 'partnerPoints' | 'rentals' | 'zones';
type MapLayerVisibility = Record<MapLayerKey, boolean>;
type LocationOption = { id: string; name: string; address?: string; lat?: number; lng?: number; source?: string; types?: string[] };
type SelectedLocation = { id: string; name: string; address?: string; lat: number; lng: number; source?: string; types?: string[] };
type TopUp = { code: string; amountCents: number; credits: number };
type TikTokPayloadValue = string | number | boolean | string[] | number[] | Record<string, string | number | boolean | undefined> | Array<Record<string, string | number | boolean | undefined>>;
type TikTokEventPayload = Record<string, TikTokPayloadValue | null | undefined>;
type CreditLedgerEntry = { id: string; delta: number; balanceAfter: number; reason: string; action?: string; metadata?: Record<string, any>; createdAt: string };
type SavedSearchSummary = {
  id: string;
  kind: string;
  query?: string;
  partners?: string;
  district?: string;
  center?: { lat: number; lng: number };
  radiusMeters?: number;
  creditsCharged: number;
  resultsCount: number;
  detailed: boolean;
  repeatable?: boolean;
  createdAt: string;
};
type SavedSearch = SavedSearchSummary & { result: any };
type SearchFeedbackRating = 'good' | 'bad';
type SearchFeedbackStatus = 'idle' | 'sending' | 'sent' | 'error';
type ComplaintStatus = 'idle' | 'sending' | 'sent' | 'error';
type ComplaintTrackingStatus = 'idle' | 'loading' | 'found' | 'error';
type SearchFeedbackContext = {
  searchId: string;
  action: string;
  query: string;
  category: string;
  partners: string;
  district: string;
  radiusMeters: number;
  resultsCount: number;
  warnings?: string[];
  createdAt: string;
};
type ComplaintFormState = {
  complaintType: 'reclamo' | 'queja';
  productType: 'producto' | 'servicio';
  productDescription: string;
  claimedAmount: string;
  consumerName: string;
  consumerDocumentType: string;
  consumerDocumentNumber: string;
  consumerAddress: string;
  consumerPhone: string;
  consumerEmail: string;
  isMinor: boolean;
  guardianName: string;
  guardianDocumentType: string;
  guardianDocumentNumber: string;
  detail: string;
  requestedAction: string;
  responseChannel: 'email' | 'phone' | 'address';
  acceptsTruth: boolean;
};
type ComplaintResponse = {
  ok: boolean;
  code: string;
  status: string;
  receiptEmailStatus?: string;
  submittedAt: string;
  responseDueAt: string;
};
type ComplaintTrackingEvent = {
  status: string;
  eventType: string;
  message?: string;
  createdAt: string;
};
type ComplaintTrackingResponse = {
  ok: boolean;
  code: string;
  status: string;
  complaintType: string;
  productType: string;
  productDescription: string;
  responseChannel: string;
  submittedAt: string;
  responseDueAt: string;
  respondedAt?: string;
  responseText?: string;
  events: ComplaintTrackingEvent[];
};
const LOW_CREDITS_THRESHOLD = 40;

type BillingStatus = {
  authenticated: boolean;
  subscribed: boolean;
  planCode?: string;
  planAmountCents: number;
  planCurrency: string;
  planCredits: number;
  weeklyCredits?: number;
  planIntervalDays: number;
  planAvailable: boolean;
  freeMonthlyCredits: number;
  freeRenewalCredits?: number;
  creditsBalance: number;
  creditsReference?: number;
  discoverRubroCost?: number;
  discoverRubroSuperCost?: number;
  searchCost?: number;
  searchExpandCost?: number;
  superSearchCost?: number;
  exportCsvCost?: number;
  exportPdfCost?: number;
  superSearchAvailable?: boolean;
  trialActionsUsed: number;
  trialActionsLimit: number;
  trialActionsLeft: number;
  currentPeriodEnd?: string;
  nextCreditRenewal?: string;
  topUps: TopUp[];
  requiresPaymentToken: boolean;
  testCheckoutEnabled: boolean;
  culqiPublicKey?: string;
  chatgptEventId?: string;
  chatgptAmountCents?: number;
  chatgptCurrency?: string;
  chatgptPlanId?: string;
};

const PRECISE_LOCATION_TYPES = new Set([
  'route',
  'street_address',
  'street_number',
  'premise',
  'subpremise',
  'establishment',
  'point_of_interest',
  'intersection',
]);

const PRECISE_LOCATION_PATTERN = /\b(av\.?|avenida|calle|jr\.?|jir[oó]n|pasaje|prolongaci[oó]n|malec[oó]n|carretera|v[ií]a|ovalo|óvalo|plaza|parque)\b|\d{2,}/i;

function isPreciseLocation(location?: Pick<SelectedLocation, 'name' | 'address' | 'types' | 'source'> | null) {
  if (!location) return false;
  if (location.types?.some(type => PRECISE_LOCATION_TYPES.has(type))) return true;
  if (location.source === 'local' && location.address === 'Perú') {
    return PRECISE_LOCATION_PATTERN.test(location.name);
  }
  return PRECISE_LOCATION_PATTERN.test(`${location.name} ${location.address || ''}`);
}

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8080';
type MapBasemap = 'roadmap' | 'hybrid';
const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://radar-bi.com').replace(/\/$/, '');
const TIKTOK_TEST_EVENT_CODE = typeof import.meta.env.VITE_TIKTOK_TEST_EVENT_CODE === 'string'
  ? import.meta.env.VITE_TIKTOK_TEST_EVENT_CODE.trim()
  : '';
const COMPLAINTS_PROVIDER_NAME = import.meta.env.VITE_COMPLAINTS_PROVIDER_NAME || 'RadarBI';
const COMPLAINTS_PROVIDER_RUC = import.meta.env.VITE_COMPLAINTS_PROVIDER_RUC || '20611098988';
const COMPLAINTS_PROVIDER_ADDRESS = import.meta.env.VITE_COMPLAINTS_PROVIDER_ADDRESS || '';
const COMPLAINTS_PROVIDER_EMAIL = import.meta.env.VITE_COMPLAINTS_PROVIDER_EMAIL || 'soporte@radar-bi.com';

type PurchaseSummary = {
  credits: number;
  value: number;
  currency: string;
  transactionId: string;
  packCode: string;
  creditsBalance: number;
};
const PERU_CENTER: [number, number] = [-9.1900, -75.0152];
const DEFAULT_TOPUPS: TopUp[] = [
  { code: 'credits_100', credits: 100, amountCents: 750 },
  { code: 'credits_500', credits: 500, amountCents: 3250 },
  { code: 'credits_1000', credits: 1000, amountCents: 5750 },
  { code: 'credits_2000', credits: 2000, amountCents: 10000 },
];
const PAID_PACKS_FROM = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(
  Math.min(...DEFAULT_TOPUPS.map(pack => pack.amountCents)) / 100,
);
const INITIAL_COMPLAINT_FORM: ComplaintFormState = {
  complaintType: 'reclamo',
  productType: 'servicio',
  productDescription: '',
  claimedAmount: '',
  consumerName: '',
  consumerDocumentType: 'dni',
  consumerDocumentNumber: '',
  consumerAddress: '',
  consumerPhone: '',
  consumerEmail: '',
  isMinor: false,
  guardianName: '',
  guardianDocumentType: 'dni',
  guardianDocumentNumber: '',
  detail: '',
  requestedAction: '',
  responseChannel: 'email',
  acceptsTruth: false,
};

function createTikTokEventId(kind: string, suffix = '') {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const cleanSuffix = suffix.replace(/[^a-z0-9_-]/gi, '').slice(0, 40);
  return `radarbi_${kind}${cleanSuffix ? `_${cleanSuffix}` : ''}_${id}`;
}

function cleanTikTokPayload(payload: TikTokEventPayload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  ) as TikTokEventPayload;
}

function trackMarketingDataLayer(eventName: string, payload: Record<string, string | number | boolean> = {}) {
  if (typeof window === 'undefined') return;
  const globals = window as Window & { dataLayer?: Array<Record<string, unknown>> };
  globals.dataLayer = globals.dataLayer || [];
  globals.dataLayer.push({ event: eventName, ...payload });
}

function trackTikTokEvent(eventName: string, payload: TikTokEventPayload = {}, eventId = createTikTokEventId(eventName.toLowerCase())) {
  if (typeof window === 'undefined') return eventId;
  const ttq = (window as any).ttq;
  if (!ttq || typeof ttq.track !== 'function') return eventId;

  const eventPayload = cleanTikTokPayload({
    ...payload,
    event_id: eventId,
    test_event_code: TIKTOK_TEST_EVENT_CODE || undefined,
  });

  ttq.track(eventName, eventPayload, { event_id: eventId });
  return eventId;
}

function moneyFromCents(amountCents: number) {
  return Math.round((amountCents / 100) * 100) / 100;
}

function tiktokTopUpPayload(pack: TopUp, currency: string): TikTokEventPayload {
  const value = moneyFromCents(pack.amountCents);
  return {
    content_type: 'product',
    content_ids: [pack.code],
    contents: [{
      content_id: pack.code,
      content_name: `RadarBI ${pack.credits} créditos`,
      content_category: 'credits_topup',
      quantity: 1,
      price: value,
    }],
    quantity: 1,
    value,
    currency,
    description: `Compra de ${pack.credits} créditos RadarBI`,
    pack_code: pack.code,
    credits: pack.credits,
  };
}

function trackTikTokAuthSuccess(user: AuthUser, method: AuthLoginMethod, options: { isNewUser?: boolean; loginEventId?: string; registrationEventId?: string } = {}) {
  trackTikTokEvent('Login', {
    description: 'RadarBI user login',
    event_category: 'account',
    method,
    status: 'success',
    email_verified: user.emailVerified ? 'true' : 'false',
  }, options.loginEventId || createTikTokEventId('login', method));

  if (options.isNewUser) {
    trackTikTokEvent('CompleteRegistration', {
      description: 'RadarBI: 100 créditos gratis al registrarte',
      event_category: 'account',
      method,
      registration_method: method,
      status: 'success',
      offer_credits: SIGNUP_OFFER_CREDITS,
      product_layers: SIGNUP_OFFER_PROPS.product_layers,
      audience: SIGNUP_OFFER_PROPS.audience,
    }, options.registrationEventId || createTikTokEventId('registration', method));
  }
}

function setMeta(selector: string, attr: 'content' | 'href', value: string) {
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute(attr, value);
  }
}

function setMetaContent(selector: string, value: string) {
  setMeta(selector, 'content', value);
}

function buildSearchURL(params: { query: string; district: string; partners: string }) {
  const search = new URLSearchParams();
  if (params.query.trim()) search.set('rubro', params.query.trim());
  if (params.district.trim()) search.set('ubicacion', params.district.trim());
  if (params.partners.trim()) search.set('socios', params.partners.trim());
  const queryString = search.toString();
  return `${SITE_URL}/${queryString ? `?${queryString}` : ''}`;
}

function readAnalysisIdFromLocation() {
  if (typeof window === 'undefined') return '';
  const fromPath = window.location.pathname.match(/^\/analisis\/([0-9a-f-]{8,})\/?$/i);
  if (fromPath?.[1]) return fromPath[1];
  return new URLSearchParams(window.location.search).get('analisis')?.trim() || '';
}

function updateSearchMeta(params: {
  query: string;
  district: string;
  partners: string;
  resultsCount: number;
  analysisId?: string;
}) {
  const business = params.query.trim() || 'negocio';
  const district = params.district.trim() || 'Perú';
  const partners = params.partners.trim();
  const title = `Estudio de mercado para ${business} en ${district} | RadarBI`;
  const description = partners
    ? `Estudio de ${business} en ${district}: competencia, quién te trae clientes (${partners}) y ${params.resultsCount} puntos encontrados.`
    : `Estudio de ${business} en ${district}: competencia, quién te trae clientes y ${params.resultsCount} puntos encontrados.`;
  const url = params.analysisId
    ? `${SITE_URL}/analisis/${params.analysisId}`
    : buildSearchURL(params);

  document.title = title;
  setMetaContent('meta[name="description"]', description);
  setMetaContent('meta[property="og:title"]', title);
  setMetaContent('meta[property="og:description"]', description);
  setMetaContent('meta[property="og:url"]', url);
  setMetaContent('meta[name="twitter:title"]', title);
  setMetaContent('meta[name="twitter:description"]', description);
  setMeta('link[rel="canonical"]', 'href', url);

  if (typeof window !== 'undefined') {
    const nextURL = new URL(url);
    window.history.replaceState(null, title, `${nextURL.pathname}${nextURL.search}`);
  }
}

function loadCulqiCheckout(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).CulqiCheckout) { resolve(); return; }
    const existing = document.getElementById('culqi-checkout-js') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('No se pudo cargar Culqi Checkout')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = 'culqi-checkout-js';
    script.src = 'https://js.culqi.com/checkout-js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('No se pudo cargar Culqi Checkout'));
    document.body.appendChild(script);
  });
}

// Thrown when the shopper closes the Culqi modal without paying. It is a normal
// outcome, not a failure, so callers should reset their UI without showing an error.
class CulqiCancelledError extends Error {
  constructor() {
    super('Compra cancelada');
    this.name = 'CulqiCancelledError';
  }
}

// Culqi Checkout exposes no "cancelled" callback, so the modal disappearing is the
// only signal that the shopper backed out. Non-rendered tags are skipped so our own
// <script id="culqi-checkout-js"> never counts as an open modal.
function culqiModalPresent(): boolean {
  const nodes = document.querySelectorAll<HTMLElement>('iframe[src*="culqi"], [id*="culqi"], [class*="culqi"]');
  for (const el of nodes) {
    if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') continue;
    if (el.getClientRects().length > 0) return true;
  }
  return false;
}

async function requestCulqiToken(params: { publicKey?: string; amountCents: number; description: string; email?: string }) {
  if (!params.publicKey) {
    throw new Error('CULQI_PUBLIC_KEY no está configurado');
  }
  await loadCulqiCheckout();
  return new Promise<string>((resolve, reject) => {
    const CulqiCheckout = (window as any).CulqiCheckout;
    if (!CulqiCheckout) {
      reject(new Error('Culqi Checkout no está disponible'));
      return;
    }

    let settled = false;
    let sawModal = false;
    let modalPoll = 0;
    let hardTimeout = 0;
    const checkout = new CulqiCheckout(params.publicKey, {
      settings: {
        title: 'RadarBI',
        currency: 'PEN',
        amount: params.amountCents,
      },
      client: {
        email: params.email || '',
      },
      options: {
        lang: 'es',
        modal: true,
        installments: true,
        paymentMethods: {
          tarjeta: true,
          yape: false,
          billetera: false,
          bancaMovil: false,
          agente: false,
          cuotealo: false,
        },
        paymentMethodsSort: ['tarjeta'],
      },
      appearance: {
        theme: 'default',
        defaultStyle: {
          bannerColor: '#0a2540',
          buttonBackground: '#0a2540',
          buttonTextColor: '#ffffff',
          priceColor: '#0a2540',
          linksColor: '#1e3a5f',
        },
      },
    });

    // Settles the promise exactly once and tears down every timer, global and
    // the modal itself, so a cancelled attempt cannot leave the caller hanging.
    const settle = (finish: () => void) => {
      if (settled) return;
      settled = true;
      window.clearInterval(modalPoll);
      window.clearTimeout(hardTimeout);
      const globals = window as any;
      if (globals.handleCulqiAction === handleCulqiAction) delete globals.handleCulqiAction;
      if (globals.__radarbiCulqiCheckout === checkout) delete globals.__radarbiCulqiCheckout;
      try {
        checkout?.close?.();
      } catch {
        /* the modal may already be gone */
      }
      finish();
    };

    function handleCulqiAction() {
      const instance = (window as any).__radarbiCulqiCheckout;
      const tokenId = instance?.token?.id || (window as any).Culqi?.token?.id;
      const error = instance?.error || (window as any).Culqi?.error;
      if (tokenId) {
        settle(() => resolve(tokenId));
        return;
      }
      if (error) {
        settle(() => reject(new Error(error.user_message || error.message || 'Culqi no pudo generar el token')));
        return;
      }
      // Some Culqi flows invoke this callback on close with neither token nor error.
      // Only treat that as a cancellation once the modal is actually gone, so an
      // intermediate callback (3-D Secure, for instance) cannot abort a live payment.
      if (!culqiModalPresent()) {
        settle(() => reject(new CulqiCancelledError()));
      }
    }

    (window as any).__radarbiCulqiCheckout = checkout;
    (window as any).handleCulqiAction = handleCulqiAction;
    checkout.culqi = handleCulqiAction;

    try {
      checkout.open();
    } catch (err: any) {
      settle(() => reject(new Error(err?.message || 'No se pudo abrir Culqi Checkout')));
      return;
    }

    // Deliberately only cancels once the modal has been *seen* and then goes away.
    // If Culqi ever renders markup this cannot recognise, the flow degrades to the
    // timeout below rather than interrupting someone mid-payment.
    modalPoll = window.setInterval(() => {
      if (culqiModalPresent()) {
        sawModal = true;
        return;
      }
      if (sawModal) settle(() => reject(new CulqiCancelledError()));
    }, 350);

    hardTimeout = window.setTimeout(() => {
      settle(() => reject(new Error('No se completó el pago en Culqi')));
    }, 5 * 60 * 1000);
  });
}

// Haversine distance in meters between two lat/lng points
function haversineDist(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

const MAX_ANALYSIS_RADIUS_METERS = 15000;
const DEFAULT_SEARCH_COST = 20;
const DEFAULT_SEARCH_EXPAND_COST = 10;
const DEFAULT_EXPORT_CSV_COST = 2;
const DEFAULT_EXPORT_PDF_COST = 10;
// Mirrors the backend eligibility rule; slightly tighter so the price shown never undercuts the charge.
const SEARCH_EXPAND_WINDOW_MS = 29 * 60 * 1000;
const SEARCH_EXPAND_MAX_CENTER_DRIFT_METERS = 50;

type ZeroResultsSearch = { lat: number; lng: number; radius: number; query: string; partners: string; at: number };

function countPlacesWithinRadius(places: Place[], lat: number, lng: number, radiusMeters: number) {
  return places.filter(p => haversineDist(lat, lng, p.lat, p.lng) <= radiusMeters).length;
}

function suggestExpandedRadius(current: number): number | null {
  if (current >= MAX_ANALYSIS_RADIUS_METERS) return null;
  const factor = current <= 500 ? 3 : 2.5;
  const next = Math.max(current + 100, Math.round((current * factor) / 100) * 100);
  return Math.min(MAX_ANALYSIS_RADIUS_METERS, next);
}

function zoneInsightTone(reason: string): 'good' | 'mid' | 'warn' {
  if (reason.startsWith('✅')) return 'good';
  if (reason.startsWith('⚠️')) return 'warn';
  return 'mid';
}

function zoneInsightText(reason: string) {
  return (reason || '').replace(/^[✅🟡⚠️]\s*/, '').trim();
}

function zoneRatioLabel(partners: number, comps: number) {
  if (comps === 0 && partners > 0) return 'Sin competencia';
  if (comps === 0 && partners === 0) return null;
  return `${partners}:${comps}`;
}

function districtFactChips(reason: string) {
  const text = reason || '';
  const chips: string[] = [];
  const nse = text.match(/NSE\s*[A-E](?:\s*\+)?(?:\s*(?:\/|y)\s*[A-E]\+?)?/i);
  if (nse) chips.push(nse[0].replace(/\s+/g, ' ').toUpperCase());
  else if (/poder adquisitivo|alto ingreso|clase media/i.test(text)) chips.push('NSE');
  if (/afluencia|peatonal|tr[aá]fico|flujo|tur[ií]st/i.test(text)) chips.push('Afluencia');
  if (/poca competencia|baja densidad|sin competencia|oportunidad/i.test(text)) chips.push('Oportunidad');
  else if (/demanda|hospital|cl[ií]nica|salud|oficin/i.test(text)) chips.push('Demanda');
  return chips.slice(0, 3);
}

function districtReasonIsLong(reason: string) {
  const text = (reason || '').trim();
  return text.length > 90 || text.split(/\s+/).length > 16;
}

function DistrictRecRow({ rec }: { rec: { district: string; reason: string } }) {
  const [open, setOpen] = useState(false);
  const chips = districtFactChips(rec.reason);
  const expandable = districtReasonIsLong(rec.reason);
  return (
    <article className={open ? 'district-rec is-open' : 'district-rec'}>
      <div className="district-rec-head">
        <strong>{rec.district}</strong>
        {chips.length > 0 && (
          <span className="district-rec-chips">
            {chips.map(chip => <span key={chip}>{chip}</span>)}
          </span>
        )}
      </div>
      <p className="district-rec-text">{rec.reason}</p>
      {expandable && (
        <button type="button" className="district-rec-more" onClick={() => setOpen(current => !current)}>
          {open ? 'Ver menos' : 'Ver más'}
        </button>
      )}
    </article>
  );
}

const PERU_LOCATION_SUGGESTIONS = [
  'Lima Metropolitana', 'Barranco', 'Miraflores', 'San Isidro', 'Santiago de Surco', 'Surquillo',
  'San Borja', 'La Molina', 'Jesús María', 'Pueblo Libre', 'Magdalena del Mar', 'San Miguel',
  'Chorrillos', 'San Juan de Miraflores', 'Villa El Salvador', 'Villa María del Triunfo',
  'Ate', 'Santa Anita', 'La Victoria', 'Breña', 'Rímac', 'Los Olivos', 'Independencia',
  'Comas', 'Carabayllo', 'San Martín de Porres', 'Callao', 'Bellavista', 'La Perla',
  'Ventanilla', 'Chaclacayo', 'Lurigancho-Chosica', 'Lurín', 'Pachacámac', 'Punta Hermosa',
  'Arequipa', 'Cusco', 'Trujillo', 'Piura', 'Chiclayo', 'Ica', 'Huancayo', 'Tacna', 'Puno',
  'Juliaca', 'Iquitos', 'Tarapoto', 'Cajamarca', 'Huaraz', 'Ayacucho', 'Moquegua', 'Tumbes'
];

const PERU_AREA_TYPES = new Set([
  'locality',
  'sublocality',
  'sublocality_level_1',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'neighborhood',
  'political',
]);

function normalizeLocationName(name: string) {
  return name.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function isPeruAreaSuggestion(suggestion: LocationOption) {
  if (suggestion.source === 'local' || suggestion.address === 'Perú') return true;
  return (suggestion.types || []).some(type => PERU_AREA_TYPES.has(type));
}

function mergeLocationSuggestions(local: LocationOption[], remote: LocationOption[]) {
  const seen = new Set<string>();
  const merged: LocationOption[] = [];
  const push = (item: LocationOption) => {
    const key = normalizeLocationName(item.name);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  };
  local.forEach(push);
  remote.filter(isPeruAreaSuggestion).forEach(push);
  remote.filter(item => !isPeruAreaSuggestion(item)).forEach(push);
  return merged.slice(0, 12);
}

function cleanProviderWarning(message: string) {
  const lower = message.toLowerCase();
  if ((lower.includes('<!doctype') || lower.includes('<html')) && lower.includes('osm')) {
    return 'OSM temporalmente no pudo responder la consulta. Prueba de nuevo o reduce el radio.';
  }
  if (
    lower.includes('overpass') ||
    lower.includes('context deadline exceeded') ||
    lower.includes('client.timeout') ||
    lower.includes('awaiting headers')
  ) {
    return 'Una fuente de mapas no respondió. Prueba de nuevo o reduce el radio.';
  }
  if (lower.includes('google places') || lower.includes('google partners')) {
    return 'Google no respondió una parte de la búsqueda. Mostramos solo lo que sí llegó.';
  }
  if (lower.includes('osm') && lower.includes('error')) {
    return 'Una fuente de mapas no respondió. Prueba de nuevo si falta algún punto.';
  }
  return message
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 220);
}

function humanizeUserError(message: string, fallback = 'No pudimos completar eso. Intenta de nuevo en un momento.') {
  const text = cleanProviderWarning(message || '');
  const lower = text.toLowerCase();
  if (
    !text ||
    lower.includes('page not found') ||
    lower.includes('<!doctype') ||
    lower.includes('<html') ||
    lower.includes('sqlstate') ||
    lower.includes('invalid character') ||
    lower.includes('bad gateway') ||
    lower.includes('internal server') ||
    lower.includes('method not allowed') ||
    /^\d{3}\b/.test(lower) ||
    (lower.includes('not found') && lower.includes('404'))
  ) {
    return fallback;
  }
  return text;
}

function friendlyNetworkError(error: unknown, fallback = 'No se pudo completar la búsqueda') {
  const message = error instanceof Error ? error.message : String(error || '');
  const lower = message.toLowerCase();
  if (lower.includes('aborted') || lower.includes('the operation was aborted')) {
    return fallback;
  }
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('load failed') ||
    lower.includes('network request failed')
  ) {
    return fallback;
  }
  return humanizeUserError(message || fallback, fallback);
}

function splitKeywordInput(value: string) {
  return value
    .split(/[,\n]/)
    .map(item => item.trim())
    .filter(Boolean);
}

function isDegradedLayerWarning(message: string) {
  const lower = (message || '').toLowerCase();
  return lower.includes('osm') || lower.includes('overpass') || lower.includes('afluencia');
}

function uniqueCleanWarnings(warnings?: string[]) {
  return Array.from(new Set((warnings || []).map(cleanProviderWarning).filter(Boolean)));
}

function LocationSearch({
  locations,
  activeLocationId,
  onAddLocation,
  onRemoveLocation,
  onActivateLocation,
  placeholder = 'Miraflores, San Isidro, Arequipa',
  seedQuery = '',
}: {
  locations: SelectedLocation[];
  activeLocationId: string;
  onAddLocation: (location: SelectedLocation) => void;
  onRemoveLocation: (id: string) => void;
  onActivateLocation: (location: SelectedLocation) => void;
  placeholder?: string;
  seedQuery?: string;
}) {
  const [input, setInput] = useState(seedQuery);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationOption[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!seedQuery) return;
    setInput(seedQuery);
  }, [seedQuery]);

  useEffect(() => {
    const value = input.trim();
    if (value.length < 2) {
      setSuggestions([]);
      return;
    }
    const query = normalizeLocationName(value);
    const localSuggestions = PERU_LOCATION_SUGGESTIONS
      .filter(item => {
        const name = normalizeLocationName(item);
        if (name.includes(query) || (name.length >= 4 && query.includes(name))) return true;
        return name.split(/\s+/).filter(token => token.length >= 5).some(token => query.includes(token));
      })
      .slice(0, 8)
      .map(item => ({ id: `local-${item}`, name: item, address: 'Perú', source: 'local' as const }));
    setSuggestions(localSuggestions);

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/locations/autocomplete?q=${encodeURIComponent(value)}`, { signal: controller.signal } as RequestInit);
        if (!res.ok) throw new Error('google autocomplete unavailable');
        const data = await res.json();
        const remote = Array.isArray(data) ? data as LocationOption[] : [];
        setSuggestions(mergeLocationSuggestions(localSuggestions, remote));
      } catch {
        setSuggestions(localSuggestions);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [input]);

  const resolveLocalLocation = async (name: string): Promise<SelectedLocation | null> => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(name + ', Perú')}&limit=1`);
      const data = await res.json();
      if (data && data.length > 0) {
        return {
          id: `local-${name}`,
          name,
          address: 'Perú',
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          source: 'local',
          types: isPreciseLocation({ name, address: 'Perú', source: 'local' }) ? ['route'] : [],
        };
      }
    } catch {
      return null;
    }
    return null;
  };

  const addSuggestion = async (suggestion?: LocationOption) => {
    const selected = suggestion || suggestions[0] || { id: `local-${input.trim()}`, name: input.trim(), address: 'Perú', source: 'local' };
    if (!selected.name) return;
    setLoading(true);
    try {
      let location: SelectedLocation | null = null;
      if (typeof selected.lat === 'number' && typeof selected.lng === 'number') {
        location = { id: selected.id, name: selected.name, address: selected.address, lat: selected.lat, lng: selected.lng, source: selected.source, types: selected.types };
      } else if (selected.source === 'google') {
        const res = await apiFetch(`/api/locations/details?id=${encodeURIComponent(selected.id)}`);
        if (res.ok) {
          const details = await res.json();
          if (typeof details.lat === 'number' && typeof details.lng === 'number') {
            location = details;
          }
        }
      }
      if (!location) {
        location = await resolveLocalLocation(selected.name);
      }
      if (!location) {
        alert('No se encontró la ubicación en Perú.');
        return;
      }
      onAddLocation(location);
      setInput('');
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    await addSuggestion();
  };

  return (
    <div className="location-picker">
      <div className="location-chips">
        {locations.map(location => (
          <span
            key={location.id}
            className={`location-chip-group ${location.id === activeLocationId ? 'location-chip-group-active' : ''}`}>
            <button
              type="button"
              className="location-chip"
              onClick={() => onActivateLocation(location)}
              title={location.address || 'Centrar mapa en esta ubicación'}>
              <MapPin size={13} aria-hidden="true"/>
              <span>{location.name}</span>
            </button>
            <button
              type="button"
              className="chip-remove"
              onClick={() => onRemoveLocation(location.id)}
              aria-label={`Quitar ubicación ${location.name}`}>
              <X size={12} aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
      <form onSubmit={handleSearch} className="location-search">
        <div className="location-input-wrap">
          <input
            id="location-search-input"
            name="location"
            type="search"
            autoComplete="off"
            aria-label="Buscar distrito, ciudad o avenida en Perú"
            value={input}
            onChange={e => { setInput(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
          />
          {open && suggestions.length > 0 && (
            <div className="location-suggestions" onWheel={event => event.stopPropagation()}>
              {suggestions.map(suggestion => (
                <button key={suggestion.id} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => addSuggestion(suggestion)}>
                  <span>{suggestion.name}</span>
                  <small>{suggestion.address || 'Perú'}{suggestion.source === 'google' ? ' · Google' : suggestion.source === 'local' ? ' · Distrito / ciudad' : ''}</small>
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="submit" disabled={loading} title="Usar ubicación">
          {loading ? '...' : <><MapPin size={15}/>Usar</>}
        </button>
      </form>
    </div>
  );
}

const CATEGORY_COLORS: Record<string, string> = {
  'restaurant': '#f43f5e',
  'cafe': '#d97706',
  'fast_food': '#f59e0b',
  'bar': '#8b5cf6',
  'pharmacy': '#10b981',
  'clinic': '#06b6d4',
  'doctors': '#06b6d4',
  'hospital': '#0ea5e9',
  'bank': '#3b82f6',
  'hotel': '#ec4899',
  'guest_house': '#f472b6',
  'supermarket': '#84cc16',
  'convenience': '#a3e635',
  'clothes': '#c026d3',
  'shoes': '#d946ef',
  'school': '#14b8a6',
  'college': '#0f766e',
  'kindergarten': '#2dd4bf',
  'language_school': '#5eead4',
  'university': '#2563eb',
  'office': '#475569',
  'coworking_space': '#64748b',
  'park': '#16a34a',
  'library': '#7c3aed',
  'fitness_centre': '#ef4444',
  'general': '#ea580c',
};

const CATEGORY_TRANSLATIONS: Record<string, string> = {
  'restaurant': 'Restaurante', 'cafe': 'Cafetería', 'fast_food': 'Comida Rápida', 'bar': 'Bar', 'pharmacy': 'Farmacia', 'clinic': 'Clínica', 'doctors': 'Consultorio Médico', 'hospital': 'Hospital', 'bank': 'Banco', 'hotel': 'Hotel', 'guest_house': 'Hospedaje', 'supermarket': 'Supermercado', 'convenience': 'Minimarket', 'clothes': 'Ropa', 'shoes': 'Zapatos', 'school': 'Colegio / Escuela', 'college': 'Instituto / Colegio', 'kindergarten': 'Inicial', 'language_school': 'Escuela de Idiomas', 'university': 'Universidad', 'office': 'Oficinas', 'coworking_space': 'Coworking', 'park': 'Parque', 'library': 'Biblioteca', 'fitness_centre': 'Gimnasio', 'general': 'Tienda General', 'kiosk': 'Kiosko', 'hardware': 'Ferretería', 'bakery': 'Panadería', 'beauty': 'Salón de Belleza', 'hairdresser': 'Peluquería', 'electronics': 'Electrónica', 'mobile_phone': 'Celulares', 'books': 'Librería', 'sports': 'Deportes', 'toys': 'Juguetería', 'car_repair': 'Taller Mecánico', 'car_parts': 'Repuestos Autos', 'gift': 'Regalos', 'stationery': 'Librería/Papelería', 'optician': 'Óptica', 'jewelry': 'Joyería', 'florist': 'Florería', 'travel_agency': 'Agencia de Viajes', 'laundry': 'Lavandería', 'pet': 'Mascotas', 'veterinary': 'Veterinaria', 'butcher': 'Carnicería', 'greengrocer': 'Verdulería', 'seafood': 'Pescadería', 'bicycle': 'Bicicletas', 'yes': 'Tienda Múltiple', 'medical_supply': 'Suministros Médicos', 'dentist': 'Dentista', 'pastry': 'Pastelería', 'deli': 'Delicatessen', 'alcohol': 'Licorería', 'beverages': 'Bebidas', 'department_store': 'Tienda por Departamento', 'mall': 'Centro Comercial', 'copyshop': 'Centro de Copiado', 'photo': 'Fotografía', 'variety_store': 'Variedades', 'art': 'Arte', 'music': 'Música', 'musical_instrument': 'Instrumentos Musicales', 'leather': 'Artículos de Cuero', 'fabric': 'Telas', 'dairy': 'Lácteos', 'wine': 'Vinos', 'tyres': 'Llantas', 'motorcycle': 'Motos', 'paint': 'Pinturas', 'doityourself': 'Bricolaje', 'houseware': 'Artículos para el Hogar', 'furniture': 'Muebles', 'craft': 'Manualidades', 'outdoor': 'Exteriores', 'fashion': 'Moda', 'bookmaker': 'Casa de Apuestas', 'money_lender': 'Prestamista', 'gas': 'Gasolinera', 'ticket': 'Boletos', 'printing': 'Imprenta', 'radiotechnics': 'Radiotécnico', 'funeral_directors': 'Funeraria', 'herbalist': 'Herbolario', 'computer': 'Computación',
};

function translateCategory(cat: string) {
  if (!cat) return 'Otro';
  return CATEGORY_TRANSLATIONS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, ' ');
}

function getColorForCategory(cat: string) {
  return CATEGORY_COLORS[cat] || '#64748b'; // default slate
}

type MarketMapViewProps = {
  center: [number, number];
  setCenter: (pos: [number, number]) => void;
  basemap: MapBasemap;
  partnerHeatPlaces: Place[];
  includePop: boolean;
  footTraffic: FootTrafficResponse | null;
  radius: number;
  setRadius: (r: number) => void;
  places: Place[];
  partnerPointPlaces: Place[];
  rentals: Array<RentalProperty & { lat: number; lng: number }>;
  zones: Zone[];
  showZones: boolean;
  bestZones?: Array<{ lat: number; lng: number; label: string; score: number; comps: number; partners: number; reason: string }>;
  selectedPointLocation: SelectedLocation | null;
  getColorForCategory: (cat: string) => string;
  translateCategory: (cat: string) => string;
  initialZoom?: number;
  compact?: boolean;
  centerDraggable?: boolean;
};

// Only mounted after "Buscar", so the click that triggered it is already gone:
// waiting for another interaction would leave the map blank.
function MarketMapStage(props: MarketMapViewProps) {
  const [MapView, setMapView] = useState<React.ComponentType<MarketMapViewProps> | null>(null);
  useEffect(() => {
    let live = true;
    void import('./MarketMap').then(mod => {
      if (live) setMapView(() => mod.default);
    });
    return () => {
      live = false;
    };
  }, []);
  if (!MapView) {
    return <div className="map" id="tour-map" role="region" aria-label="Mapa de análisis comercial de Perú" />;
  }
  return <MapView {...props} />;
}

// =================== ONBOARDING TOUR ===================
const TOUR_STORAGE_KEY = 'peru-market-tour-completed';

interface TourStep {
  target: string;
  title: string;
  content: string;
  position: 'bottom' | 'top' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: 'tour-search-panel',
    title: '👋 ¡Bienvenido a RadarBI!',
    content: 'Este es tu panel de configuración. Aquí defines los parámetros de tu análisis de mercado. Para iniciar la búsqueda necesitas al menos tu rubro o quién te trae clientes.',
    position: 'bottom'
  },
  {
    target: 'tour-location',
    title: '📍 Ubicación',
    content: 'Escribe cualquier ciudad, distrito o zona de Perú (ej: "Chosica", "Surco", "Arequipa"). Al presionar "Ir", el mapa se centrará automáticamente en esa ubicación.',
    position: 'bottom'
  },
  {
    target: 'tour-rubro',
    title: '🏪 Tu Rubro',
    content: 'Define tu tipo de negocio (ej: "farmacia", "restaurante"). El sistema buscará todos los competidores de ese rubro en el área. Si lo dejas vacío, agrega al menos quién te trae clientes.',
    position: 'bottom'
  },
  {
    target: 'tour-partners',
    title: '🤝 Quién te trae clientes',
    content: 'Agrega negocios que atraerían gente a tu local (ej: "clínica", "banco"). Se muestran en el mapa. La IA también sugiere algunos según tu rubro.',
    position: 'bottom'
  },
  {
    target: 'tour-action-bar',
    title: '🚀 Acciones',
    content: '"Buscar" muestra en el mapa la competencia y quién te trae clientes dentro del radio.',
    position: 'bottom'
  },
  {
    target: 'tour-map',
    title: '🗺️ El Mapa',
    content: 'Arrastra el marcador azul para cambiar el centro de análisis. Usa el control de radio abajo a la izquierda para ajustar el área. Luego pulsa "Actualizar datos en el mapa" para recalcular. Los puntos de colores son establecimientos, los azules socios comerciales, y los dorados ($) son locales en alquiler.',
    position: 'top'
  },
  {
    target: 'tour-best-zone',
    title: '📍 Mejor Zona',
    content: 'Este tip junto al mapa busca la ubicación con más socios y menos competencia dentro del radio. Úsalo para elegir dónde poner tu local.',
    position: 'left'
  }
];

interface TourRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

function setPageInert(inert: boolean) {
  document.body.classList.toggle('tour-open', inert);
  document.querySelectorAll<HTMLElement>('.app, .auth-actions, .floating-login-card').forEach(node => {
    node.inert = inert;
  });
}

function OnboardingTour({ onComplete }: { onComplete: () => void }) {
  const [tourSteps] = useState(() => {
    const present = TOUR_STEPS.filter(item => document.getElementById(item.target));
    return present.length ? present : TOUR_STEPS;
  });
  const [step, setStep] = useState(0);
  const [highlight, setHighlight] = useState<TourRect | null>(null);
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const measureHighlight = useCallback(() => {
    const el = document.getElementById(tourSteps[step].target);
    if (el) {
      const isCompactTour = window.innerWidth <= 820;
      const rect = el.getBoundingClientRect();
      const mobileBottomSheetReserve = isCompactTour ? 252 : 0;
      const margin = isCompactTour ? 10 : 6;
      const maxVisibleBottom = window.innerHeight - mobileBottomSheetReserve;
      const top = Math.max(margin, rect.top - margin);
      const left = Math.max(margin, rect.left - margin);
      const right = Math.min(window.innerWidth - margin, rect.right + margin);
      const bottom = Math.min(maxVisibleBottom - margin, rect.bottom + margin);
      setHighlight({
        left,
        top,
        right,
        bottom: Math.max(top + 44, bottom),
        width: Math.max(44, right - left),
        height: Math.max(44, Math.max(top + 44, bottom) - top),
      });
    }
  }, [step]);

  const updateHighlight = useCallback(() => {
    const el = document.getElementById(tourSteps[step].target);
    if (!el) return;
    const isCompactTour = window.innerWidth <= 820;
    const rect = el.getBoundingClientRect();
    const visible = rect.top >= 8 && rect.bottom <= window.innerHeight - 8;
    if (!visible) {
      el.scrollIntoView({ behavior: 'auto', block: isCompactTour ? 'start' : 'center' });
    }
    window.requestAnimationFrame(measureHighlight);
  }, [measureHighlight, step]);

  const finish = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    updateHighlight();
    const handleViewportChange = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      measureHighlight();
    };
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [measureHighlight, updateHighlight]);

  useEffect(() => {
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPageInert(true);
    const frame = window.requestAnimationFrame(() => dialogRef.current?.focus());
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finish();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey);
      setPageInert(false);
      previouslyFocused.current?.focus?.();
    };
  }, [finish]);

  const next = () => {
    if (step < tourSteps.length - 1) setStep(step + 1);
    else finish();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const currentStep = tourSteps[step];
  const isCompactTour = viewport.width <= 820;
  
  const getTooltipStyle = (): React.CSSProperties => {
    if (isCompactTour) return {};
    if (!highlight) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    
    const padding = 16;
    const tooltipWidth = Math.min(380, viewport.width - padding * 2);
    
    switch (currentStep.position) {
      case 'bottom':
        return {
          top: highlight.bottom + padding,
          left: Math.max(padding, Math.min(highlight.left + highlight.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding)),
          maxWidth: tooltipWidth
        };
      case 'top':
        return {
          bottom: window.innerHeight - highlight.top + padding,
          left: Math.max(padding, Math.min(highlight.left + highlight.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding)),
          maxWidth: tooltipWidth
        };
      default:
        return {
          top: highlight.bottom + padding,
          left: Math.max(padding, highlight.left),
          maxWidth: tooltipWidth
        };
    }
  };

  return createPortal(
    <div
      className="tour-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
      aria-describedby="tour-body"
      ref={dialogRef}
      tabIndex={-1}
    >
      <svg className="tour-backdrop" aria-hidden="true">
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {highlight && (
              <rect 
                x={highlight.left} 
                y={highlight.top} 
                width={highlight.width} 
                height={highlight.height} 
                rx="12" 
                fill="black" 
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(15, 23, 42, 0.65)" mask="url(#tour-mask)" />
      </svg>
      
      {highlight && (
        <div
          className="tour-highlight"
          aria-hidden="true"
          style={{
            transform: `translate(${highlight.left}px, ${highlight.top}px)`,
            width: highlight.width,
            height: highlight.height,
          }}
        />
      )}

      <div className="tour-card" style={getTooltipStyle()}>
        <div className="tour-dots" aria-hidden="true">
          {tourSteps.map((_, i) => (
            <div key={i} className={`tour-dot ${i === step ? 'tour-dot-active' : ''} ${i < step ? 'tour-dot-complete' : ''}`} />
          ))}
        </div>
        
        <p className="tour-card-title" id="tour-title">{currentStep.title}</p>
        <p id="tour-body">{currentStep.content}</p>
        
        <div className="tour-actions">
          <button 
            type="button"
            onClick={finish} 
            className="tour-skip"
          >
            Omitir tour
          </button>
          <div className="tour-step-actions">
            {step > 0 && (
              <button 
                type="button"
                onClick={prev}
                className="tour-secondary"
              >
                Anterior
              </button>
            )}
            <button 
              type="button"
              onClick={next}
              className="tour-primary"
            >
              {step === tourSteps.length - 1 ? '¡Entendido!' : `Siguiente (${step + 1}/${tourSteps.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}


function formatMoney(cents: number, currency = 'PEN') {
  return new Intl.NumberFormat('es-PE', { style: 'currency', currency }).format((cents || 0) / 100);
}

function describeCreditEntry(entry: CreditLedgerEntry) {
  const labels: Record<string, string> = {
    free_monthly_grant: 'Créditos gratis',
    free_monthly_renewal: 'Renovación mensual',
    monthly_subscription: 'Créditos agregados',
    monthly_renewal: 'Renovación de créditos',
    credits_topup: 'Compra de créditos',
    usage: 'Consumo de créditos',
    refund: 'Devolución de créditos',
  };
  if (entry.reason === 'refund') {
    if ((entry.action || '').includes('discover.rubro.super')) return 'Devolución: súper búsqueda de rubro';
    if ((entry.action || '').includes('discover.rubro')) return 'Devolución: búsqueda de rubro';
    if ((entry.action || '').includes('market.super_search')) return 'Devolución: súper búsqueda';
    if ((entry.action || '').includes('evaluation.unlock')) return 'Devolución: evaluación de local';
    return 'Devolución de créditos';
  }
  if (entry.reason === 'usage') {
    const actionLabels: Record<string, string> = {
      'market.search': 'Análisis de zona',
      'market.search.expand': 'Ampliación de radio',
      'recommend.partners': 'Recomendación de socios',
      'recommend.districts': 'Mejor zona',
      'discover.rubro': 'Búsqueda de rubro',
      'discover.rubro.super': 'Súper búsqueda de rubro',
      'market.super_search': 'Súper búsqueda',
      'evaluation.unlock': 'Evaluación de local',
    };
    return actionLabels[entry.action || ''] || 'Uso de la plataforma';
  }
  return labels[entry.reason] || entry.reason.replace(/_/g, ' ');
}

function savedSearchKindLabel(kind: string) {
  if (kind === 'market.super_search') return 'Súper búsqueda';
  if (kind === 'discover.rubro.super') return 'Súper rubro';
  if (kind.startsWith('discover')) return 'Mejor rubro';
  if (kind.includes('expand')) return 'Ampliación';
  return 'Búsqueda';
}

function savedSearchTitle(item: SavedSearchSummary) {
  if ((item.kind || '').startsWith('discover')) return item.district || 'Mejor rubro de la zona';
  return item.query || item.district || 'Búsqueda de zona';
}

function reportPlaceFromSaved(place: any): ReportPlace {
  return {
    name: place?.name || 'Sin nombre',
    source: place?.source,
    category: place?.category,
    lat: Number(place?.lat) || 0,
    lng: Number(place?.lng) || 0,
    address: place?.address,
    rating: place?.rating,
    reviews: place?.reviews,
    estimatedTrafficScore: place?.estimatedTrafficScore,
  };
}

function reportInputFromSaved(item: SavedSearch): MarketReportInput {
  const result = item.result || {};
  const center = item.center || result.center || {};
  const isDiscover = (item.kind || '').startsWith('discover');
  return {
    query: item.query || result.opportunities?.[0]?.label || 'Búsqueda',
    partners: item.partners || '',
    district: item.district || result.district || '',
    radiusMeters: item.radiusMeters || result.radiusMeters || 1000,
    center: [Number(center.lat) || 0, Number(center.lng) || 0],
    generatedAt: item.createdAt ? new Date(item.createdAt) : undefined,
    detailed: Boolean(item.detailed || result.superReport),
    superReport: result.superReport || undefined,
    discover: isDiscover ? {
      district: result.district || item.district || '',
      radiusMeters: result.radiusMeters || item.radiusMeters || 1000,
      opportunities: result.opportunities || [],
      saturated: result.saturated || [],
    } : undefined,
    summary: result.summary,
    competitors: (result.places || []).map(reportPlaceFromSaved),
    partnerPlaces: (result.partners || []).map(reportPlaceFromSaved),
    zones: (result.opportunityZones || []).map((zone: any) => ({
      name: zone.name,
      district: zone.district,
      lat: zone.lat,
      lng: zone.lng,
      score: zone.score,
      competitorCount: zone.competitorCount,
      partnerCount: zone.partnerCount,
      notes: zone.notes,
      purchasingPower: zone.purchasingPower,
      commercialRent: zone.commercialRent,
      recommendation: zone.recommendation,
      populationDensity: zone.populationDensity,
      availableRentals: zone.availableRentals,
    })),
    warnings: result.warnings,
    populationStats: result.populationStats,
    footTrafficSummary: result.footTraffic?.summary,
  };
}

function SearchHistoryPopover({
  items,
  loading,
  openingId,
  downloadingId,
  exportPdfCost,
  onClose,
  onOpen,
  onRepeat,
  onDownload,
  onOpenEvaluations,
}: {
  items: SavedSearchSummary[];
  loading: boolean;
  openingId: string | null;
  downloadingId: string | null;
  exportPdfCost: number;
  onClose: () => void;
  onOpen: (id: string) => void;
  onRepeat: (item: SavedSearchSummary) => void;
  onDownload: (id: string) => void;
  onOpenEvaluations?: () => void;
}) {
  return (
    <div className="search-history-popover">
      <div className="credit-history-head">
        <div>
          <strong>Historial de búsquedas</strong>
          <span>Reabre un resultado o descarga su informe</span>
        </div>
        <button type="button" className="icon-close" onClick={onClose} aria-label="Cerrar historial">
          <X size={16} />
        </button>
      </div>
      {onOpenEvaluations && (
        <button type="button" className="search-history-evaluations" onClick={onOpenEvaluations}>
          <Store size={16} /> <span><b>Mis evaluaciones de local</b><small>Semáforo y números de los locales que evaluaste</small></span>
        </button>
      )}
      <div className="credit-history-list">
        {loading ? (
          <div className="credit-history-empty"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Cargando búsquedas...</div>
        ) : items.length === 0 ? (
          <div className="credit-history-empty">Aún no tienes búsquedas guardadas.</div>
        ) : items.map(item => (
          <div className="search-history-item" key={`${item.repeatable ? 'repeat' : 'saved'}-${item.id}`}>
            <button
              type="button"
              className="search-history-open"
              onClick={() => item.repeatable ? onRepeat(item) : onOpen(item.id)}
              disabled={openingId === item.id || downloadingId === item.id}>
              <div className="search-history-copy">
                <b>{savedSearchTitle(item)}</b>
                <span>{item.district || 'Zona guardada'}</span>
                <small>
                  {new Date(item.createdAt).toLocaleString('es-PE', { dateStyle: 'medium', timeStyle: 'short' })}
                  {' · '}
                  {item.repeatable ? 'Repetir búsqueda' : savedSearchKindLabel(item.kind)}
                  {!item.repeatable && item.detailed ? ' · detallado' : ''}
                  {item.resultsCount ? ` · ${item.resultsCount} resultados` : ''}
                </small>
              </div>
              {openingId === item.id ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={16} />}
            </button>
            {!item.repeatable && (
            <button
              type="button"
              className="search-history-download"
              onClick={() => onDownload(item.id)}
              disabled={openingId === item.id || downloadingId === item.id}
              title={item.detailed ? `Descargar informe detallado · ${exportPdfCost} créditos` : `Descargar informe · ${exportPdfCost} créditos`}
              aria-label={item.detailed ? `Descargar informe detallado · ${exportPdfCost} créditos` : `Descargar informe · ${exportPdfCost} créditos`}>
              {downloadingId === item.id ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={16} />}
            </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CreditHistoryPopover({
  entries,
  loading,
  onClose,
  billing,
  onBuyMore,
}: {
  entries: CreditLedgerEntry[];
  loading: boolean;
  onClose: () => void;
  billing: BillingStatus;
  onBuyMore: () => void;
}) {
  const renewal = billing.nextCreditRenewal ? new Date(billing.nextCreditRenewal).toLocaleDateString('es-PE') : 'el próximo mes';

  return (
    <div className="credit-history-popover">
      <div className="credit-history-head">
        <div>
          <strong>Historial de créditos</strong>
          <span>Altas, renovaciones y consumos</span>
        </div>
        <button type="button" className="icon-close" onClick={onClose} aria-label="Cerrar historial">
          <X size={16} />
        </button>
      </div>
      <div className="credit-history-summary">
        <div>
          <span className="credit-history-balance"><Wallet size={16}/><b>{billing.creditsBalance}</b> disponibles</span>
          <small>+{billing.freeRenewalCredits ?? billing.freeMonthlyCredits} gratis el {renewal}</small>
        </div>
        <button type="button" className="credit-history-buy" onClick={onBuyMore}>
          <PlusCircle size={14} /> Comprar más créditos
        </button>
      </div>
      <div className="credit-history-list">
        {loading ? (
          <div className="credit-history-empty"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Cargando movimientos...</div>
        ) : entries.length === 0 ? (
          <div className="credit-history-empty">Aún no hay movimientos de créditos.</div>
        ) : entries.map(entry => {
          const positive = entry.delta >= 0;
          return (
            <div className="credit-history-item" key={entry.id}>
              <div className={positive ? 'credit-history-icon credit-history-icon-plus' : 'credit-history-icon credit-history-icon-minus'}>
                {positive ? <PlusCircle size={16} /> : <MinusCircle size={16} />}
              </div>
              <div className="credit-history-copy">
                <b>{describeCreditEntry(entry)}</b>
                <span>{new Date(entry.createdAt).toLocaleString('es-PE', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                <small>Saldo: {entry.balanceAfter} créditos</small>
              </div>
              <strong className={positive ? 'credit-delta-positive' : 'credit-delta-negative'}>
                {positive ? '+' : ''}{entry.delta}
              </strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BillingCreditsLoadingCard() {
  return (
    <>
      <div className="billing-head">
        <h2>Créditos</h2>
        <p>Cargando saldo y compras...</p>
        <div className="billing-balance"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />...</div>
      </div>
      <small className="billing-note">Elige un paquete. Es un pago único: no hay suscripción.</small>
      <div className="topup-grid">
        {DEFAULT_TOPUPS.map(pack => (
          <button key={pack.code} className="topup-button" disabled>
            <Sparkles size={14}/>
            {pack.credits} · {formatMoney(pack.amountCents, 'PEN')}
          </button>
        ))}
      </div>
    </>
  );
}

function BillingCreditsPurchase({
  status,
  loading,
  onBuyCredits,
  title = 'Créditos',
  subtitle,
  offer = false,
}: {
  status: BillingStatus;
  loading: boolean;
  onBuyCredits: (packCode: string) => void;
  title?: string;
  subtitle?: string;
  offer?: boolean;
}) {
  const renewal = status.nextCreditRenewal ? new Date(status.nextCreditRenewal).toLocaleDateString('es-PE') : 'próximo mes';

  return (
    <>
      <div className="billing-head">
        <h2>{title}</h2>
        <p>{subtitle ?? `${status.creditsBalance} créditos disponibles`}</p>
        <div className="billing-balance"><Wallet size={16}/>{status.creditsBalance} créditos</div>
      </div>
      {!offer && (
        <>
          <div className="billing-row">
            <span>Gratis mensual</span>
            <b>{status.freeRenewalCredits ?? status.freeMonthlyCredits} créditos</b>
          </div>
          <div className="billing-row">
            <span>Renovación</span>
            <b>{renewal}</b>
          </div>
        </>
      )}
      <small className="billing-note">Elige un paquete. Es un pago único, sin suscripción.</small>
      <div className="topup-grid topup-grid-cards">
        {status.topUps.map(pack => (
          <button key={pack.code} className="topup-card" onClick={() => onBuyCredits(pack.code)} disabled={loading}>
            {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
            <b>{pack.credits.toLocaleString('es-PE')}</b>
            <small>créditos</small>
            <span>{formatMoney(pack.amountCents, status.planCurrency)}</span>
          </button>
        ))}
      </div>
      {status.testCheckoutEnabled && (
        <small className="billing-note">Checkout en modo prueba: registra compras sin cargo real. Configura Culqi para producción.</small>
      )}
    </>
  );
}

function CreditPacksStrip({
  packs,
  currency,
  loading,
  onSelect,
  onDismiss,
}: {
  packs: TopUp[];
  currency: string;
  loading: boolean;
  onSelect: (packCode: string) => void;
  onDismiss: () => void;
}) {
  return (
    <section className="pack-strip" aria-label="Paquetes de créditos">
      <div className="pack-strip-copy">
        <strong>Hay paquetes de pago</strong>
        <span>Los créditos gratis son para probar. Cada paquete se paga una sola vez, sin suscripción.</span>
      </div>
      <div className="pack-strip-grid">
        {packs.map(pack => (
          <button
            key={pack.code}
            type="button"
            className="pack-strip-button"
            onClick={() => onSelect(pack.code)}
            disabled={loading}
          >
            <b>{pack.credits.toLocaleString('es-PE')}</b>
            <small>créditos</small>
            <span>{formatMoney(pack.amountCents, currency)}</span>
          </button>
        ))}
      </div>
      <button type="button" className="pack-strip-dismiss" onClick={onDismiss} aria-label="Ocultar paquetes">
        <X size={14} />
      </button>
    </section>
  );
}

function ConfirmCreditPurchaseModal({
  pack,
  currency,
  loading,
  onConfirm,
  onClose,
}: {
  pack: TopUp;
  currency: string;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [accepted, setAccepted] = useState(false);
  const priceLabel = formatMoney(pack.amountCents, currency);

  return (
    <div className="app-modal-overlay confirm-purchase-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-purchase-title" onClick={onClose}>
      <div className="app-modal-backdrop" aria-hidden="true" />
      <div className="card billing-card app-modal-panel" onClick={e => e.stopPropagation()}>
        <button type="button" className="app-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        <div className="confirm-purchase">
          <h2 id="confirm-purchase-title">Antes de pagar</h2>
          <p className="confirm-purchase-pack">
            <b>{pack.credits.toLocaleString('es-PE')} créditos</b>
            <span>{priceLabel} · pago único</span>
          </p>
          <ToolDisclaimerAccept
            id="confirm-buy-disclaimer"
            accepted={accepted}
            onChange={setAccepted}
          />
          <button
            type="button"
            className="billing-primary"
            onClick={onConfirm}
            disabled={!accepted || loading}
          >
            {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : `Pagar ${priceLabel}`}
          </button>
          <button type="button" className="confirm-purchase-back" onClick={onClose}>
            Volver a los paquetes
          </button>
        </div>
      </div>
    </div>
  );
}

function CreditsPaywallModal({
  status,
  loading,
  onBuyCredits,
  onClose,
  title,
  subtitle,
  offer,
}: {
  status: BillingStatus | null;
  loading: boolean;
  onBuyCredits: (packCode: string) => void;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  offer?: boolean;
}) {
  return (
    <div className="app-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="app-modal-backdrop" aria-hidden="true" />
      <div className="card billing-card app-modal-panel" onClick={e => e.stopPropagation()}>
        <button type="button" className="app-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        {!status ? (
          <BillingCreditsLoadingCard />
        ) : (
          <BillingCreditsPurchase
            status={status}
            loading={loading}
            onBuyCredits={onBuyCredits}
            title={title}
            subtitle={subtitle}
            offer={offer}
          />
        )}
      </div>
    </div>
  );
}

// Purely presentational: the conversion is reported where the payment is confirmed,
// not where its receipt happens to be rendered.
function PurchaseSummaryModal({
  summary,
  onClose,
}: {
  summary: PurchaseSummary;
  onClose: () => void;
}) {
  return (
    <div className="app-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="purchase-summary-title" onClick={onClose}>
      <div className="app-modal-backdrop" aria-hidden="true" />
      <div className="card billing-card app-modal-panel purchase-summary-panel" onClick={e => e.stopPropagation()}>
        <button type="button" className="app-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        <div className="purchase-summary">
          <div className="purchase-summary-icon" aria-hidden="true">
            <CheckCircle2 size={36} />
          </div>
          <h2 id="purchase-summary-title">Pago confirmado</h2>
          <p>Tu compra se registró correctamente. Ya puedes seguir analizando con tus créditos.</p>
          <dl className="purchase-summary-details">
            <div>
              <dt>Créditos comprados</dt>
              <dd>{summary.credits.toLocaleString('es-PE')}</dd>
            </div>
            <div>
              <dt>Monto pagado</dt>
              <dd>{formatMoney(Math.round(summary.value * 100), summary.currency)}</dd>
            </div>
            <div>
              <dt>Saldo actual</dt>
              <dd>{summary.creditsBalance.toLocaleString('es-PE')}</dd>
            </div>
            <div>
              <dt>Referencia</dt>
              <dd className="purchase-summary-ref">{summary.transactionId}</dd>
            </div>
          </dl>
          <button type="button" className="billing-primary" onClick={onClose}>
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

type RubroDriver = { key: string; label: string; count: number };
type RubroOpportunity = {
  id: string;
  label: string;
  query: string;
  score: number;
  level: 'alta' | 'media' | 'baja';
  competitors: number;
  competitorsCapped?: boolean;
  validated: boolean;
  drivers: RubroDriver[];
  reason: string;
  idea?: string;
};
type RubroDiscoveryResult = {
  district: string;
  center: { lat: number; lng: number };
  radiusMeters: number;
  opportunities: RubroOpportunity[] | null;
  saturated: RubroOpportunity[] | null;
  warnings: string[] | null;
  cached: boolean;
  creditsCharged: number;
  savedSearchId?: string;
  createdAt: string;
};
type DiscoveryFeedback = { rating: SearchFeedbackRating; status: SearchFeedbackStatus };

const DEFAULT_DISCOVER_RUBRO_COST = 75;
const DEFAULT_DISCOVER_RUBRO_SUPER_COST = 150;
const DISCOVERY_RADIUS_OPTIONS = [500, 1000, 2000];

type SuperSearchInsight = { title: string; detail: string; sourceUrls?: string[] };
type SuperSearchRental = { title: string; price?: string; area?: string; address?: string; source?: string; url: string };
type SuperSearchReport = {
  executiveSummary: string;
  verdict: 'go' | 'caution' | 'avoid';
  verdictLabel: string;
  score: number;
  zoneInsights: SuperSearchInsight[];
  competitionReview: string;
  nseSummary?: string;
  risks: string[];
  nextSteps?: string[];
  rentalListings: SuperSearchRental[];
  sources: { title: string; url: string }[];
  webSearches: number;
};

const DEFAULT_SUPER_SEARCH_COST = 120;
const SUPER_SEARCH_STEPS: Array<{ label: string; at: number }> = [
  { label: 'Mapeando la competencia', at: 0 },
  { label: 'Leyendo reseñas', at: 10 },
  { label: 'Buscando locales en alquiler', at: 22 },
  { label: 'Revisando nivel socioeconómico', at: 38 },
  { label: 'Buscando noticias de la zona', at: 52 },
  { label: 'Cruzando las fuentes', at: 66 },
  { label: 'Armando tu informe', at: 85 },
];
const SUPER_SEARCH_EXPECTED_SECONDS = 100;

function SuperSearchConfirmModal({ cost, balance, district, onConfirm, onCancel }: {
  cost: number;
  balance: number;
  district: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="app-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="super-confirm-title" onClick={onCancel}>
      <div className="app-modal-backdrop" aria-hidden="true" />
      <div className="card billing-card app-modal-panel" onClick={e => e.stopPropagation()}>
        <button type="button" className="app-modal-close" onClick={onCancel} aria-label="Cerrar">✕</button>
        <div className="rubro-confirm">
          <div className="rubro-confirm-icon super-confirm-icon" aria-hidden="true"><Sparkles size={28} /></div>
          <h2 id="super-confirm-title">Súper búsqueda</h2>
          <p>Además del mapa, investigamos en internet <b>{district}</b>: locales en alquiler reales, nivel socioeconómico, noticias y lo que dicen las reseñas de tu competencia. Tarda entre 1 y 2 minutos.</p>
          <dl className="purchase-summary-details">
            <div>
              <dt>Costo</dt>
              <dd>{cost} créditos</dd>
            </div>
            <div>
              <dt>Te quedarán</dt>
              <dd>{Math.max(balance - cost, 0)} créditos</dd>
            </div>
          </dl>
          <small>Tarda 1-2 minutos. Si falla o no llega a responder, te devolvemos los créditos.</small>
          <div className="rubro-confirm-actions">
            <button type="button" className="rubro-confirm-cancel" onClick={onCancel}>Cancelar</button>
            <button type="button" className="billing-primary super-confirm-go" onClick={onConfirm}>Usar {cost} créditos</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuperSearchLoadingOverlay() {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const progress = Math.min(95, Math.round(95 * (1 - Math.exp(-elapsed / (SUPER_SEARCH_EXPECTED_SECONDS * 0.55)))));
  const activeStep = SUPER_SEARCH_STEPS.reduce((current, step, index) => (progress >= step.at ? index : current), 0);
  const minutes = Math.floor(elapsed / 60);
  const seconds = String(elapsed % 60).padStart(2, '0');

  return (
    <div className="super-loading" role="status" aria-live="polite">
      <div className="card super-loading-panel">
        <div className="loader-spinner" aria-hidden="true" />
        <h2>Investigando la zona</h2>
        <p className="super-loading-phrase">{SUPER_SEARCH_STEPS[activeStep]?.label}</p>
        <small>{minutes}:{seconds} · no cierres esta pestaña.</small>
        <div className="super-loading-bar" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
        <ol className="super-loading-steps">
          {SUPER_SEARCH_STEPS.map((step, index) => (
            <li key={step.label} className={index < activeStep ? 'is-done' : index === activeStep ? 'is-active' : ''}>
              {index < activeStep ? <CheckCircle2 size={16} aria-hidden="true" /> : <span className="super-step-dot" aria-hidden="true" />}
              {step.label}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function sourceHost(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function SuperSearchReportCard({ report, onClose, onDownload, exportingPdf, exportPdfCost }: {
  report: SuperSearchReport;
  onClose: () => void;
  onDownload?: () => void;
  exportingPdf?: boolean;
  exportPdfCost?: number;
}) {
  const verdictTone = report.verdict === 'go' ? 'good' : report.verdict === 'avoid' ? 'bad' : 'mid';
  return (
    <section className="card super-report" aria-labelledby="super-report-title">
      <div className="super-report-head">
        <div>
          <span className="super-report-kicker"><Sparkles size={14} aria-hidden="true" /> Súper búsqueda · {report.webSearches} búsquedas en internet</span>
          <h2 id="super-report-title">{report.verdictLabel || 'Informe de la zona'}</h2>
        </div>
        <div className={`super-report-score is-${verdictTone}`}>
          <b>{report.score}</b>
          <small>/100</small>
        </div>
        {onDownload && (
          <button type="button" className="super-report-download" onClick={onDownload} disabled={exportingPdf}>
            {exportingPdf ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={15} />}
            {exportingPdf ? 'Generando...' : `Descargar informe completo · ${exportPdfCost ?? 10} créditos`}
          </button>
        )}
        <button type="button" className="super-report-close" onClick={onClose} aria-label="Cerrar informe">✕</button>
      </div>
      <p className="super-report-summary">{report.executiveSummary}</p>

      {report.zoneInsights?.length > 0 && (
        <div className="super-report-insights">
          {report.zoneInsights.map((insight, i) => (
            <article key={`${insight.title}-${i}`}>
              <h3>{insight.title}</h3>
              <p>{insight.detail}</p>
              {insight.sourceUrls?.length ? (
                <small>{insight.sourceUrls.map(url => <a key={url} href={url} target="_blank" rel="noreferrer">{sourceHost(url)}</a>)}</small>
              ) : null}
            </article>
          ))}
        </div>
      )}

      <div className="super-report-columns">
        {report.competitionReview && (
          <div>
            <h3>Tu competencia</h3>
            <p>{report.competitionReview}</p>
          </div>
        )}
        {report.nseSummary && (
          <div>
            <h3>Público de la zona</h3>
            <p>{report.nseSummary}</p>
          </div>
        )}
      </div>

      {report.rentalListings?.length > 0 && (
        <div className="super-report-rentals">
          <h3>Locales en alquiler encontrados</h3>
          <ul>
            {report.rentalListings.map(rental => (
              <li key={rental.url}>
                <a href={rental.url} target="_blank" rel="noreferrer">{rental.title}</a>
                <span>{[rental.price, rental.area, rental.address].filter(Boolean).join(' · ')}</span>
                <small>{rental.source || sourceHost(rental.url)}</small>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="super-report-columns">
        {report.risks?.length > 0 && (
          <div>
            <h3>Riesgos</h3>
            <ul>{report.risks.map(risk => <li key={risk}>{risk}</li>)}</ul>
          </div>
        )}
        {report.nextSteps?.length ? (
          <div>
            <h3>Próximos pasos</h3>
            <ul>{report.nextSteps.map(step => <li key={step}>{step}</li>)}</ul>
          </div>
        ) : null}
      </div>

      {report.sources?.length > 0 && (
        <details className="super-report-sources">
          <summary>Fuentes ({report.sources.length})</summary>
          <ul>
            {report.sources.map(source => (
              <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title || sourceHost(source.url)}</a></li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function formatRadius(meters: number) {
  return meters >= 1000 ? `${(meters / 1000).toLocaleString('es-PE')} km` : `${meters} m`;
}

function RubroDiscoveryConfirmModal({
  cost,
  balance,
  district,
  radius,
  isSuper,
  onConfirm,
  onCancel,
}: {
  cost: number;
  balance: number;
  district: string;
  radius: number;
  isSuper: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="app-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rubro-confirm-title" onClick={onCancel}>
      <div className="app-modal-backdrop" aria-hidden="true" />
      <div className="card billing-card app-modal-panel" onClick={e => e.stopPropagation()}>
        <button type="button" className="app-modal-close" onClick={onCancel} aria-label="Cerrar">✕</button>
        <div className="rubro-confirm">
          <div className="rubro-confirm-icon" aria-hidden="true">{isSuper ? <Sparkles size={28} /> : <Lightbulb size={28} />}</div>
          <h2 id="rubro-confirm-title">{isSuper ? 'Súper búsqueda: qué negocio poner' : 'Búsqueda: qué negocio poner'}</h2>
          <p>{isSuper
            ? <>Investigamos todos los rubros en <b>{district}</b> (radio {formatRadius(radius)}): demanda, competencia y lo que hay en internet. Tarda entre 1 y 2 minutos.</>
            : <>Analizamos demanda y competencia de todos los rubros en <b>{district}</b> (radio {formatRadius(radius)}). Tarda alrededor de un minuto.</>}</p>
          <dl className="purchase-summary-details">
            <div>
              <dt>Costo</dt>
              <dd>{cost} créditos</dd>
            </div>
            <div>
              <dt>Te quedarán</dt>
              <dd>{Math.max(balance - cost, 0)} créditos</dd>
            </div>
          </dl>
          <small>Si falla o no llega a responder, te devolvemos los créditos. Si repites la misma zona y radio en 24 horas, no se cobra de nuevo.</small>
          <div className="rubro-confirm-actions">
            <button type="button" className="rubro-confirm-cancel" onClick={onCancel}>Cancelar</button>
            <button type="button" className="billing-primary" onClick={onConfirm}>Usar {cost} créditos</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RubroDiscoveryPanel({
  result,
  loading,
  isSuper,
  feedback,
  exportingPdf,
  searchCost,
  exportPdfCost,
  onAnalyze,
  onFeedback,
  onDownload,
}: {
  result: RubroDiscoveryResult | null;
  loading: boolean;
  isSuper: boolean;
  feedback: DiscoveryFeedback | null;
  exportingPdf?: boolean;
  searchCost: number;
  exportPdfCost: number;
  onAnalyze: (opportunity: RubroOpportunity, rank: number) => void;
  onFeedback: (rating: SearchFeedbackRating) => void;
  onDownload?: () => void;
}) {
  if (loading || !result) {
    return (
      <section className="rubro-discovery is-loading" aria-live="polite">
        <div className="loader-spinner" style={{ width: '28px', height: '28px' }}></div>
        <strong>{isSuper ? 'Súper búsqueda en marcha…' : 'Búsqueda en marcha…'}</strong>
        <p>{isSuper
          ? 'Contamos competidores, viviendas y lo que hay en internet. Puede tardar hasta 2 minutos.'
          : 'Contamos competidores y demanda en la zona. Puede tardar alrededor de un minuto.'}</p>
      </section>
    );
  }
  const opportunities = result.opportunities || [];
  const saturated = result.saturated || [];
  const warnings = result.warnings || [];
  const feedbackLocked = feedback?.status === 'sending' || feedback?.status === 'sent';
  return (
    <section className="rubro-discovery" aria-labelledby="rubro-discovery-title">
      <header className="rubro-discovery-head">
        <div>
          <p className="rubro-discovery-kicker">Mejor rubro de la zona</p>
          <h2 id="rubro-discovery-title">{result.district || 'Zona seleccionada'} · {formatRadius(result.radiusMeters)}</h2>
        </div>
        <div className="rubro-discovery-actions">
          <span className="rubro-discovery-chip">
            {result.cached ? 'Guardado · sin costo' : `${result.creditsCharged} créditos`}
          </span>
          {onDownload && (
            <button type="button" className="table-export" onClick={onDownload} disabled={exportingPdf}>
              {exportingPdf ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={15} />}
              {exportingPdf ? 'Generando...' : `${isSuper ? 'Descargar informe detallado' : 'Descargar informe'} · ${exportPdfCost} créditos`}
            </button>
          )}
        </div>
      </header>

      {opportunities.length === 0 ? (
        <p className="rubro-discovery-empty">No encontramos suficientes datos de comercios en esta zona. Prueba con un radio más grande.</p>
      ) : (
        <ol className="rubro-discovery-list">
          {opportunities.map((opp, index) => (
            <li key={opp.id} className={`rubro-opportunity is-${opp.level}`}>
              <div className="rubro-opportunity-head">
                <span className="rubro-rank" aria-hidden="true">{index + 1}</span>
                <strong>{opp.label}</strong>
                <span className="rubro-score" title="Oportunidad estimada de 0 a 100">
                  {opp.score}<small>/100</small>
                </span>
              </div>
              <div className="rubro-score-bar" aria-hidden="true"><i style={{ width: `${Math.max(opp.score, 4)}%` }} /></div>
              <p>{opp.reason}</p>
              {opp.idea && <p className="rubro-idea"><Lightbulb size={14} aria-hidden="true" /> {opp.idea}</p>}
              <div className="rubro-opportunity-foot">
                <span>
                  {opp.competitors}{opp.competitorsCapped ? '+' : ''} competidores en el radio
                  {opp.validated ? ' · verificado con Google' : ''}
                </span>
                <button type="button" onClick={() => onAnalyze(opp, index)}>
                  <Search size={14} aria-hidden="true" /> Analizar en el mapa · {searchCost} créditos
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {saturated.length > 0 && (
        <div className="rubro-saturated">
          <h3>Mejor evitar aquí</h3>
          <ul>
            {saturated.map(item => (
              <li key={item.id}>
                <b>{item.label}</b>
                <span>{item.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <p className="rubro-discovery-note"><Info size={14} aria-hidden="true" /> {warnings.join(' ')}</p>
      )}
      <p className="rubro-discovery-note">
        Estimado con datos públicos de OpenStreetMap y Google. Úsalo para priorizar y luego analiza el rubro en el mapa.
      </p>

      <div className="rubro-discovery-feedback" role="group" aria-label="Calificar este ranking">
        <span>¿Te sirvió este ranking?</span>
        <button
          type="button"
          className={feedback?.rating === 'good' ? 'feedback-choice feedback-choice-active' : 'feedback-choice'}
          aria-pressed={feedback?.rating === 'good'}
          disabled={feedbackLocked}
          onClick={() => onFeedback('good')}>
          <ThumbsUp size={15} /> Sí
        </button>
        <button
          type="button"
          className={feedback?.rating === 'bad' ? 'feedback-choice feedback-choice-active feedback-choice-bad' : 'feedback-choice'}
          aria-pressed={feedback?.rating === 'bad'}
          disabled={feedbackLocked}
          onClick={() => onFeedback('bad')}>
          <ThumbsDown size={15} /> No
        </button>
        {feedback?.status === 'sent' && <small className="feedback-note">Gracias, quedó guardado.</small>}
        {feedback?.status === 'error' && <small className="feedback-note feedback-note-error">No se pudo guardar ahora.</small>}
      </div>
    </section>
  );
}

const ANALYSIS_LOADING_ACTIONS = [
  'Analizando',
  'Revisando',
  'Procesando',
  'Comparando',
  'Evaluando',
  'Organizando',
  'Contrastando',
  'Validando',
  'Interpretando',
  'Estudiando',
  'Explorando',
  'Priorizando',
];

const ANALYSIS_LOADING_DETAILS = [
  'los negocios y aliados encontrados en la zona',
  'la distribución de competidores dentro del radio seleccionado',
  'las señales comerciales que pueden ayudarte a decidir',
  'los puntos con mayor potencial para tu búsqueda',
  'la relación entre socios cercanos y competencia',
  'las oportunidades disponibles alrededor de la ubicación',
  'los datos del mapa para ofrecerte resultados más útiles',
  'las zonas con mejores condiciones para tu negocio',
  'la información necesaria para construir el análisis',
  'los resultados para mostrarte primero lo más relevante',
];

// 12 acciones × 10 detalles = 120 mensajes distintos.
const ANALYSIS_LOADING_MESSAGES = ANALYSIS_LOADING_ACTIONS.flatMap(action =>
  ANALYSIS_LOADING_DETAILS.map(detail => `${action} ${detail}...`)
);

function AnalysisLoadingOverlay({ message }: { message?: string }) {
  const [messageIndex, setMessageIndex] = useState(() =>
    Math.floor(Math.random() * ANALYSIS_LOADING_MESSAGES.length)
  );

  useEffect(() => {
    if (message) return undefined;
    const interval = window.setInterval(() => {
      setMessageIndex(current => {
        const offset = 1 + Math.floor(Math.random() * (ANALYSIS_LOADING_MESSAGES.length - 1));
        return (current + offset) % ANALYSIS_LOADING_MESSAGES.length;
      });
    }, 3000);
    return () => window.clearInterval(interval);
  }, [message]);

  return (
    <div className="loading-overlay" role="status" aria-live="polite">
      <div className="loader-spinner"></div>
      <span key={message || messageIndex} className="loading-message">{message || ANALYSIS_LOADING_MESSAGES[messageIndex]}</span>
    </div>
  );
}

// Mirrors the region-scoped consent defaults in index.html: the banner is only
// shown where opt-in is required. Elsewhere measurement is on by default and the
// visitor can still opt out from the footer.
function consentBannerRequired() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return /^(Europe\/|Atlantic\/(Reykjavik|Canary|Madeira|Azores|Faroe))/.test(timeZone);
  } catch {
    return true;
  }
}

function GoogleConsentBanner({ onOpenPrivacy }: { onOpenPrivacy: () => void }) {
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (window.localStorage.getItem(GOOGLE_CONSENT_STORAGE_KEY)) return false;
    } catch {
      return consentBannerRequired();
    }
    return consentBannerRequired();
  });

  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(GOOGLE_CONSENT_OPEN_EVENT, reopen);
    return () => window.removeEventListener(GOOGLE_CONSENT_OPEN_EVENT, reopen);
  }, []);

  if (!open) return null;

  const choose = (choice: GoogleConsentChoice) => {
    updateGoogleConsent(choice);
    setOpen(false);
  };

  // A complementary landmark, not a modal dialog: it does not trap focus and the page
  // stays usable behind it, so role="dialog"/aria-modal would be inaccurate (and an
  // <aside> may not take the dialog role). aria-labelledby names the region instead.
  return (
    <aside className="consent-banner" aria-labelledby="google-consent-title">
      <div className="consent-copy">
        <strong id="google-consent-title">Tu privacidad en RadarBI</strong>
        <p>
          Usamos tecnologías de Google y de ChatGPT para medir registros, pagos y mejorar nuestros anuncios.
          Puedes aceptar o rechazar este uso; las funciones esenciales de RadarBI seguirán disponibles.
        </p>
        <button type="button" className="consent-privacy-link" onClick={onOpenPrivacy}>
          Ver Política de Privacidad
        </button>
      </div>
      <div className="consent-actions">
        <button type="button" className="consent-reject" onClick={() => choose('denied')}>
          Rechazar
        </button>
        <button type="button" className="consent-accept" onClick={() => choose('granted')}>
          Aceptar
        </button>
      </div>
    </aside>
  );
}


type SearchExample = {
  id: string;
  label: string;
  rubro: string;
  partners: string[];
  location: SelectedLocation;
};

const SEARCH_EXAMPLES: SearchExample[] = [
  {
    id: 'farmacia-miraflores',
    label: 'Farmacia · Miraflores',
    rubro: 'farmacia',
    partners: ['clínica', 'hospital'],
    location: { id: 'example-miraflores', name: 'Miraflores', address: 'Lima, Perú', lat: -12.1219, lng: -77.0297, source: 'example' },
  },
  {
    id: 'cevicheria-barranco',
    label: 'Cevichería · Barranco',
    rubro: 'cevichería',
    partners: ['hotel', 'centro comercial'],
    location: { id: 'example-barranco', name: 'Barranco', address: 'Lima, Perú', lat: -12.1498, lng: -77.0218, source: 'example' },
  },
  {
    id: 'gimnasio-san-borja',
    label: 'Gimnasio · San Borja',
    rubro: 'gimnasio',
    partners: ['oficina', 'supermercado'],
    location: { id: 'example-san-borja', name: 'San Borja', address: 'Lima, Perú', lat: -12.108, lng: -77.001, source: 'example' },
  },
];

const LOCATION_STATS = [
  {
    id: 'mype_survival',
    value: '1 de 3',
    label: 'micro y pequeñas empresas formales del Perú ya no opera al tercer año.',
    source: 'PRODUCE, empresas creadas en 2015',
    url: 'https://www.producempresarial.pe/tejido-empresas-tablero-sobrevivencia/',
  },
  {
    id: 'location_model',
    value: '77%',
    label: 'de acierto al predecir qué tiendas serían rentables solo con datos de su ubicación: gente que pasa, competencia y nivel de la zona.',
    source: 'Data Day Monterrey 2022, 100+ tiendas',
    url: 'https://sg.com.mx/dataday/slides/DDMty2022-ModeloLocalizacionTiendas-Ignacio.pdf',
  },
  {
    id: 'competition_radius',
    value: '226 m',
    label: 'es la distancia en la que tener menos competidores alrededor se asoció con más ventas.',
    source: 'Estudio de 12,500 tiendas, MDPI 2022',
    url: 'https://www.mdpi.com/2220-9964/11/5/302',
  },
] as const;

function LocationStats() {
  const [revealed, setRevealed] = useState<string[]>([]);

  const reveal = (statId: string, position: number) => {
    if (revealed.includes(statId)) return;
    const next = [...revealed, statId];
    setRevealed(next);
    const props = {
      stat_id: statId,
      position,
      revealed_count: next.length,
      total_stats: LOCATION_STATS.length,
    };
    trackAmplitudeEvent('Location Stat Revealed', props);
    trackMarketingDataLayer('radarbi_location_stat_revealed', props);
    if (next.length === LOCATION_STATS.length) {
      trackAmplitudeEvent('Location Stats All Revealed', { total_stats: LOCATION_STATS.length });
      trackMarketingDataLayer('radarbi_location_stats_all_revealed', { total_stats: LOCATION_STATS.length });
    }
  };

  return (
    <section className="location-stats" aria-labelledby="location-stats-title">
      <div className="location-stats-head">
        <h2 id="location-stats-title">Por qué importa dónde abres</h2>
        <p>No hay un porcentaje que sirva para todos: depende del rubro y la cuadra. Toca cada número para verlo.</p>
      </div>
      <ul className="location-stats-list">
        {LOCATION_STATS.map((stat, index) => {
          const isRevealed = revealed.includes(stat.id);
          return (
          <li key={stat.id}>
            <button
              type="button"
              className={`location-stat-value${isRevealed ? ' is-revealed' : ''}`}
              onClick={() => reveal(stat.id, index + 1)}
              aria-pressed={isRevealed}
              aria-label={isRevealed ? stat.value : 'Revelar estadística'}>
              <span className="location-stat-number" aria-hidden={!isRevealed}>{stat.value}</span>
              {!isRevealed && <span className="location-stat-hint" aria-hidden="true">Toca para ver</span>}
            </button>
            <span className="location-stat-label">{stat.label}</span>
            <a
              className="location-stat-source"
              href={stat.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAmplitudeEvent('Location Stat Source Clicked', { stat_id: stat.id })}>
              Fuente: {stat.source}
            </a>
          </li>
          );
        })}
      </ul>
    </section>
  );
}

type LandingCaseId = 'evaluate' | 'open' | 'discover';

const LANDING_CASES: Array<{
  id: LandingCaseId;
  label: string;
  icon: React.ReactNode;
  locationLabel: string;
  placeholder: string;
  hint: string;
}> = [
  {
    id: 'open',
    label: 'Voy a abrir un local',
    icon: <Store size={18} aria-hidden="true" />,
    locationLabel: 'Zona',
    placeholder: 'Distrito o avenida: Miraflores, Av. Larco…',
    hint: 'Ves la competencia y quién atrae gente a esa zona.',
  },
  {
    id: 'discover',
    label: 'No sé qué negocio poner',
    icon: <Lightbulb size={18} aria-hidden="true" />,
    locationLabel: 'Zona',
    placeholder: 'Distrito o avenida: Surco, Av. Benavides…',
    hint: 'Te decimos qué rubros tienen más demanda y menos competencia.',
  },
];

const EVALUATE_CASE: (typeof LANDING_CASES)[number] = {
  id: 'evaluate',
  label: 'Evalúa un local',
  icon: <Building2 size={18} aria-hidden="true" />,
  locationLabel: 'Dirección',
  placeholder: 'Av. Larco 345, Miraflores',
  hint: 'Te decimos si te conviene antes de firmar.',
};

function LandingCaseTabs({ value, onChange, cases = LANDING_CASES }: { value: LandingCaseId; onChange: (id: LandingCaseId) => void; cases?: typeof LANDING_CASES }) {
  return (
    <div className={`case-tabs ${cases.length > 2 ? 'case-tabs-3' : ''}`} role="tablist" aria-label="¿Qué quieres hacer?">
      {cases.map(item => (
        <button
          type="button"
          role="tab"
          key={item.id}
          aria-selected={item.id === value}
          className={item.id === value ? 'is-active' : ''}
          onClick={() => onChange(item.id)}>
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

const GOOGLE_STATIC_MAPS_KEY = typeof import.meta.env.VITE_GOOGLE_MAPS_JS_KEY === 'string'
  ? import.meta.env.VITE_GOOGLE_MAPS_JS_KEY.trim()
  : '';

const LANDING_PREVIEW_ZOOM = 16;
const LANDING_PREVIEW_CENTER: [number, number] = [landingExample.center.lat, landingExample.center.lng];

const LANDING_PREVIEW_STYLES = [
  'feature:poi.business|visibility:off',
  'feature:transit.station|element:labels.icon|visibility:off',
  'feature:poi|element:labels.text|visibility:simplified',
  'element:geometry|saturation:-18|lightness:8',
  'feature:water|color:0xc5dce8',
  'feature:road.highway|element:geometry|color:0xf2d39b',
];

function staticCirclePath(lat: number, lng: number, meters: number, steps = 36) {
  const dLat = meters / 111320;
  const dLng = meters / (111320 * Math.cos(lat * Math.PI / 180));
  const points: string[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const angle = (i / steps) * 2 * Math.PI;
    points.push(`${(lat + dLat * Math.sin(angle)).toFixed(5)},${(lng + dLng * Math.cos(angle)).toFixed(5)}`);
  }
  return points.join('|');
}

function landingPreviewMapUrl() {
  if (!GOOGLE_STATIC_MAPS_KEY) return '';
  const [lat, lng] = LANDING_PREVIEW_CENTER;
  const params = new URLSearchParams({
    center: `${lat},${lng}`,
    zoom: String(LANDING_PREVIEW_ZOOM),
    size: '640x480',
    scale: '2',
    maptype: 'roadmap',
    language: 'es',
    region: 'pe',
  });
  LANDING_PREVIEW_STYLES.forEach(style => params.append('style', style));
  params.append('path', `color:0x6366f1cc|weight:2|fillcolor:0x6366f10d|${staticCirclePath(lat, lng, landingExample.radiusMeters)}`);
  params.append('markers', `size:tiny|color:0xe11d48|${landingExample.competitors.map(p => `${p.lat},${p.lng}`).join('|')}`);
  params.append('markers', `size:tiny|color:0x2563eb|${landingExample.partners.map(p => `${p.lat},${p.lng}`).join('|')}`);
  params.append('markers', `size:small|color:0x4f46e5|${lat},${lng}`);
  params.set('key', GOOGLE_STATIC_MAPS_KEY);
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

const LANDING_PREVIEW_MAP_URL = landingPreviewMapUrl();
const LANDING_CATEGORY_LABELS: Record<string, string> = { pharmacy: 'Farmacia', clinic: 'Clínica', dentist: 'Dentista' };
const noopRadius = (_radius: number) => {};

function locationNameFromGoogleComponents(results: Array<{ address_components?: Array<{ long_name: string; types: string[] }> }>) {
  const pick = (types: string[]) => {
    for (const result of results) {
      const match = result.address_components?.find(component => types.some(type => component.types.includes(type)));
      if (match?.long_name) return match.long_name;
    }
    return '';
  };
  const name = pick(['sublocality_level_1', 'sublocality', 'neighborhood', 'locality', 'administrative_area_level_3', 'administrative_area_level_2']);
  const region = pick(['administrative_area_level_2', 'administrative_area_level_1']) || 'Perú';
  return name ? { name, address: region } : null;
}

async function reverseGeocodeDistrict(lat: number, lng: number, signal: AbortSignal): Promise<{ name: string; address: string } | null> {
  const googleNs = (window as { google?: { maps?: { Geocoder?: new () => { geocode: (req: unknown, cb: (results: unknown, status: string) => void) => void } } } }).google;
  if (googleNs?.maps?.Geocoder) {
    try {
      const geocoder = new googleNs.maps.Geocoder();
      const results = await new Promise<Array<{ address_components?: Array<{ long_name: string; types: string[] }> }>>((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng }, language: 'es', region: 'PE' }, (res, status) => {
          if (status === 'OK' && Array.isArray(res)) resolve(res);
          else reject(new Error(status));
        });
      });
      if (signal.aborted) return null;
      const parsed = locationNameFromGoogleComponents(results);
      if (parsed) return parsed;
    } catch {
      if (signal.aborted) return null;
    }
  }
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1&accept-language=es`, { signal });
    if (!res.ok) return null;
    const address = (await res.json())?.address || {};
    const name = address.suburb || address.city_district || address.neighbourhood || address.city || address.town || address.village || address.municipality;
    if (!name) return null;
    return { name, address: address.state_district || address.state || 'Perú' };
  } catch {
    return null;
  }
}

function LandingPreview({ movedCenter, onMoveCenter, onUseExample }: { movedCenter: [number, number] | null; onMoveCenter: (pos: [number, number]) => void; onUseExample: () => void }) {
  const [googleFailed, setGoogleFailed] = useState(false);
  const [MapView, setMapView] = useState<React.ComponentType<MarketMapViewProps> | null>(null);
  const useGoogle = Boolean(LANDING_PREVIEW_MAP_URL) && !googleFailed;
  const moved = Boolean(movedCenter);

  useEffect(() => {
    let live = true;
    const load = () => {
      void import('./MarketMap').then(mod => {
        if (live) setMapView(() => mod.default);
      });
    };
    if (moved) {
      load();
      return () => { live = false; };
    }
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'scroll'];
    events.forEach(type => window.addEventListener(type, load, { once: true, passive: true }));
    const idleTimer = window.setTimeout(load, 2500);
    return () => {
      live = false;
      window.clearTimeout(idleTimer);
      events.forEach(type => window.removeEventListener(type, load));
    };
  }, [moved]);

  return (
    <figure className={`landing-preview-card${MapView ? ' is-live' : ''}`}>
      <img
        className="landing-preview-static"
        src={useGoogle ? LANDING_PREVIEW_MAP_URL : '/landing-example-map.webp'}
        width={1280}
        height={960}
        alt={`Mapa de ejemplo: ${landingExample.competitors.length} ${landingExample.competitorLabel} y ${landingExample.partners.length} ${landingExample.partnerLabel} cerca de ${landingExample.center.name}`}
        decoding="async"
        onError={() => setGoogleFailed(true)}
      />
      {MapView && (
        <div className="landing-preview-live">
          <MapView
            center={movedCenter ?? LANDING_PREVIEW_CENTER}
            setCenter={onMoveCenter}
            basemap="roadmap"
            partnerHeatPlaces={[]}
            includePop={false}
            footTraffic={null}
            radius={landingExample.radiusMeters}
            setRadius={noopRadius}
            places={moved ? [] : landingExample.competitors}
            partnerPointPlaces={moved ? [] : landingExample.partners}
            rentals={[]}
            zones={[]}
            showZones={false}
            selectedPointLocation={null}
            getColorForCategory={() => '#e11d48'}
            translateCategory={category => LANDING_CATEGORY_LABELS[category] || category}
            initialZoom={LANDING_PREVIEW_ZOOM}
            compact
            centerDraggable
          />
        </div>
      )}
      <figcaption className="landing-preview-tag">{moved ? 'Tu punto de búsqueda' : `Ejemplo · ${landingExample.label}`}</figcaption>
      <button type="button" className="landing-preview-use" onClick={onUseExample}>
        Probar este ejemplo
      </button>
      {moved ? (
        <p className="landing-preview-hint">Escribe tu rubro o quién te trae clientes y toca <b>Buscar</b> para analizar este punto.</p>
      ) : (
      <div className="landing-preview-metrics">
        <div className="preview-metric">
          <b className="is-competition">{landingExample.competitors.length}</b>
          <span>{landingExample.competitorLabel} a {landingExample.radiusMeters} m</span>
        </div>
        <div className="preview-metric">
          <b className="is-partner">{landingExample.partners.length}</b>
          <span>{landingExample.partnerLabel} que te traen clientes</span>
        </div>
      </div>
      )}
      {!useGoogle && !MapView && <small className="landing-preview-credit">Mapa: Esri, OpenStreetMap</small>}
    </figure>
  );
}

function App({ authUser, onLogout, requireAuth }: { authUser: AuthUser | null; onLogout: () => void; requireAuth: () => boolean }) {
  const [category, setCategory] = useState('');
  const [competitorKeywords, setCompetitorKeywords] = useState<string[]>([]);
  const [competitorInput, setCompetitorInput] = useState('');
  const query = [...competitorKeywords, ...splitKeywordInput(competitorInput)].join(', ');
  
  const [partnersList, setPartnersList] = useState<string[]>([]);
  const [partnerInput, setPartnerInput] = useState('');
  
  const defaultLocation: SelectedLocation = { id: 'default-lima', name: 'Lima Metropolitana', address: 'Perú', lat: -12.0464, lng: -77.0428, source: 'default' };
  const [locations, setLocations] = useState<SelectedLocation[]>([]);
  const [activeLocationId, setActiveLocationId] = useState('');
  const district = locations.map(location => location.name).join(', ');
  const districtForSearch = district || defaultLocation.name;
  const districtLabel = district || 'Ubicación sin seleccionar';
  const [customCenter, setCustomCenter] = useState<[number, number]>([-12.0464, -77.0428]);
  const [landingCenterMoved, setLandingCenterMoved] = useState(false);
  const [radius, setRadius] = useState(500); // 500m default
  const [includePop, setIncludePop] = useState(false);
  const [footTraffic, setFootTraffic] = useState<FootTrafficResponse | null>(null);
  const [footTrafficLoading, setFootTrafficLoading] = useState(false);
  
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [featureNotice, setFeatureNotice] = useState('');
  const [locationSeed, setLocationSeed] = useState('');
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const sessionCreditsStartRef = useRef<{ userId: string; start: number } | null>(null);
  const [creditHistoryOpen, setCreditHistoryOpen] = useState(false);
  const [creditHistory, setCreditHistory] = useState<CreditLedgerEntry[]>([]);
  const [creditHistoryLoading, setCreditHistoryLoading] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [searchHistoryOpen, setSearchHistoryOpen] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SavedSearchSummary[]>([]);
  const [searchHistoryLoading, setSearchHistoryLoading] = useState(false);
  const [openingSavedSearchId, setOpeningSavedSearchId] = useState<string | null>(null);
  const [downloadingSavedSearchId, setDownloadingSavedSearchId] = useState<string | null>(null);
  const [showCreditsPaywall, setShowCreditsPaywall] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [pendingCreditPack, setPendingCreditPack] = useState<TopUp | null>(null);
  const [showHalfCreditsNotice, setShowHalfCreditsNotice] = useState(false);
  const [packStripDismissed, setPackStripDismissed] = useState(() => (
    authUser ? localStorage.getItem(`radarbi-packs-intro:${authUser.id}`) === '1' : false
  ));
  const [purchaseSummary, setPurchaseSummary] = useState<PurchaseSummary | null>(null);
  const [emptySearchAlert, setEmptySearchAlert] = useState(false);
  const [searchedArea, setSearchedArea] = useState<{ lat: number; lng: number; radius: number } | null>(null);
  const [zeroResultsSearch, setZeroResultsSearch] = useState<ZeroResultsSearch | null>(null);
  const [zeroResultsCardDismissedAt, setZeroResultsCardDismissedAt] = useState<number | null>(null);
  const [lastSearchContext, setLastSearchContext] = useState<SearchFeedbackContext | null>(null);
  const [feedbackRating, setFeedbackRating] = useState<SearchFeedbackRating | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState<SearchFeedbackStatus>('idle');

  const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);
  const [mapBasemap, setMapBasemap] = useState<MapBasemap>('roadmap');
  const [visibleMapLayers, setVisibleMapLayers] = useState<MapLayerVisibility>({
    competitors: true,
    partnerHeat: false,
    partnerPoints: true,
    rentals: true,
    zones: true,
  });
  const [footTrafficWarning, setFootTrafficWarning] = useState('');
  const [bestZoneData, setBestZoneData] = useState<any>(null);
  const [activeBestZone, setActiveBestZone] = useState<number | null>(null);
  const [loadingBestZone, setLoadingBestZone] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    trackAmplitudeEvent('Landing Viewed', { authenticated: Boolean(authUser), ...SIGNUP_OFFER_PROPS });
    trackTikTokEvent('ViewContent', {
      description: 'RadarBI landing: competencia y quién te trae clientes. 100 créditos gratis.',
      content_name: 'RadarBI landing',
      content_category: 'market_research',
      content_type: 'product',
      ...SIGNUP_OFFER_PROPS,
    });
    trackMarketingDataLayer('radarbi_landing_viewed', { authenticated: Boolean(authUser), ...SIGNUP_OFFER_PROPS });
    ensureGoogleTags();

    const engagementTimers = [30, 60, 180].map(seconds => window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        trackAmplitudeEvent('Page Engagement Reached', { seconds, authenticated: Boolean(authUser) });
      }
    }, seconds * 1000));

    return () => engagementTimers.forEach(timer => window.clearTimeout(timer));
  }, []);

  const allPlaces = data?.places || [];
  const allPartnersData = data?.partners || [];
  const populationData = footTraffic?.populationData || data?.populationData || [];
  const populationStats = footTraffic?.populationStats || data?.populationStats;
  const zones = data?.opportunityZones || [];
  const summary = data?.summary;
  const activeLocation = locations.find(location => location.id === activeLocationId) || null;
  const selectedPointLocation = isPreciseLocation(activeLocation) ? activeLocation : null;

  // Filter places and partners by the radius that was used at search time (not live slider)
  const [searchRadius, setSearchRadius] = useState(radius);
  const places = allPlaces.filter(p => haversineDist(customCenter[0], customCenter[1], p.lat, p.lng) <= searchRadius);
  const partnersData = allPartnersData.filter(p => haversineDist(customCenter[0], customCenter[1], p.lat, p.lng) <= searchRadius);
  const rentalPlaces = zones
    .flatMap(z => z.availableRentals || [])
    .filter((r): r is RentalProperty & { lat: number; lng: number } => typeof r.lat === 'number' && typeof r.lng === 'number');
  const visiblePlaces = visibleMapLayers.competitors
    ? places.filter(p => !hiddenCategories.includes(p.category))
    : [];
  const visiblePartnerHeatPlaces = visibleMapLayers.partnerHeat ? partnersData : [];
  const visiblePartnerPointPlaces = visibleMapLayers.partnerPoints ? partnersData : [];
  const visibleRentals = visibleMapLayers.rentals ? rentalPlaces : [];
  const visibleZones = visibleMapLayers.zones ? zones.filter(z => z.id !== 'z-center') : [];
  const rawWarnings = uniqueCleanWarnings([...(data?.warnings || []), ...(footTraffic?.warnings || []), footTrafficWarning]);
  const hasSearchResults = allPlaces.length + allPartnersData.length > 0;
  const warningMessages = hasSearchResults
    ? rawWarnings.filter(message => !isDegradedLayerWarning(message))
    : rawWarnings;
  const footTrafficReady = Boolean(
    includePop &&
    !footTrafficLoading &&
    footTraffic &&
    footTraffic.available !== false &&
    (footTraffic.segments?.length || 0) > 0 &&
    !footTrafficWarning &&
    !rawWarnings.some(isDegradedLayerWarning)
  );
  const layerNotice = hasSearchResults && includePop && !footTrafficLoading && !footTrafficReady
    ? 'La hora pico de esta calle no está disponible ahora. El mapa y los establecimientos sí se calcularon.'
    : '';

  const pendingPartner = partnerInput.trim();
  const searchPartners = pendingPartner && !partnersList.includes(pendingPartner)
    ? [...partnersList, pendingPartner]
    : partnersList;
  const hasSearchInput = Boolean(query.trim()) || searchPartners.length > 0;
  const searchAreaChanged = Boolean(searchedArea) && (
    haversineDist(searchedArea!.lat, searchedArea!.lng, customCenter[0], customCenter[1]) > 5
    || searchedArea!.radius !== radius
  );
  const searchCost = billing?.searchCost ?? DEFAULT_SEARCH_COST;
  const searchExpandCost = billing?.searchExpandCost ?? DEFAULT_SEARCH_EXPAND_COST;
  const exportCsvCost = billing?.exportCsvCost ?? DEFAULT_EXPORT_CSV_COST;
  const exportPdfCost = billing?.exportPdfCost ?? DEFAULT_EXPORT_PDF_COST;
  const suggestedExpandRadius = zeroResultsSearch ? suggestExpandedRadius(zeroResultsSearch.radius) : null;
  const showZeroResultsCard = Boolean(zeroResultsSearch) && !searchAreaChanged && !loading && !loadingBestZone && !bestZoneData;
  function expansionQualifies(center: [number, number], nextRadius: number) {
    const zero = zeroResultsSearch;
    if (!zero) return false;
    return Date.now() - zero.at < SEARCH_EXPAND_WINDOW_MS
      && zero.query === query
      && zero.partners === searchPartners.join(', ')
      && nextRadius > zero.radius
      && nextRadius <= MAX_ANALYSIS_RADIUS_METERS
      && haversineDist(zero.lat, zero.lng, center[0], center[1]) <= SEARCH_EXPAND_MAX_CENTER_DRIFT_METERS;
  }
  const refreshIsExpansion = searchAreaChanged && expansionQualifies(customCenter, radius);

  const sessionCreditsStart = authUser && sessionCreditsStartRef.current?.userId === authUser.id
    ? sessionCreditsStartRef.current.start
    : 0;
  const creditsHalfSpent = Boolean(authUser && billing?.authenticated)
    && sessionCreditsStart > 0
    && (billing?.creditsBalance ?? 0) <= sessionCreditsStart / 2;
  const showPackStrip = Boolean(authUser && billing?.authenticated)
    && !packStripDismissed
    && !showCreditsPaywall
    && !showBuyCredits
    && !showHalfCreditsNotice
    && !purchaseSummary;

  useEffect(() => {
    if (hasSearchInput) setEmptySearchAlert(false);
  }, [hasSearchInput]);

  useEffect(() => {
    if (!authUser || !billing?.authenticated || billing.creditsBalance <= 0) return;
    if (showCreditsPaywall || showBuyCredits || purchaseSummary) return;
    const noticeKey = `radarbi-products-intro:${authUser.id}`;
    if (localStorage.getItem(noticeKey)) return;
    localStorage.setItem(noticeKey, '1');
    setShowHalfCreditsNotice(true);
    trackAmplitudeEvent('Credit Products Intro Shown', {
      credits_balance: billing.creditsBalance,
    });
  }, [authUser, billing, showCreditsPaywall, showBuyCredits, purchaseSummary]);

  const packStripTracked = useRef(false);
  useEffect(() => {
    if (!showPackStrip || packStripTracked.current) return;
    packStripTracked.current = true;
    trackAmplitudeEvent('Credit Packs Shown', {
      source: 'search_strip',
      credits_balance: billing?.creditsBalance ?? 0,
    });
  }, [showPackStrip, billing?.creditsBalance]);

  // Extract unique categories from places for the legend
  const uniqueCategories = Array.from(new Set(places.map(p => p.category).filter(Boolean)));
  const toggleMapLayer = (layer: MapLayerKey) => {
    setVisibleMapLayers(prev => {
      const enabled = !prev[layer];
      trackAmplitudeEvent('Map Layer Toggled', { layer, enabled });
      return { ...prev, [layer]: enabled };
    });
  };
  const toggleCategoryVisibility = (categoryName: string) => {
    setHiddenCategories(prev => {
      const wasHidden = prev.includes(categoryName);
      trackAmplitudeEvent('Map Category Toggled', { category: categoryName, enabled: wasHidden });
      return wasHidden ? prev.filter(item => item !== categoryName) : [...prev, categoryName];
    });
  };

  const addPartner = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && partnerInput.trim()) {
      e.preventDefault();
      if (!partnersList.includes(partnerInput.trim())) {
        setPartnersList([...partnersList, partnerInput.trim()]);
      }
      setPartnerInput('');
    }
  };

  const addCompetitorKeywords = () => {
    const newKeywords = splitKeywordInput(competitorInput);
    if (newKeywords.length === 0) return;
    setCompetitorKeywords(prev => {
      const existing = new Set(prev.map(item => item.toLowerCase()));
      const merged = [...prev];
      for (const keyword of newKeywords) {
        const key = keyword.toLowerCase();
        if (!existing.has(key)) {
          merged.push(keyword);
          existing.add(key);
        }
      }
      return merged;
    });
    setCompetitorInput('');
  };

  const onCompetitorKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && competitorInput.trim()) {
      e.preventDefault();
      addCompetitorKeywords();
    }
  };

  const removeCompetitorKeyword = (keyword: string) => {
    setCompetitorKeywords(competitorKeywords.filter(item => item !== keyword));
  };

  const addLocation = (location: SelectedLocation) => {
    setLocations([location]);
    setActiveLocationId(location.id);
    setCustomCenter([location.lat, location.lng]);
    if (location.source !== 'example') setLandingCenterMoved(true);
  };

  const activateLocation = (location: SelectedLocation) => {
    setActiveLocationId(location.id);
    setCustomCenter([location.lat, location.lng]);
  };

  const reverseGeocodeAbort = useRef<AbortController | null>(null);
  const applyMapLocation = (pos: [number, number], place?: { name: string; address: string } | null) => {
    const location: SelectedLocation = {
      id: `map-pin-${pos[0].toFixed(5)},${pos[1].toFixed(5)}`,
      name: place?.name || 'Punto en el mapa',
      address: place?.address || 'Arrastra el punto para cambiar la zona',
      lat: pos[0],
      lng: pos[1],
      source: 'map',
    };
    setLocations(prev => {
      const index = prev.findIndex(item => item.id === activeLocationId);
      if (prev.length > 1 && index >= 0) return prev.map((item, i) => (i === index ? location : item));
      return [location];
    });
    setActiveLocationId(location.id);
  };
  const moveCenterFromMap = (pos: [number, number]) => {
    setCustomCenter(pos);
    setLandingCenterMoved(true);
    applyMapLocation(pos);
    reverseGeocodeAbort.current?.abort();
    const controller = new AbortController();
    reverseGeocodeAbort.current = controller;
    void reverseGeocodeDistrict(pos[0], pos[1], controller.signal).then(place => {
      if (controller.signal.aborted) return;
      applyMapLocation(pos, place);
    });
  };

  const removeLocation = (id: string) => {
    setLocations(prev => {
      const next = prev.filter(location => location.id !== id);
      const safeNext = next;
      if (id === activeLocationId) {
        const fallback = safeNext[safeNext.length - 1];
        setActiveLocationId(fallback?.id || '');
        setCustomCenter(fallback ? [fallback.lat, fallback.lng] : [defaultLocation.lat, defaultLocation.lng]);
      }
      return safeNext;
    });
  };

  const removePartner = (p: string) => {
    setPartnersList(partnersList.filter(item => item !== p));
  };

  const [hasSearched, setHasSearched] = useState(false);
  const [landingCase, setLandingCase] = useState<LandingCaseId>('open');
  const showLanding = !hasSearched && !authUser;
  const evalConfig = useEvalConfig(apiFetch, authUser?.id);
  const evalAvailable = Boolean(evalConfig && (evalConfig.enabled || evalConfig.earlyAccess));
  const evalEnabled = Boolean(evalConfig?.enabled);
  const landingCases = evalAvailable ? [EVALUATE_CASE, ...LANDING_CASES] : LANDING_CASES;
  const isEvaluateMode = landingCase === 'evaluate' && evalAvailable;
  const [evalFlow, setEvalFlow] = useState<{ start: EvalStartContext; evaluationId?: string } | null>(null);
  const [evalShareToken, setEvalShareToken] = useState(() => new URLSearchParams(window.location.search).get('evaluacion') || '');
  const [evalOutcomeId, setEvalOutcomeId] = useState(() => new URLSearchParams(window.location.search).get('evaluacion_resultado') || '');
  const evalDefaultApplied = useRef(false);
  const activeLandingCase = LANDING_CASES.find(item => item.id === landingCase) || LANDING_CASES[0];
  const isDiscoverMode = landingCase === 'discover';
  const [rubroDiscovery, setRubroDiscovery] = useState<RubroDiscoveryResult | null>(null);
  const [discoveringRubro, setDiscoveringRubro] = useState(false);
  const [showDiscoverConfirm, setShowDiscoverConfirm] = useState(false);
  const [pendingDiscoverSuper, setPendingDiscoverSuper] = useState(false);
  const [discoveryRadius, setDiscoveryRadius] = useState(1000);
  const [discoveryFeedback, setDiscoveryFeedback] = useState<DiscoveryFeedback | null>(null);
  const [discoverLocationAlert, setDiscoverLocationAlert] = useState(false);
  const [superReport, setSuperReport] = useState<SuperSearchReport | null>(null);
  const [superSearching, setSuperSearching] = useState(false);
  const [showSuperConfirm, setShowSuperConfirm] = useState(false);
  const superSearchCost = billing?.superSearchCost ?? DEFAULT_SUPER_SEARCH_COST;
  const superSearchAvailable = Boolean(billing?.superSearchAvailable);
  const [pendingSearch, setPendingSearch] = useState(false);
  const discoverRubroCost = billing?.discoverRubroCost ?? DEFAULT_DISCOVER_RUBRO_COST;
  const discoverRubroSuperCost = billing?.discoverRubroSuperCost ?? DEFAULT_DISCOVER_RUBRO_SUPER_COST;
  const showDiscoveryPanel = isDiscoverMode && (discoveringRubro || Boolean(rubroDiscovery));

  useEffect(() => {
    if (locations.length > 0) setDiscoverLocationAlert(false);
  }, [locations.length]);

  useEffect(() => {
    if (!evalAvailable || evalDefaultApplied.current) return;
    evalDefaultApplied.current = true;
    const params = new URLSearchParams(window.location.search);
    const hasOwnParams = [...params.keys()].some(key => !key.startsWith('utm_') && !key.startsWith('evaluacion') && key !== 'ttclid' && key !== 'gclid' && key !== 'fbclid');
    if (!hasSearched && !hasOwnParams) setLandingCase('evaluate');
  }, [evalAvailable, hasSearched]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const evaluationId = params.get('evaluacion_id');
    if (evaluationId) setEvalFlow({ start: { source: 'email' }, evaluationId });
    const unsubscribe = params.get('baja_evaluaciones');
    if (unsubscribe) {
      apiFetch('/api/evaluations/unsubscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: unsubscribe }) })
        .then(res => setFeatureNotice(res.ok ? 'Listo: ya no te enviaremos recordatorios ni encuestas de evaluaciones.' : 'El enlace para darte de baja no es válido.'))
        .catch(() => setFeatureNotice('No pudimos darte de baja ahora. Intenta de nuevo en un momento.'));
      params.delete('baja_evaluaciones');
      window.history.replaceState(null, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
    }
  }, []);

  const clearEvalParam = (key: string) => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(key)) return;
    params.delete(key);
    window.history.replaceState(null, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
  };

  const openEvaluateFlow = (start: EvalStartContext, evaluationId?: string) => {
    setAccountMenuOpen(false);
    setSearchHistoryOpen(false);
    setEvalFlow({ start, evaluationId });
  };

  const closeEvaluateFlow = useCallback(() => {
    setEvalFlow(null);
    clearEvalParam('evaluacion_id');
  }, []);

  const evalDeps: EvalDeps = {
    apiFetch,
    requestCulqiToken,
    isPaymentCancelled: error => error instanceof CulqiCancelledError,
    user: authUser ? { id: authUser.id, email: authUser.email } : null,
    requireAuth,
    creditsBalance: billing?.authenticated ? billing.creditsBalance : null,
    onBillingChanged: () => void loadBilling(),
    onBuyCredits: (source) => openBuyCredits(source),
    onBuyCreditPack: (packCode) => requestBuyCredits(packCode),
    creditPacks: (billing?.topUps.length ? billing.topUps : DEFAULT_TOPUPS).map((pack) => ({
      code: pack.code,
      credits: pack.credits,
      priceLabel: formatMoney(pack.amountCents, billing?.planCurrency || 'PEN'),
    })),
    buyingCredits: billingLoading,
    siteUrl: window.location.origin,
  };

  const selectLandingCase = (id: LandingCaseId) => {
    if (id === landingCase) return;
    setLandingCase(id);
    setEmptySearchAlert(false);
    setDiscoverLocationAlert(false);
    trackAmplitudeEvent('Landing Case Selected', { case_id: id });
    trackMarketingDataLayer('radarbi_landing_case_selected', { case_id: id });
  };

  const openSignupOffer = (source: string) => {
    trackAmplitudeEvent('Signup Offer Clicked', { source, ...SIGNUP_OFFER_PROPS });
    trackTikTokEvent('ClickButton', {
      description: 'Regístrate y gana 100 créditos gratis',
      content_name: 'signup_offer',
      content_category: 'account',
      ...SIGNUP_OFFER_PROPS,
    });
    trackMarketingDataLayer('radarbi_signup_offer_clicked', { source, ...SIGNUP_OFFER_PROPS });
    requireAuth();
  };

  const applySearchExample = (example: SearchExample, source: string) => {
    setCompetitorKeywords([example.rubro]);
    setCompetitorInput('');
    setPartnersList(example.partners);
    setPartnerInput('');
    setLocations([example.location]);
    setActiveLocationId(example.location.id);
    setCustomCenter([example.location.lat, example.location.lng]);
    trackAmplitudeEvent('Search Example Applied', { example_id: example.id, source });
    trackMarketingDataLayer('radarbi_search_example_applied', { example_id: example.id, source });
    window.requestAnimationFrame(() => {
      document.getElementById('tour-action-bar')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };
  const currentFetchId = useRef(0);
  const restoredAnalysisId = useRef('');

  const loadBilling = useCallback(async () => {
    try {
      const res = await apiFetch('/api/billing/status');
      if (!res.ok) return;
      setBilling(await res.json());
    } catch {
      // Billing is optional when the backend is running without DB.
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    loadBilling();
  }, [authUser, loadBilling]);

  useEffect(() => {
    if (!authUser) {
      sessionCreditsStartRef.current = null;
      return;
    }
    if (!billing?.authenticated) return;
    const current = sessionCreditsStartRef.current;
    if (!current || current.userId !== authUser.id) {
      sessionCreditsStartRef.current = { userId: authUser.id, start: billing.creditsBalance };
      return;
    }
    if (current.start === 0 && billing.creditsBalance > 0) {
      sessionCreditsStartRef.current = { userId: authUser.id, start: billing.creditsBalance };
    }
  }, [authUser, billing]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rubro = (params.get('rubro') || '').trim();
    const socios = (params.get('socios') || '').trim();
    const ubicacion = (params.get('ubicacion') || '').trim();
    if (rubro) setCompetitorKeywords(splitKeywordInput(rubro));
    if (socios) setPartnersList(splitKeywordInput(socios));
    if (ubicacion) setLocationSeed(ubicacion);
  }, []);

  useEffect(() => {
    if (!searchHistoryOpen && !creditHistoryOpen && !accountMenuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setSearchHistoryOpen(false);
      setCreditHistoryOpen(false);
      setAccountMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchHistoryOpen, creditHistoryOpen, accountMenuOpen]);

  useEffect(() => {
    if (!authUser) {
      setPackStripDismissed(false);
      return;
    }
    setPackStripDismissed(localStorage.getItem(`radarbi-packs-intro:${authUser.id}`) === '1');
  }, [authUser]);

  useEffect(() => {
    if (!includePop) {
      setFootTraffic(null);
      setFootTrafficLoading(false);
      setFootTrafficWarning('');
      setErr(prev => (prev.includes('afluencia') || prev.includes('OSM')) ? '' : prev);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      if (!authUser) {
        setFootTrafficLoading(false);
        return;
      }
      setFootTrafficLoading(true);
      setFootTrafficWarning('');
      try {
        const res = await apiFetch('/api/market/foot-traffic', {
          method: 'POST',
          signal: controller.signal,
          body: JSON.stringify({
            district: districtForSearch,
            radiusMeters: radius,
            center: { lat: customCenter[0], lng: customCenter[1] },
          }),
        } as RequestInit);
        if (!res.ok) throw new Error(await readAPIError(res));
        const payload = await res.json();
        setFootTraffic(payload);
        setFootTrafficWarning('');
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          setFootTraffic(null);
          setFootTrafficWarning(cleanProviderWarning(e.message || 'No se pudo cargar la capa de afluencia'));
        }
      } finally {
        if (!controller.signal.aborted) setFootTrafficLoading(false);
      }
    }, 500);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [includePop, radius, customCenter[0], customCenter[1], districtForSearch, authUser]);

  async function loadCreditHistory() {
    if (!authUser) return;
    setCreditHistoryLoading(true);
    try {
      const res = await apiFetch('/api/billing/credits/history');
      if (!res.ok) throw new Error(await readAPIError(res));
      const payload = await res.json();
      setCreditHistory(payload.entries || []);
    } catch (e: any) {
      setErr(e.message || 'No se pudo cargar el historial de créditos');
    } finally {
      setCreditHistoryLoading(false);
    }
  }

  function openBuyCredits(source: string) {
    trackAmplitudeEvent('Top Up Opened', { source, credits_balance: billing?.creditsBalance ?? 0 });
    trackMarketingDataLayer('radarbi_topup_opened', { source });
    setCreditHistoryOpen(false);
    setSearchHistoryOpen(false);
    setAccountMenuOpen(false);
    setShowBuyCredits(true);
  }

  function dismissPackStrip() {
    if (authUser) localStorage.setItem(`radarbi-packs-intro:${authUser.id}`, '1');
    setPackStripDismissed(true);
    trackAmplitudeEvent('Credit Packs Dismissed', { credits_balance: billing?.creditsBalance ?? 0 });
  }

  function selectCreditPack(packCode: string, source: string) {
    trackAmplitudeEvent('Credit Pack Selected', { pack_code: packCode, source, credits_balance: billing?.creditsBalance ?? 0 });
    if (billing?.topUps.some(pack => pack.code === packCode) || DEFAULT_TOPUPS.some(pack => pack.code === packCode)) {
      requestBuyCredits(packCode);
      return;
    }
    openBuyCredits(source);
  }

  async function toggleCreditHistory() {
    if (!creditHistoryOpen) {
      trackAmplitudeEvent('Credits Menu Opened', { source: 'header_pill', credits_balance: billing?.creditsBalance ?? 0 });
      trackMarketingDataLayer('radarbi_credits_menu_opened', { source: 'header_pill' });
      setSearchHistoryOpen(false);
      setAccountMenuOpen(false);
      setCreditHistoryOpen(true);
      await loadCreditHistory();
    } else {
      setCreditHistoryOpen(false);
    }
  }

  async function loadSearchHistory() {
    setSearchHistoryLoading(true);
    try {
      const res = await apiFetch('/api/searches');
      if (!res.ok) throw new Error(await readAPIError(res));
      const payload = await res.json();
      setSearchHistory(Array.isArray(payload) ? payload : []);
    } catch (e: any) {
      setErr(e.message || 'No se pudo cargar el historial de búsquedas');
    } finally {
      setSearchHistoryLoading(false);
    }
  }

  async function toggleSearchHistory() {
    if (!searchHistoryOpen) {
      setCreditHistoryOpen(false);
      setAccountMenuOpen(false);
      setSearchHistoryOpen(true);
      trackAmplitudeEvent('Search History Opened');
      trackAmplitudeEvent('history_opened');
      await loadSearchHistory();
    } else {
      setSearchHistoryOpen(false);
    }
  }

  async function openSavedSearch(id: string) {
    setOpeningSavedSearchId(id);
    setErr('');
    try {
      const res = await apiFetch(`/api/searches/${id}`);
      if (!res.ok) throw new Error(await readAPIError(res));
      const item: SavedSearch = await res.json();
      const result = item.result || {};
      const center = item.center || result.center;
      if (center?.lat && center?.lng) {
        const location: SelectedLocation = {
          id: `saved-${item.id}`,
          name: item.district || result.district || 'Zona guardada',
          address: item.district || result.district || '',
          lat: center.lat,
          lng: center.lng,
          source: 'saved',
        };
        setLocations([location]);
        setActiveLocationId(location.id);
        setCustomCenter([center.lat, center.lng]);
        setSearchedArea({ lat: center.lat, lng: center.lng, radius: item.radiusMeters || result.radiusMeters || 1000 });
      }
      if ((item.kind || '').startsWith('discover')) {
        setLandingCase('discover');
        setHasSearched(true);
        setRubroDiscovery(result);
        setPendingDiscoverSuper(item.detailed);
        setDiscoveryRadius(item.radiusMeters || result.radiusMeters || 1000);
        setSuperReport(null);
        setData(null);
      } else {
        const keywords = (item.query || '').split(',').map(value => value.trim()).filter(Boolean);
        const partners = (item.partners || '').split(',').map(value => value.trim()).filter(Boolean);
        setLandingCase('open');
        setHasSearched(true);
        setRubroDiscovery(null);
        setCompetitorKeywords(keywords);
        setCompetitorInput('');
        setPartnersList(partners);
        setPartnerInput('');
        if (item.radiusMeters) {
          setRadius(item.radiusMeters);
          setSearchRadius(item.radiusMeters);
        }
        setData(result);
        setSuperReport(item.kind === 'market.super_search' ? result.superReport || null : null);
        setZeroResultsSearch(null);
      }
      setSearchHistoryOpen(false);
      if (item.id) {
        window.history.replaceState({ analysisId: item.id }, '', `/analisis/${item.id}`);
      }
      trackAmplitudeEvent('Saved Search Opened', { kind: item.kind, detailed: item.detailed });
      trackAmplitudeEvent('result_reopened', { kind: item.kind });
      window.requestAnimationFrame(() => {
        document.getElementById(item.kind.startsWith('discover') ? 'rubro-discovery-title' : 'tour-map')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (e: any) {
      setErr(e.message || 'No pudimos abrir esa búsqueda');
    } finally {
      setOpeningSavedSearchId(null);
    }
  }

  useEffect(() => {
    const id = readAnalysisIdFromLocation();
    if (!id) return;
    if (!authUser) {
      setFeatureNotice('Inicia sesión para volver a ver este análisis. Abrirlo no vuelve a cobrar.');
      return;
    }
    if (restoredAnalysisId.current === id) return;
    restoredAnalysisId.current = id;
    void openSavedSearch(id);
  }, [authUser]);

  function registerFeatureInterest(feature: string) {
    trackAmplitudeEvent('feature_interest', { feature });
    setFeatureNotice('Quedó registrado tu interés.');
  }

  async function repeatPastSearch(item: SavedSearchSummary) {
    setOpeningSavedSearchId(item.id);
    setSearchHistoryOpen(false);
    setErr('');
    setFeatureNotice('');
    try {
      const res = await apiFetch('/api/searches/repeat', {
        method: 'POST',
        body: JSON.stringify({ id: item.id }),
      });
      if (!res.ok) throw new Error(await readAPIError(res));
      const result = await res.json();
      const center = item.center || result.center;
      if (center?.lat && center?.lng) {
        const location: SelectedLocation = {
          id: `repeat-${item.id}`,
          name: item.district || result.district || 'Zona guardada',
          address: item.district || '',
          lat: center.lat,
          lng: center.lng,
          source: 'saved',
        };
        setLocations([location]);
        setActiveLocationId(location.id);
        setCustomCenter([center.lat, center.lng]);
        setSearchedArea({ lat: center.lat, lng: center.lng, radius: item.radiusMeters || result.radiusMeters || 500 });
      }
      if ((item.kind || '').startsWith('discover')) {
        setLandingCase('discover');
        setHasSearched(true);
        setRubroDiscovery(result);
        setData(null);
        setSuperReport(null);
      } else {
        const keywords = (item.query || '').split(',').map(value => value.trim()).filter(Boolean);
        const partners = (item.partners || '').split(',').map(value => value.trim()).filter(Boolean);
        setLandingCase('open');
        setHasSearched(true);
        setRubroDiscovery(null);
        setCompetitorKeywords(keywords);
        setPartnersList(partners);
        if (item.radiusMeters) {
          setRadius(item.radiusMeters);
          setSearchRadius(item.radiusMeters);
        }
        setData(result);
        setSuperReport(item.kind === 'market.super_search' ? result.superReport || null : null);
      }
      const savedId = result.savedSearchId as string | undefined;
      updateSearchMeta({
        query: item.query || '',
        district: item.district || '',
        partners: item.partners || '',
        resultsCount: item.resultsCount || 0,
        analysisId: savedId,
      });
      trackAmplitudeEvent('search_completed', { source: 'history_repeat', kind: item.kind, ok: true });
      await loadBilling();
      const sameDayDiscover = (item.kind || '').startsWith('discover') && result.cached;
      setFeatureNotice(sameDayDiscover
        ? 'Abrimos de nuevo el mismo análisis de rubro. No se cobró.'
        : (item.kind || '').startsWith('discover')
          ? 'Repetimos el análisis de rubro.'
          : 'Repetimos la búsqueda. Se cobra como un análisis nuevo.');
    } catch (e: any) {
      setErr(humanizeUserError(e?.message, 'No pudimos repetir esa búsqueda. Intenta de nuevo.'));
      trackAmplitudeEvent('search_failed', { source: 'history_repeat', kind: item.kind });
      await loadBilling();
    } finally {
      setOpeningSavedSearchId(null);
    }
  }

  async function readAPIError(res: Response) {
    const raw = await res.text();
    let payload: any = raw;
    try {
      payload = raw ? JSON.parse(raw) : {};
    } catch {
      payload = raw;
    }
    if (res.status === 401) {
      requireAuth();
      return 'Inicia sesión o regístrate para continuar.';
    }
    if (res.status === 402) {
      await loadBilling();
      setShowCreditsPaywall(true);
      return typeof payload === 'object'
        ? `Necesitas créditos para esta acción (${payload.cost || 0} créditos).`
        : 'Necesitas créditos para continuar.';
    }
    const technical = typeof payload === 'object' ? (payload.error || JSON.stringify(payload)) : String(payload || '');
    if (res.status >= 500 || res.status === 404) {
      console.info('[radarbi] api.error', { status: res.status, body: technical.slice(0, 180) });
    }
    return humanizeUserError(technical, 'No pudimos completar eso. Intenta de nuevo en un momento.');
  }

  function requireCredits(): boolean {
    if (!requireAuth()) return false;
    if (!billing) {
      void loadBilling();
      setErr('Cargando información de créditos...');
      return false;
    }
    if (billing.creditsBalance <= 0) {
      setShowCreditsPaywall(true);
      return false;
    }
    return true;
  }

  function requestBuyCredits(packCode: string) {
    if (!requireAuth()) return;
    const pack = (billing?.topUps.length ? billing.topUps : DEFAULT_TOPUPS).find(item => item.code === packCode);
    if (!pack) {
      setErr('Paquete de créditos no disponible');
      return;
    }
    setPendingCreditPack(pack);
  }

  async function buyCredits(packCode: string) {
    if (!requireAuth()) return;
    const pack = billing?.topUps.find(item => item.code === packCode);
    if (!pack) { setErr('Paquete de créditos no disponible'); return; }
    const currency = billing?.planCurrency || 'PEN';
    const value = moneyFromCents(pack.amountCents);
    // A test-mode checkout grants credits without charging anything, so it must never
    // reach Google Ads: those would be conversions nobody paid for.
    const chargesRealMoney = !billing?.testCheckoutEnabled;
    const paymentPayload = tiktokTopUpPayload(pack, currency);
    const purchaseEventId = createTikTokEventId('purchase', pack.code);
    setBillingLoading(true); setErr('');
    try {
      trackTikTokEvent('InitiateCheckout', {
        ...paymentPayload,
        status: 'started',
      }, createTikTokEventId('checkout', pack.code));

      const tokenId = billing?.requiresPaymentToken
        ? await requestCulqiToken({
          publicKey: billing.culqiPublicKey,
          amountCents: pack.amountCents,
          description: `RadarBI · ${pack.credits} créditos`,
          email: authUser?.email,
        })
        : '';
      if (tokenId) {
        trackTikTokEvent('AddPaymentInfo', {
          ...paymentPayload,
          status: 'tokenized',
        }, createTikTokEventId('paymentinfo', pack.code));
      }
      const res = await apiFetch('/api/billing/credits/buy', {
        method: 'POST',
        body: JSON.stringify({ tokenId, packCode, tiktokEventId: purchaseEventId, ...chatGPTAttribution() })
      });
      if (!res.ok) throw new Error(await readAPIError(res));
      const nextBilling = await res.json();
      if (authUser && sessionCreditsStartRef.current?.userId === authUser.id) {
        sessionCreditsStartRef.current = {
          userId: authUser.id,
          start: sessionCreditsStartRef.current.start + pack.credits,
        };
      }
      setBilling(nextBilling);
      if (nextBilling.creditsBalance > 0) setShowCreditsPaywall(false);
      setShowBuyCredits(false);
      setShowHalfCreditsNotice(false);
      setPendingCreditPack(null);
      trackTikTokEvent('Purchase', {
        ...paymentPayload,
        order_id: purchaseEventId,
        status: 'success',
        credits_balance: nextBilling.creditsBalance,
      }, purchaseEventId);
      trackAmplitudeEvent('Credits Purchased', { pack_code: pack.code, credits: pack.credits });
      // Only a charge the backend settled reaches this line — the same point where it
      // notifies Discord — so Google Ads hears about paid purchases and nothing else.
      // Both hits go out: the page_view feeds the URL-based goal and the conversion
      // event covers the same action configured by event, whichever Ads is using.
      // Enhanced conversions need a real email on the hit — never fire with blanks.
      if (chargesRealMoney) {
        const purchaseEmail = authUser?.email || undefined;
        if (purchaseEmail) setGoogleUserData({ email: purchaseEmail });
        sendPurchasePageView({ email: purchaseEmail });
        trackGooglePurchase({
          value,
          currency,
          transactionId: purchaseEventId,
          credits: pack.credits,
          email: purchaseEmail,
        });
        if (nextBilling.chatgptEventId) {
          void trackChatGPTSubscription({
            eventId: nextBilling.chatgptEventId,
            planId: nextBilling.chatgptPlanId || pack.code,
            amountCents: nextBilling.chatgptAmountCents || pack.amountCents,
            currency: nextBilling.chatgptCurrency || currency,
            email: purchaseEmail,
            userId: authUser?.id,
          });
        }
      }
      setPurchaseSummary({
        credits: pack.credits,
        value,
        currency,
        transactionId: purchaseEventId,
        packCode: pack.code,
        creditsBalance: nextBilling.creditsBalance,
      });
      if (authUser) {
        localStorage.setItem(`radarbi-packs-intro:${authUser.id}`, '1');
        setPackStripDismissed(true);
      }
    } catch (e: any) {
      // Closing the Culqi modal is a normal choice, not an error worth alarming about.
      if (e instanceof CulqiCancelledError) {
        trackAmplitudeEvent('Credits Purchase Cancelled', { pack_code: pack.code, credits: pack.credits });
      } else {
        setErr(e.message || 'No se pudo comprar créditos');
      }
    } finally {
      setBillingLoading(false);
    }
  }

  function closePurchaseSummary() {
    setPurchaseSummary(null);
  }

  async function search(options: { radiusOverride?: number; expand?: boolean } = {}) {
    if (!hasSearchInput) {
      setEmptySearchAlert(true);
      trackAmplitudeEvent('Market Search Blocked Empty');
      document.getElementById('competitor-keyword-input')?.focus();
      return;
    }
    if (!requireCredits()) return;
    if (searchPartners !== partnersList) {
      setPartnersList(searchPartners);
      setPartnerInput('');
    }
    const searchCenter = customCenter;
    const searchRadiusUsed = options.radiusOverride ?? radius;
    if (searchRadiusUsed !== radius) setRadius(searchRadiusUsed);
    const expansion = expansionQualifies(searchCenter, searchRadiusUsed);
    const expansionRequested = Boolean(options.expand) || expansion;
    trackAmplitudeEvent('Market Search Started', {
      category: category || 'unspecified',
      district: districtForSearch || 'unspecified',
      radius_meters: searchRadiusUsed,
      partners_count: searchPartners.length,
      refresh: Boolean(searchedArea),
      expansion,
      ...(expansion && zeroResultsSearch ? { previous_radius_meters: zeroResultsSearch.radius } : {}),
    });
    setHasSearched(true);
    setSearchRadius(searchRadiusUsed); // Lock the filter radius to what was used at search time
    setBestZoneData(null);
    setActiveBestZone(null);
    setSuperReport(null);
    const fetchId = ++currentFetchId.current;
    setFeedbackRating(null);
    setFeedbackComment('');
    setFeedbackStatus('idle');
    setLastSearchContext(null);
    setLoading(true); setErr('');
    const searchStarted = performance.now();
    try {
      const partnersStr = searchPartners.join(', ');
      const searchId = createTikTokEventId('search', 'market');
      const res = await apiFetch('/api/market/search', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ query, category, partners: partnersStr, district: districtForSearch, radiusMeters: searchRadiusUsed, center: { lat: searchCenter[0], lng: searchCenter[1] }, includeOsm: true, includeGoogle: true, includePopulation: false, maxResults: 1000, tiktokEventId: searchId, tiktokEventCategory: 'market_search', ...(expansionRequested ? { expandFromZeroResults: true } : {}) })
      });
      console.info('[radarbi] market.search', {
        duration_ms: Math.round(performance.now() - searchStarted),
        query,
        district: districtForSearch,
        ok: res.ok,
      });
      
      if (fetchId !== currentFetchId.current) return; // Prevent race conditions
      
      if (!res.ok) throw new Error(await readAPIError(res));
      const result = await res.json();
      const resultsCount = (result.places?.length || 0) + (result.partners?.length || 0);
      const resultsInRadius = countPlacesWithinRadius([...(result.places || []), ...(result.partners || [])], searchCenter[0], searchCenter[1], searchRadiusUsed);
      setData(result);
      setSearchedArea({ lat: searchCenter[0], lng: searchCenter[1], radius: searchRadiusUsed });
      if (resultsInRadius === 0) {
        const nextRadius = suggestExpandedRadius(searchRadiusUsed);
        setZeroResultsSearch({ lat: searchCenter[0], lng: searchCenter[1], radius: searchRadiusUsed, query, partners: partnersStr, at: Date.now() });
        trackAmplitudeEvent('Zero Results Shown', {
          category: category || 'unspecified',
          district: districtForSearch || 'unspecified',
          radius_meters: searchRadiusUsed,
          suggested_radius_meters: nextRadius ?? 'none',
          at_max_radius: nextRadius === null,
          expansion,
        });
      } else {
        setZeroResultsSearch(null);
      }
      setVisibleMapLayers(prev => ({
        ...prev,
        partnerHeat: false,
        partnerPoints: true,
      }));
      const searchContext = {
        searchId,
        action: 'market.search',
        query,
        category,
        partners: partnersStr,
        district: districtForSearch,
        radiusMeters: searchRadiusUsed,
        center: { lat: searchCenter[0], lng: searchCenter[1] },
        resultsCount,
        warnings: result.warnings || [],
        createdAt: new Date().toISOString(),
      };
      setLastSearchContext(searchContext);
      trackTikTokEvent('Search', {
        search_string: query.trim() || category.trim() || districtForSearch,
        description: 'RadarBI: competencia y quién te trae clientes',
        event_category: 'market_search',
        query,
        category,
        district: districtForSearch,
        partners: partnersStr,
        partners_count: searchPartners.length,
        radius_meters: searchRadiusUsed,
        results_count: resultsCount,
        competitors_count: result.places?.length || 0,
        partners_found_count: result.partners?.length || 0,
        status: 'success',
      }, searchId);
      updateSearchMeta({ query, district: districtForSearch, partners: partnersStr, resultsCount, analysisId: result.savedSearchId });
      trackAmplitudeEvent('search_completed', { ok: true, kind: 'market.search', results_count: resultsCount });
      trackAmplitudeEvent('Market Search Completed', {
        category: category || 'unspecified',
        district: districtForSearch || 'unspecified',
        radius_meters: searchRadiusUsed,
        results_count: resultsCount,
        results_in_radius: resultsInRadius,
        partners_count: searchPartners.length,
        expansion,
      });
      await loadBilling();
      setHiddenCategories([]);
    } catch (e:any) {
      if (fetchId === currentFetchId.current) {
        console.info('[radarbi] market.search', {
          duration_ms: Math.round(performance.now() - searchStarted),
          query,
          district: districtForSearch,
          ok: false,
        });
        setErr(friendlyNetworkError(e, 'No pudimos completar la búsqueda. Intenta de nuevo en un momento.'));
        trackAmplitudeEvent('search_failed', { kind: 'market.search' });
        trackAmplitudeEvent('Market Search Failed');
      }
    }
    finally { 
      if (fetchId === currentFetchId.current) setLoading(false); 
    }
  }

  function requestSuperSearch() {
    if (!hasSearchInput) {
      setEmptySearchAlert(true);
      trackAmplitudeEvent('Super Search Blocked Empty');
      document.getElementById('competitor-keyword-input')?.focus();
      return;
    }
    if (!requireAuth()) return;
    if (!billing) {
      void loadBilling();
      setErr('Cargando información de créditos...');
      return;
    }
    if (!superSearchAvailable) {
      registerFeatureInterest('super_search');
      return;
    }
    if (billing.creditsBalance < superSearchCost) {
      trackAmplitudeEvent('Super Search Insufficient Credits', { credits_balance: billing.creditsBalance, cost: superSearchCost });
      setErr(`La súper búsqueda usa ${superSearchCost} créditos y te quedan ${billing.creditsBalance}.`);
      openBuyCredits('super_search');
      return;
    }
    setErr('');
    setShowSuperConfirm(true);
    trackAmplitudeEvent('Super Search Confirm Shown', { cost: superSearchCost });
  }

  async function runSuperSearch() {
    setShowSuperConfirm(false);
    if (searchPartners !== partnersList) {
      setPartnersList(searchPartners);
      setPartnerInput('');
    }
    const searchCenter = customCenter;
    const searchRadiusUsed = radius;
    const partnersStr = searchPartners.join(', ');
    trackAmplitudeEvent('Super Search Confirmed', { district: districtForSearch || 'unspecified', radius_meters: searchRadiusUsed, cost: superSearchCost });
    setHasSearched(true);
    setSearchRadius(searchRadiusUsed);
    setBestZoneData(null);
    setActiveBestZone(null);
    setSuperReport(null);
    const fetchId = ++currentFetchId.current;
    setFeedbackRating(null);
    setFeedbackComment('');
    setFeedbackStatus('idle');
    setLastSearchContext(null);
    setSuperSearching(true);
    setErr('');
    const started = performance.now();
    const controller = new AbortController();
    const watchdog = window.setTimeout(() => controller.abort(), 200_000);
    try {
      const res = await apiFetch('/api/market/super-search', {
        method: 'POST', headers: authHeaders(),
        signal: controller.signal,
        body: JSON.stringify({ query, category, partners: partnersStr, district: districtForSearch, radiusMeters: searchRadiusUsed, center: { lat: searchCenter[0], lng: searchCenter[1] }, includeOsm: true, includeGoogle: true, includePopulation: false, maxResults: 1000 }),
      });
      if (fetchId !== currentFetchId.current) return;
      if (!res.ok) throw new Error(await readAPIError(res));
      const result = await res.json();
      const resultsCount = (result.places?.length || 0) + (result.partners?.length || 0);
      setData(result);
      setSuperReport(result.superReport || null);
      setSearchedArea({ lat: searchCenter[0], lng: searchCenter[1], radius: searchRadiusUsed });
      setZeroResultsSearch(null);
      setVisibleMapLayers(prev => ({ ...prev, partnerHeat: false, partnerPoints: true }));
      const superContext = {
        searchId: createTikTokEventId('search', 'super'),
        action: 'market.super_search',
        query,
        category,
        partners: partnersStr,
        district: districtForSearch,
        radiusMeters: searchRadiusUsed,
        center: { lat: searchCenter[0], lng: searchCenter[1] },
        resultsCount,
        warnings: result.warnings || [],
        createdAt: new Date().toISOString(),
      };
      setLastSearchContext(superContext);
      updateSearchMeta({ query, district: districtForSearch, partners: partnersStr, resultsCount, analysisId: result.savedSearchId });
      trackAmplitudeEvent('search_completed', { ok: true, kind: 'market.super_search', results_count: resultsCount });
      trackAmplitudeEvent('Super Search Completed', {
        district: districtForSearch || 'unspecified',
        radius_meters: searchRadiusUsed,
        results_count: resultsCount,
        duration_ms: Math.round(performance.now() - started),
        web_searches: result.superReport?.webSearches ?? 0,
        verdict: result.superReport?.verdict ?? 'none',
        rental_listings: result.superReport?.rentalListings?.length ?? 0,
      });
      await loadBilling();
      setHiddenCategories([]);
      window.requestAnimationFrame(() => {
        document.getElementById('super-report-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (e: any) {
      if (fetchId === currentFetchId.current) {
        setErr(friendlyNetworkError(e, 'La súper búsqueda no pudo terminar. Si se descontaron créditos, ya están de vuelta.'));
        trackAmplitudeEvent('search_failed', { kind: 'market.super_search' });
        trackAmplitudeEvent('Super Search Failed', { duration_ms: Math.round(performance.now() - started) });
        await loadBilling();
      }
    } finally {
      window.clearTimeout(watchdog);
      if (fetchId === currentFetchId.current) setSuperSearching(false);
    }
  }

  async function submitSearchFeedback(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!feedbackRating || !lastSearchContext) return;
    setFeedbackStatus('sending');
    try {
      const res = await apiFetch('/api/feedback/search', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          rating: feedbackRating,
          comment: feedbackComment,
          search: lastSearchContext,
        }),
      });
      if (!res.ok) throw new Error(await readAPIError(res));
      setFeedbackStatus('sent');
      trackAmplitudeEvent('Search Feedback Submitted', { rating: feedbackRating });
    } catch {
      setFeedbackStatus('error');
    }
  }

  function requestRubroDiscovery(useSuper: boolean) {
    if (locations.length === 0) {
      setDiscoverLocationAlert(true);
      trackAmplitudeEvent('Rubro Discovery Blocked No Location', { super: useSuper });
      return;
    }
    if (!requireAuth()) return;
    if (!billing) {
      void loadBilling();
      setErr('Cargando información de créditos...');
      return;
    }
    if (useSuper && !superSearchAvailable) {
      registerFeatureInterest('discover_super');
      return;
    }
    const cost = useSuper ? discoverRubroSuperCost : discoverRubroCost;
    if (billing.creditsBalance < cost) {
      trackAmplitudeEvent('Rubro Discovery Insufficient Credits', { credits_balance: billing.creditsBalance, cost, super: useSuper });
      setErr(`Esta búsqueda usa ${cost} créditos y te quedan ${billing.creditsBalance}.`);
      openBuyCredits('discover_rubro');
      return;
    }
    setPendingDiscoverSuper(useSuper);
    trackAmplitudeEvent('Rubro Discovery Confirm Shown', { cost, radius_meters: discoveryRadius, super: useSuper });
    setShowDiscoverConfirm(true);
  }

  async function runRubroDiscovery() {
    const useSuper = pendingDiscoverSuper;
    const cost = useSuper ? discoverRubroSuperCost : discoverRubroCost;
    setShowDiscoverConfirm(false);
    const center = customCenter;
    const radiusUsed = discoveryRadius;
    setDiscoveringRubro(true);
    setRubroDiscovery(null);
    setDiscoveryFeedback(null);
    setErr('');
    trackAmplitudeEvent('Rubro Discovery Started', { district: districtForSearch, radius_meters: radiusUsed, super: useSuper, cost });
    const controller = new AbortController();
    const watchdog = window.setTimeout(() => controller.abort(), useSuper ? 200_000 : 110_000);
    try {
      const res = await apiFetch('/api/market/discover-rubro', {
        method: 'POST',
        headers: authHeaders(),
        signal: controller.signal,
        body: JSON.stringify({ district: districtForSearch, radiusMeters: radiusUsed, center: { lat: center[0], lng: center[1] }, super: useSuper }),
      });
      if (!res.ok) throw new Error(await readAPIError(res));
      const result: RubroDiscoveryResult = await res.json();
      setRubroDiscovery(result);
      setHasSearched(true);
      if (result.savedSearchId) {
        window.history.replaceState({ analysisId: result.savedSearchId }, '', `/analisis/${result.savedSearchId}`);
      }
      trackAmplitudeEvent('search_completed', { ok: true, kind: useSuper ? 'discover.rubro.super' : 'discover.rubro' });
      const topRubro = result.opportunities?.[0]?.id || 'none';
      trackAmplitudeEvent('Rubro Discovery Completed', {
        district: districtForSearch,
        radius_meters: result.radiusMeters,
        top_rubro: topRubro,
        opportunities_count: result.opportunities?.length || 0,
        cached: result.cached,
        credits_charged: result.creditsCharged,
        super: useSuper,
      });
      trackMarketingDataLayer('radarbi_rubro_discovery_completed', { top_rubro: topRubro, cached: result.cached, super: useSuper });
      await loadBilling();
    } catch (e: any) {
      trackAmplitudeEvent('search_failed', { kind: useSuper ? 'discover.rubro.super' : 'discover.rubro' });
      trackAmplitudeEvent('Rubro Discovery Failed', { super: useSuper });
      setErr(friendlyNetworkError(e, 'No pudimos terminar la búsqueda. Te devolvimos los créditos; intenta de nuevo en un momento.'));
      await loadBilling();
    } finally {
      window.clearTimeout(watchdog);
      setDiscoveringRubro(false);
    }
  }

  function analyzeDiscoveredRubro(opportunity: RubroOpportunity, rank: number) {
    trackAmplitudeEvent('Rubro Discovery Analyze Clicked', { rubro: opportunity.id, rank: rank + 1, score: opportunity.score });
    setCompetitorKeywords([opportunity.query]);
    setCompetitorInput('');
    setPartnersList([]);
    setPartnerInput('');
    if (rubroDiscovery) setRadius(rubroDiscovery.radiusMeters);
    setLandingCase('open');
    setPendingSearch(true);
  }

  async function submitDiscoveryFeedback(rating: SearchFeedbackRating) {
    if (!rubroDiscovery) return;
    setDiscoveryFeedback({ rating, status: 'sending' });
    const opportunities = rubroDiscovery.opportunities || [];
    try {
      const res = await apiFetch('/api/feedback/search', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          rating,
          comment: '',
          search: {
            action: pendingDiscoverSuper ? 'discover.rubro.super' : 'discover.rubro',
            query: opportunities.map(item => item.id).join(', '),
            district: rubroDiscovery.district,
            radiusMeters: rubroDiscovery.radiusMeters,
            resultsCount: opportunities.length,
            center: rubroDiscovery.center,
            cached: rubroDiscovery.cached,
          },
        }),
      });
      if (!res.ok) throw new Error(await readAPIError(res));
      setDiscoveryFeedback({ rating, status: 'sent' });
      trackAmplitudeEvent('Rubro Discovery Feedback Submitted', { rating });
    } catch {
      setDiscoveryFeedback({ rating, status: 'error' });
    }
  }

  // Runs after the state set by analyzeDiscoveredRubro has rendered, so
  // search() reads the new rubro instead of the previous one.
  useEffect(() => {
    if (!pendingSearch) return;
    setPendingSearch(false);
    void search();
  }, [pendingSearch]);

  async function searchBestZone() {
    if (!requireCredits()) return;
    if (!query.trim()) { alert('Ingresa un rubro para buscar la mejor zona'); return; }
    trackAmplitudeEvent('Best Zone Search Started', {
      category: category || 'unspecified',
      district: districtForSearch || 'unspecified',
      radius_meters: radius,
    });
    setLoadingBestZone(true);
    setBestZoneData(null);
    setActiveBestZone(null);
    // Best-zone also logs a market search, so the server no longer sees the zero-result search as the latest one.
    setZeroResultsSearch(null);
    const searchStarted = performance.now();
    try {
      const partnersStr = partnersList.join(', ');
      const bestZoneEventId = createTikTokEventId('search', 'best_zone');
      // Search within the user's selected radius
      const res = await apiFetch('/api/market/search', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ query, category, partners: partnersStr, district: districtForSearch, radiusMeters: radius, center: { lat: customCenter[0], lng: customCenter[1] }, includeOsm: true, includeGoogle: true, includePopulation: false, maxResults: 1000, tiktokEventId: bestZoneEventId, tiktokEventCategory: 'best_zone_search' })
      });
      console.info('[radarbi] market.search best_zone', {
        duration_ms: Math.round(performance.now() - searchStarted),
        query,
        district: districtForSearch,
        ok: res.ok,
      });
      if (!res.ok) throw new Error(await readAPIError(res));
      const result = await res.json();
      await loadBilling();
      
      // === SPATIAL ANALYSIS: IDW + Competition Avoidance ===
      const comps = (result.places || []).filter((p: Place) => haversineDist(customCenter[0], customCenter[1], p.lat, p.lng) <= radius);
      const parts = (result.partners || []).filter((p: Place) => haversineDist(customCenter[0], customCenter[1], p.lat, p.lng) <= radius);
      
      // Step 1: Generate candidate points (adaptive grid + strategic midpoints)
      const candidates: { lat: number; lng: number }[] = [];
      const gridStep = radius / 1000 * 0.002;
      const numSteps = Math.min(15, Math.max(5, Math.round(radius / 100)));
      for (let i = 0; i <= numSteps; i++) {
        for (let j = 0; j <= numSteps; j++) {
          const clat = customCenter[0] + (i / numSteps - 0.5) * gridStep * 2;
          const clng = customCenter[1] + (j / numSteps - 0.5) * gridStep * 2;
          if (haversineDist(customCenter[0], customCenter[1], clat, clng) <= radius) {
            candidates.push({ lat: clat, lng: clng });
          }
        }
      }
      // Midpoints between partner pairs
      for (let i = 0; i < parts.length && i < 10; i++) {
        for (let j = i + 1; j < parts.length && j < 10; j++) {
          candidates.push({ lat: (parts[i].lat + parts[j].lat) / 2, lng: (parts[i].lng + parts[j].lng) / 2 });
        }
      }
      
      // Step 2: Score each candidate using Inverse Distance Weighting
      const scored = candidates.map(c => {
        let partnerScore = 0;
        for (const p of parts) { partnerScore += 1000 / Math.max(haversineDist(c.lat, c.lng, p.lat, p.lng), 10); }
        let compPenalty = 0; let nearestCompDist = Infinity;
        for (const p of comps) { const d = Math.max(haversineDist(c.lat, c.lng, p.lat, p.lng), 10); compPenalty += 500 / d; nearestCompDist = Math.min(nearestCompDist, d); }
        const nearbyComps = comps.filter((p: Place) => haversineDist(c.lat, c.lng, p.lat, p.lng) <= 200).length;
        const nearbyPartners = parts.filter((p: Place) => haversineDist(c.lat, c.lng, p.lat, p.lng) <= 300).length;
        return { ...c, score: (partnerScore * 2) - (compPenalty * 3) + Math.min(nearestCompDist / 10, 50), nearbyComps, nearbyPartners, nearestCompDist: Math.round(nearestCompDist) };
      }).sort((a, b) => b.score - a.score);
      
      // Step 3: Pick top 3 ensuring min 150m separation
      const bestZns: typeof scored = [];
      for (const s of scored) { if (bestZns.length >= 3) break; if (!bestZns.some(bz => haversineDist(bz.lat, bz.lng, s.lat, s.lng) < 150)) bestZns.push(s); }
      
      const maxS = bestZns[0]?.score || 1; const minS = bestZns[bestZns.length - 1]?.score || 0; const rng = maxS - minS || 1;
      const zonesResult = bestZns.map((bz, i) => ({
        lat: bz.lat, lng: bz.lng, label: `Zona Recomendada ${i + 1}`,
        score: Math.round(40 + ((bz.score - minS) / rng) * 60), comps: bz.nearbyComps, partners: bz.nearbyPartners, nearestCompDist: bz.nearestCompDist,
        reason: bz.nearbyComps === 0 && bz.nearbyPartners > 0
          ? `✅ ${bz.nearbyPartners} socios a <300m, sin competencia a <200m. Competidor más cercano: ${bz.nearestCompDist}m`
          : bz.nearbyPartners > bz.nearbyComps ? `🟡 ${bz.nearbyPartners} socios vs ${bz.nearbyComps} competidores. Buena relación`
          : `⚠️ ${bz.nearbyComps} competidores, ${bz.nearbyPartners} socios. Evalúa diferenciación`
      }));
      
      setBestZoneData({ zones: zonesResult, totalComps: comps.length, totalPartners: parts.length, radiusUsed: radius });
      trackAmplitudeEvent('Best Zone Search Completed', {
        category: category || 'unspecified',
        district: districtForSearch || 'unspecified',
        radius_meters: radius,
        competitors_count: comps.length,
        partners_count: parts.length,
        recommended_zones_count: zonesResult.length,
      });
      trackTikTokEvent('Search', {
        search_string: query.trim() || category.trim() || districtForSearch,
        description: 'RadarBI: mejor zona y quién te trae clientes',
        event_category: 'best_zone_search',
        query,
        category,
        district: districtForSearch,
        partners: partnersStr,
        partners_count: partnersList.length,
        radius_meters: radius,
        results_count: comps.length + parts.length,
        competitors_count: comps.length,
        partners_found_count: parts.length,
        recommended_zones_count: zonesResult.length,
        search_type: 'best_zone',
        status: 'success',
      }, bestZoneEventId);
      
      // Also fetch district-wide recommendations from LLM
      try {
        const recRes = await apiFetch('/api/market/recommend-districts', {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ query, district: districtForSearch })
        });
        if (recRes.ok) {
          const recData = await recRes.json();
          setBestZoneData((prev: any) => prev ? { ...prev, districtRecs: recData.recommendations || [] } : prev);
        }
      } catch {} // Non-critical
    } catch (e: any) {
      console.info('[radarbi] market.search best_zone', {
        duration_ms: Math.round(performance.now() - searchStarted),
        query,
        district: districtForSearch,
        ok: false,
      });
      trackAmplitudeEvent('Best Zone Search Failed');
      setErr(friendlyNetworkError(e, 'No se pudo analizar la mejor zona'));
    } finally {
      setLoadingBestZone(false);
    }
  }
  
  const [recommendedPartners, setRecommendedPartners] = useState<string[]>([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  // Auto-search is removed by user request. All searches must be manual via the "Buscar" button.

  // Fetch partner recommendations when the rubro changes.
  useEffect(() => {
    const rubro = query.trim();
    if (!rubro || isDiscoverMode) {
      setRecommendedPartners([]);
      setLoadingRecs(false);
      return;
    }
    setLoadingRecs(true);
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/market/recommend-partners?q=${encodeURIComponent(rubro)}`, {
          signal: controller.signal,
        } as RequestInit);
        if (!res.ok) throw new Error(await readAPIError(res));
        const json = await res.json();
        if (!Array.isArray(json)) {
          setRecommendedPartners([]);
          return;
        }
        const recs = json.map((item: unknown) => String(item).trim()).filter(Boolean);
        setRecommendedPartners(recs);
        setPartnersList(prev => (prev.length === 0 ? recs.slice(0, 3) : prev));
        if (authUser) void loadBilling();
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setRecommendedPartners([]);
      } finally {
        if (!controller.signal.aborted) setLoadingRecs(false);
      }
    }, 500);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, isDiscoverMode, authUser, loadBilling]);

  const addPartnerFromRec = (p: string) => {
    if (!partnersList.includes(p)) {
      setPartnersList([...partnersList, p]);
    }
    setRecommendedPartners(recommendedPartners.filter(rec => rec !== p));
  };

  const exportCsv = async () => {
    if (!requireCredits()) return;
    setBillingLoading(true); setErr('');
    try {
      const billRes = await apiFetch('/api/billing/credits/consume', {
        method: 'POST',
        body: JSON.stringify({ action: 'export.csv' })
      });
      if (!billRes.ok) throw new Error(await readAPIError(billRes));
      setBilling(await billRes.json());
    } catch (e: any) {
      setErr(e.message || 'No se pudo descontar créditos para exportar');
      setBillingLoading(false);
      return;
    }
    setBillingLoading(false);

    const rows = [['name','source','type','lat','lng','address','rating','reviews','trafficScore','category','buildingType','buildingLevels'], 
      ...places.map(p => [p.name,p.source,'competitor',p.lat,p.lng,p.address||'',p.rating||'',p.reviews||'',p.estimatedTrafficScore||'',p.category||'','','']),
      ...partnersData.map(p => [p.name,p.source,'partner',p.lat,p.lng,p.address||'',p.rating||'',p.reviews||'',p.estimatedTrafficScore||'',p.category||'','','']),
      ...populationData.map(p => [p.name || 'Edificio residencial', p.source, 'residential', p.lat, p.lng, p.address || '', '', '', '', p.category || 'residential', p.buildingType || '', p.buildingLevels || ''])
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replaceAll('"','""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href=url; a.download=`market-${query}.csv`; a.click(); URL.revokeObjectURL(url);
    trackAmplitudeEvent('CSV Exported', {
      competitors_count: places.length,
      partners_count: partnersData.length,
      residential_signals_count: populationData.length,
    });
  };

  const exportPdf = async () => {
    const discoverReady = Boolean(showDiscoveryPanel && rubroDiscovery);
    if ((!hasSearchResults && !discoverReady) || exportingPdf) return;
    if (!requireCredits()) return;
    setErr('');
    setExportingPdf(true);
    setBillingLoading(true);
    try {
      const billRes = await apiFetch('/api/billing/credits/consume', {
        method: 'POST',
        body: JSON.stringify({ action: 'export.pdf' })
      });
      if (!billRes.ok) throw new Error(await readAPIError(billRes));
      setBilling(await billRes.json());
      setBillingLoading(false);
      const { buildMarketReportPdf } = await import('./reportPdf');
      const detailed = Boolean(superReport) || Boolean(discoverReady && pendingDiscoverSuper);
      await buildMarketReportPdf({
        query: discoverReady ? (rubroDiscovery?.opportunities?.[0]?.label || 'Mejor rubro') : query,
        partners: partnersList.join(', '),
        district: discoverReady ? (rubroDiscovery?.district || districtForSearch) : districtForSearch,
        radiusMeters: discoverReady ? (rubroDiscovery?.radiusMeters || discoveryRadius) : searchRadius,
        center: customCenter,
        detailed,
        superReport: superReport || undefined,
        discover: discoverReady && rubroDiscovery ? {
          district: rubroDiscovery.district,
          radiusMeters: rubroDiscovery.radiusMeters,
          opportunities: rubroDiscovery.opportunities || [],
          saturated: rubroDiscovery.saturated || [],
        } : undefined,
        summary,
        competitors: places,
        partnerPlaces: partnersData,
        zones,
        bestZones: bestZoneData?.zones?.length ? bestZoneData : undefined,
        warnings: warningMessages,
        populationStats,
        footTrafficSummary: footTraffic?.summary,
      });
      trackAmplitudeEvent('PDF Exported', {
        competitors_count: places.length,
        partners_count: partnersData.length,
        residential_signals_count: populationData.length,
        zones_count: zones.length,
        best_zone_included: Boolean(bestZoneData?.zones?.length),
        detailed,
      });
    } catch (e: any) {
      setErr(e.message || 'No se pudo generar el informe PDF');
    } finally {
      setBillingLoading(false);
      setExportingPdf(false);
    }
  };

  const downloadSavedSearchReport = async (id: string) => {
    if (exportingPdf || downloadingSavedSearchId) return;
    if (!requireCredits()) return;
    setErr('');
    setDownloadingSavedSearchId(id);
    setExportingPdf(true);
    setBillingLoading(true);
    try {
      const res = await apiFetch(`/api/searches/${id}`);
      if (!res.ok) throw new Error(await readAPIError(res));
      const item: SavedSearch = await res.json();
      const billRes = await apiFetch('/api/billing/credits/consume', {
        method: 'POST',
        body: JSON.stringify({ action: 'export.pdf' })
      });
      if (!billRes.ok) throw new Error(await readAPIError(billRes));
      setBilling(await billRes.json());
      setBillingLoading(false);
      const { buildMarketReportPdf } = await import('./reportPdf');
      await buildMarketReportPdf(reportInputFromSaved(item));
      trackAmplitudeEvent('PDF Exported', {
        source: 'search_history',
        kind: item.kind,
        detailed: item.detailed,
      });
    } catch (e: any) {
      setErr(e.message || 'No se pudo descargar el informe');
    } finally {
      setBillingLoading(false);
      setExportingPdf(false);
      setDownloadingSavedSearchId(null);
    }
  };

  // Tour state: delay until after first paint so LCP is the HTML hero, not the overlay.
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (isAuditBrowser()) return;
    if (localStorage.getItem(TOUR_STORAGE_KEY) === 'true') return;
    const start = () => setShowTour(true);
    const useIdle = typeof window.requestIdleCallback === 'function';
    const idle = useIdle
      ? window.requestIdleCallback(start, { timeout: 2500 })
      : window.setTimeout(start, 1200);
    return () => {
      if (useIdle) window.cancelIdleCallback(idle as number);
      else window.clearTimeout(idle as number);
    };
  }, []);
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | 'complaints' | null>(null);
  const [complaintForm, setComplaintForm] = useState<ComplaintFormState>(INITIAL_COMPLAINT_FORM);
  const [complaintStatus, setComplaintStatus] = useState<ComplaintStatus>('idle');
  const [complaintError, setComplaintError] = useState('');
  const [complaintReceipt, setComplaintReceipt] = useState<ComplaintResponse | null>(null);
  const [complaintLookupCode, setComplaintLookupCode] = useState('');
  const [complaintLookupEmail, setComplaintLookupEmail] = useState(authUser?.email || '');
  const [complaintTrackingStatus, setComplaintTrackingStatus] = useState<ComplaintTrackingStatus>('idle');
  const [complaintTrackingError, setComplaintTrackingError] = useState('');
  const [complaintTracking, setComplaintTracking] = useState<ComplaintTrackingResponse | null>(null);

  useEffect(() => {
    if (!authUser) return;
    setComplaintForm(prev => ({
      ...prev,
      consumerName: prev.consumerName || authUser.name || '',
      consumerEmail: prev.consumerEmail || authUser.email || '',
    }));
    setComplaintLookupEmail(prev => prev || authUser.email || '');
  }, [authUser]);

  const updateComplaintField = <K extends keyof ComplaintFormState>(field: K, value: ComplaintFormState[K]) => {
    setComplaintForm(prev => ({ ...prev, [field]: value }));
    if (complaintStatus === 'error') {
      setComplaintStatus('idle');
      setComplaintError('');
    }
  };

  const resetComplaintForm = () => {
    setComplaintForm({
      ...INITIAL_COMPLAINT_FORM,
      consumerName: authUser?.name || '',
      consumerEmail: authUser?.email || '',
    });
    setComplaintStatus('idle');
    setComplaintError('');
    setComplaintReceipt(null);
  };

  async function trackComplaint(e: React.FormEvent) {
    e.preventDefault();
    setComplaintTrackingStatus('loading');
    setComplaintTrackingError('');
    setComplaintTracking(null);
    try {
      const params = new URLSearchParams({
        code: complaintLookupCode.trim(),
        email: complaintLookupEmail.trim(),
      });
      const res = await apiFetch(`/api/complaints?${params.toString()}`, { method: 'GET' });
      const raw = await res.text();
      const payload = raw ? JSON.parse(raw) : {};
      if (!res.ok) throw new Error(payload.error || 'No se pudo consultar la hoja');
      setComplaintTracking(payload);
      setComplaintTrackingStatus('found');
    } catch (e: any) {
      setComplaintTrackingStatus('error');
      setComplaintTrackingError(e.message || 'No se pudo consultar la hoja');
    }
  }

  async function submitComplaint(e: React.FormEvent) {
    e.preventDefault();
    setComplaintStatus('sending');
    setComplaintError('');
    setComplaintReceipt(null);
    const amount = complaintForm.claimedAmount.trim();
    const claimedAmountValue = amount === '' ? null : Number(amount);
    if (claimedAmountValue !== null && (!Number.isFinite(claimedAmountValue) || claimedAmountValue < 0)) {
      setComplaintStatus('error');
      setComplaintError('Ingresa un monto reclamado válido.');
      return;
    }
    try {
      const res = await apiFetch('/api/complaints', {
        method: 'POST',
        body: JSON.stringify({
          ...complaintForm,
          claimedAmount: claimedAmountValue ?? undefined,
        }),
      });
      const raw = await res.text();
      const payload = raw ? JSON.parse(raw) : {};
      if (!res.ok) throw new Error(payload.error || 'No se pudo registrar la hoja de reclamación');
      setComplaintReceipt(payload);
      setComplaintLookupCode(payload.code || '');
      setComplaintLookupEmail(complaintForm.consumerEmail);
      setComplaintStatus('sent');
    } catch (e: any) {
      setComplaintStatus('error');
      setComplaintError(e.message || 'No se pudo registrar la hoja de reclamación');
    }
  }

  const mapLayerOptions: { key: MapLayerKey; label: string; count: number }[] = [
    { key: 'competitors', label: 'Rubros', count: places.length },
    { key: 'partnerHeat', label: 'Calor socios', count: partnersData.length },
    { key: 'partnerPoints', label: 'Puntos socios', count: partnersData.length },
    { key: 'rentals', label: 'Locales IA', count: rentalPlaces.length },
    { key: 'zones', label: 'Zonas IA', count: zones.filter(z => z.id !== 'z-center').length + (bestZoneData?.zones?.length || 0) },
  ];

  return <div className={authUser ? 'app is-authed' : 'app'}>
    <GoogleConsentBanner onOpenPrivacy={() => setLegalModal('privacy')} />
    {showTour && !isEvaluateMode && !evalFlow && <OnboardingTour onComplete={() => setShowTour(false)} />}
    <header className="topbar">
      <div className="topbar-inner">
        <a className="topbar-brand" href="/" aria-label="RadarBI inicio">
          <span className="radar-mark" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </span>
          <span className="topbar-wordmark">Radar<span>BI</span></span>
        </a>
        {authUser && (
          <div className="user-menu">
            <div className="topbar-tools">
              <button type="button" className="history-pill" onClick={() => void toggleSearchHistory()} aria-expanded={searchHistoryOpen}>
                <Clock size={15} /> <span>Historial</span>
              </button>
              {searchHistoryOpen && (
                <SearchHistoryPopover
                  items={searchHistory}
                  loading={searchHistoryLoading}
                  openingId={openingSavedSearchId}
                  downloadingId={downloadingSavedSearchId}
                  exportPdfCost={exportPdfCost}
                  onClose={() => setSearchHistoryOpen(false)}
                  onOpen={id => void openSavedSearch(id)}
                  onRepeat={item => void repeatPastSearch(item)}
                  onDownload={id => void downloadSavedSearchReport(id)}
                  onOpenEvaluations={evalEnabled ? () => openEvaluateFlow({ source: 'historial' }) : undefined}
                />
              )}
              <button type="button" className="credit-pill" onClick={toggleCreditHistory} aria-expanded={creditHistoryOpen} disabled={!billing} title="Ver créditos y comprar">
                <Wallet size={15} />
                {billing ? billing.creditsBalance.toLocaleString('es-PE') : '…'}
                {billing ? <ChevronDown size={14} /> : <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              </button>
              <button type="button" className="credit-topup-button" onClick={() => openBuyCredits('header')} title="Ver paquetes de créditos">
                <PlusCircle size={15} />
                <span>Paquetes</span>
              </button>
              {billing && creditHistoryOpen && (
                <CreditHistoryPopover
                  entries={creditHistory}
                  loading={creditHistoryLoading}
                  onClose={() => setCreditHistoryOpen(false)}
                  billing={billing}
                  onBuyMore={() => openBuyCredits('history_menu')}
                />
              )}
            </div>
            <div className="account-menu">
              <button
                type="button"
                className="user-pill"
                onClick={() => {
                  setCreditHistoryOpen(false);
                  setSearchHistoryOpen(false);
                  setAccountMenuOpen(open => !open);
                }}
                aria-expanded={accountMenuOpen}
                aria-haspopup="menu">
                {authUser.avatarUrl ? (
                  <img src={authUser.avatarUrl} alt="" width={24} height={24} loading="lazy" decoding="async" referrerPolicy="no-referrer" />
                ) : (
                  <UserIcon size={16} />
                )}
                <span>{(authUser.name || authUser.email).split(' ')[0].split('@')[0]}</span>
                <ChevronDown size={14} />
              </button>
              {accountMenuOpen && (
                <div className="account-popover" role="menu">
                  <div className="account-popover-id">
                    <b>{authUser.name || 'Tu cuenta'}</b>
                    <span>{authUser.email}</span>
                  </div>
                  <button type="button" className="account-popover-buy" onClick={() => { setAccountMenuOpen(false); void toggleSearchHistory(); }}>
                    <Clock size={14} /> Mis análisis
                  </button>
                  {evalEnabled && (
                    <button type="button" className="account-popover-buy" onClick={() => openEvaluateFlow({ source: 'historial' })}>
                      <Store size={14} /> Mis evaluaciones de local
                    </button>
                  )}
                  <button type="button" className="account-popover-buy" onClick={() => openBuyCredits('account_menu')}>
                    <PlusCircle size={14} /> Comprar créditos
                  </button>
                  <button type="button" className="account-popover-logout" onClick={onLogout}>
                    <LogOut size={14} /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>

    <div className="app-body">
    <div className="landing-stack is-split">
    <div className="landing-main">
    <section className="intro">
      {!authUser && (
        <button type="button" className="signup-pill" onClick={() => openSignupOffer('landing_pill')}>
          <span>100</span>
          créditos gratis al registrarte
        </button>
      )}
      <div className="intro-copy">
        {isEvaluateMode ? (
          <h1 className="intro-title">¿Vas a alquilar un local? Descubre si te conviene antes de firmar.</h1>
        ) : (
          <>
            <h1 className="intro-title">Mira la cuadra antes de abrir tu negocio en Perú</h1>
            <p className="intro-lead">Competencia y quién te trae clientes, en un mapa.</p>
          </>
        )}
        {!authUser && (
          <p className="intro-packs">100 créditos para probar. Si te sirve, hay paquetes desde {PAID_PACKS_FROM}.</p>
        )}
      </div>
    </section>

    <LandingCaseTabs value={landingCase} onChange={selectLandingCase} cases={landingCases} />

    {isEvaluateMode && evalConfig ? (
      <EvaluateLocalStart
        config={evalConfig}
        apiFetch={apiFetch}
        signedIn={Boolean(authUser)}
        onStart={() => openEvaluateFlow({ source: 'pestana' })}
        onOpenList={() => openEvaluateFlow({ source: 'historial' })}
      />
    ) : (
    <section className="search-panel" id="tour-search-panel">
      <div className="search-fields">
      <div className="search-field search-field-location" id="tour-location">
        <div className="field-head">
          <span>{activeLandingCase.locationLabel}</span>
          <small className={locations.length ? 'is-ready' : ''}>{locations.length ? 'Lista' : 'Elige una zona'}</small>
        </div>
        <LocationSearch 
          locations={locations}
          activeLocationId={activeLocationId}
          onAddLocation={addLocation}
          onRemoveLocation={removeLocation}
          onActivateLocation={activateLocation}
          placeholder={activeLandingCase.placeholder}
          seedQuery={locationSeed}
        />
        <p className="case-hint">{activeLandingCase.hint}</p>
      </div>
      {!isDiscoverMode && <>
      <div className={`search-field${emptySearchAlert ? ' is-missing' : ''}`} id="tour-rubro">
        <div className="field-head">
          <span>Tu rubro</span>
          <small>Competencia</small>
        </div>
        <div className="tags-container keyword-container">
          {competitorKeywords.map(keyword => (
            <span key={keyword} className="tag competitor-tag">
              {keyword}
              <button
                type="button"
                className="chip-remove"
                onClick={() => removeCompetitorKeyword(keyword)}
                aria-label={`Quitar rubro ${keyword}`}>
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          ))}
          <input
            id="competitor-keyword-input"
            name="rubro"
            autoComplete="off"
            aria-label="Añadir rubro de tu negocio para buscar competidores"
            value={competitorInput}
            onChange={e=>setCompetitorInput(e.target.value)}
            onKeyDown={onCompetitorKeyDown}
            onBlur={addCompetitorKeywords}
            placeholder={competitorKeywords.length === 0 ? "Ej: farmacia, botica" : "Añadir rubro"}
            className="tag-input"
          />
        </div>
      </div>
      <div className={`search-field${emptySearchAlert ? ' is-missing' : ''}`} id="tour-partners">
        <div className="field-head">
          <span>Quién te trae clientes</span>
          <small>Opcional</small>
        </div>
        <div className={`tags-container${loadingRecs ? ' is-loading-recs' : ''}`}>
          {partnersList.map(p => (
            <span key={p} className="tag">
              {p}
              <button
                type="button"
                className="chip-remove"
                onClick={() => removePartner(p)}
                aria-label={`Quitar socio comercial ${p}`}>
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          ))}
          {loadingRecs && partnersList.length === 0 && (
            <>
              <span className="partner-skel" aria-hidden="true" />
              <span className="partner-skel" aria-hidden="true" />
              <span className="partner-skel" aria-hidden="true" />
            </>
          )}
          <input 
            id="partner-keyword-input"
            name="socios"
            autoComplete="off"
            aria-label="Añadir un tipo de negocio que atraiga clientes a tu local"
            value={partnerInput} 
            onChange={e=>setPartnerInput(e.target.value)} 
            onKeyDown={addPartner}
            placeholder={loadingRecs && partnersList.length === 0 ? 'Buscando quién te trae clientes…' : partnersList.length === 0 ? "Ej: hospital, clínica, oficinas" : "Añadir socio"} 
            className="tag-input"
          />
        </div>
        {(recommendedPartners.length > 0 || loadingRecs) && <div className="suggested-partners" aria-live="polite">
          {loadingRecs ? (
            <span className="suggested-partners-loading">
              <Loader2 size={14} aria-hidden="true" />
              La IA está buscando quién te trae clientes…
            </span>
          ) : (
            <>
              <span>Sugeridos IA:</span>
              {recommendedPartners.filter(p => !partnersList.includes(p)).map(p => (
                <button
                  type="button"
                  key={p}
                  className="suggested-partner"
                  onClick={()=>addPartnerFromRec(p)}
                  aria-label={`Agregar socio sugerido ${p}`}>+{p}</button>
              ))}
            </>
          )}
        </div>}
      </div>
      </>}
      </div>
      <div className="search-toolbar">
      <div className="search-side-panel">
        {isDiscoverMode ? (
          <div className="discovery-radius" role="group" aria-label="Radio del análisis">
            <span>Radio</span>
            {DISCOVERY_RADIUS_OPTIONS.map(option => (
              <button
                key={option}
                type="button"
                className={discoveryRadius === option ? 'is-active' : ''}
                aria-pressed={discoveryRadius === option}
                onClick={() => setDiscoveryRadius(option)}>
                {formatRadius(option)}
              </button>
            ))}
          </div>
        ) : (
        <label className={`check-label layer-toggle ${includePop ? 'layer-toggle-active' : ''}`}>
          <input
            type="checkbox"
            checked={includePop}
            onChange={e => {
              if (e.target.checked && !requireAuth()) return;
              setIncludePop(e.target.checked);
            }}
          />
          <span className="toggle-mark"></span>
          <span>
            <strong>Hora pico de la calle</strong>
            <small>{footTrafficLoading ? 'Actualizando...' : 'Opcional'}</small>
          </span>
        </label>
        )}
      </div>
      <div className="search-actions" id="tour-action-bar">
        {isDiscoverMode ? (
          <>
          <span className="action-tooltip-wrap">
            <button
              type="button"
              onClick={() => requestRubroDiscovery(false)}
              disabled={discoveringRubro}
              aria-describedby="discover-search-tooltip"
              className="action-button action-button-primary">
              {discoveringRubro && !pendingDiscoverSuper ? <div className="loader-spinner" style={{width: '16px', height: '16px', borderTopColor: 'white'}}></div> : <Lightbulb size={18} />}
              {discoveringRubro && !pendingDiscoverSuper ? 'Investigando...' : `Búsqueda · ${discoverRubroCost} créditos`}
            </button>
            <span id="discover-search-tooltip" role="tooltip" className="action-tooltip">
              Demanda y competencia de la zona, con un modelo más económico.
            </span>
          </span>
          {superSearchAvailable ? (
          <span className="action-tooltip-wrap">
            <button
              type="button"
              onClick={() => requestRubroDiscovery(true)}
              disabled={discoveringRubro}
              aria-describedby="discover-super-tooltip"
              className="action-button action-button-super">
              {discoveringRubro && pendingDiscoverSuper ? <div className="loader-spinner" style={{width: '16px', height: '16px', borderTopColor: 'white'}}></div> : <Sparkles size={18} aria-hidden="true" />}
              {discoveringRubro && pendingDiscoverSuper ? 'Investigando...' : `Súper búsqueda · ${discoverRubroSuperCost} créditos`}
            </button>
            <span id="discover-super-tooltip" role="tooltip" className="action-tooltip">
              Un modelo más potente investiga en internet: noticias, reseñas y señales extra de la zona.
            </span>
          </span>
          ) : (
          <span className="action-soon">
            <button type="button" className="action-button action-button-super is-soon" disabled>
              Súper búsqueda · Próximamente
            </button>
            <button type="button" className="action-notify" onClick={() => registerFeatureInterest('discover_super')}>
              Avísame
            </button>
          </span>
          )}
          </>
        ) : (
        <span className="action-tooltip-wrap">
          <button
            type="button"
            onClick={() => void search()}
            disabled={loading}
            aria-describedby="search-tooltip"
            className="action-button action-button-primary">
            {loading ? <div className="loader-spinner" style={{width: '16px', height: '16px', borderTopColor: 'white'}}></div> : <Search size={18} />}
            {loading ? 'Buscando...' : `Buscar · ${searchCost} créditos`}
          </button>
          <span id="search-tooltip" role="tooltip" className="action-tooltip">
            Busca competidores y quién te trae clientes dentro del radio.
          </span>
        </span>
        )}
        {!isDiscoverMode && authUser && (superSearchAvailable ? (
          <span className="action-tooltip-wrap">
            <button
              type="button"
              onClick={requestSuperSearch}
              disabled={loading || superSearching}
              aria-describedby="super-search-tooltip"
              className="action-button action-button-super">
              <Sparkles size={18} aria-hidden="true" />
              {superSearching ? 'Investigando...' : `Súper búsqueda · ${superSearchCost} créditos`}
            </button>
            <span id="super-search-tooltip" role="tooltip" className="action-tooltip">
              Un modelo más potente investiga en internet: locales en alquiler reales, nivel socioeconómico, noticias y reseñas.
            </span>
          </span>
        ) : (
          <span className="action-soon">
            <button type="button" className="action-button action-button-super is-soon" disabled>
              Súper búsqueda · Próximamente
            </button>
            <button type="button" className="action-notify" onClick={() => registerFeatureInterest('super_search')}>
              Avísame
            </button>
          </span>
        ))}
      </div>
      </div>
      {isDiscoverMode && discoverLocationAlert && (
        <p className="search-empty-alert" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <span><b>¡Whoops!</b> Elige una zona para descubrir qué negocio conviene ahí.</span>
        </p>
      )}
      {!isDiscoverMode && emptySearchAlert && (
        <p className="search-empty-alert" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <span><b>¡Whoops!</b> Debes poner tu rubro o quién te trae clientes para iniciar tu búsqueda.</span>
        </p>
      )}
      {authUser && billing?.authenticated && (creditsHalfSpent || billing.creditsBalance < LOW_CREDITS_THRESHOLD) && (
        <p className="credits-low">
          <Wallet size={15} />
          <span>
            Te quedan <b>{billing.creditsBalance}</b> créditos
            {creditsHalfSpent ? <> de {sessionCreditsStart}. Ya usaste más de la mitad.</> : '.'}
          </span>
          <button type="button" onClick={() => openBuyCredits(creditsHalfSpent ? 'half_balance' : 'low_balance')}>Comprar más créditos</button>
        </p>
      )}
    </section>
    )}

    {showPackStrip && billing && (
      <CreditPacksStrip
        packs={billing.topUps.length ? billing.topUps : DEFAULT_TOPUPS}
        currency={billing.planCurrency || 'PEN'}
        loading={billingLoading}
        onSelect={packCode => selectCreditPack(packCode, 'search_strip')}
        onDismiss={dismissPackStrip}
      />
    )}
    </div>

    {showDiscoveryPanel ? (
      <RubroDiscoveryPanel
        result={rubroDiscovery}
        loading={discoveringRubro}
        isSuper={pendingDiscoverSuper}
        feedback={discoveryFeedback}
        exportingPdf={exportingPdf}
        searchCost={searchCost}
        exportPdfCost={exportPdfCost}
        onAnalyze={analyzeDiscoveredRubro}
        onFeedback={rating => void submitDiscoveryFeedback(rating)}
        onDownload={() => void exportPdf()}
      />
    ) : hasSearched ? (
      <div className="split-map">
      <section className="map-card">
        {/* Action toolbar above map */}
        <div className="map-toolbar">
          <div className="map-basemap" role="group" aria-label="Estilo del mapa">
            <button
              type="button"
              className={mapBasemap === 'roadmap' ? 'is-active' : ''}
              aria-pressed={mapBasemap === 'roadmap'}
              onClick={() => setMapBasemap('roadmap')}>
              <Layers size={14}/> Calles
            </button>
            <button
              type="button"
              className={mapBasemap === 'hybrid' ? 'is-active' : ''}
              aria-pressed={mapBasemap === 'hybrid'}
              onClick={() => setMapBasemap('hybrid')}>
              <Satellite size={14}/> Satélite
            </button>
          </div>
          <div className="map-toolbar-meta">
            {evalEnabled && (
              <button
                type="button"
                className="eval-zone-button"
                onClick={() => {
                  const active = locations.find(location => location.id === activeLocationId);
                  openEvaluateFlow({
                    source: 'resultado_busqueda',
                    lat: customCenter[0],
                    lng: customCenter[1],
                    address: active && isPreciseLocation(active) ? [active.name, active.address].filter(Boolean).join(', ') : '',
                    rubroQuery: competitorKeywords[0],
                  });
                }}>
                <Building2 size={14} /> Evaluar un local en esta zona
              </button>
            )}
            <span className="toolbar-meta">Radio: {radius}m · {districtLabel}</span>
            {includePop && <span className="toolbar-meta traffic-meta">{footTrafficLoading ? 'Actualizando afluencia...' : layerNotice ? 'Afluencia no disponible ahora' : `${footTraffic?.segments.length || 0} calles evaluadas`}</span>}
          </div>
        </div>
        <div className="map-stage">
        {(loading || loadingBestZone) && (
          <AnalysisLoadingOverlay
            message={loadingBestZone ? 'Comparando la relación entre socios cercanos y competencia...' : undefined}
          />
        )}
        {searchAreaChanged && !loading && !loadingBestZone && (
          <div className="map-refresh-prompt" role="status">
            <span>Cambiaste la zona. El mapa aún muestra la búsqueda anterior.</span>
            <button
              type="button"
              onClick={() => {
                trackAmplitudeEvent('Map Refresh Clicked', { radius_meters: radius, expansion: refreshIsExpansion });
                void search();
              }}>
              <RefreshCw size={16} aria-hidden="true" /> Actualizar datos en el mapa · {refreshIsExpansion ? searchExpandCost : searchCost} créditos
            </button>
          </div>
        )}
        {showZeroResultsCard && zeroResultsSearch && zeroResultsCardDismissedAt !== zeroResultsSearch.at && (
          <div className="map-zero-results" role="status">
            <button
              type="button"
              className="map-zero-results-close"
              aria-label="Cerrar aviso"
              onClick={() => setZeroResultsCardDismissedAt(zeroResultsSearch.at)}>
              <X size={14} aria-hidden="true" />
            </button>
            <strong>No encontramos resultados en {zeroResultsSearch.radius} m</strong>
            {suggestedExpandRadius ? (
              <>
                <p>Quizás debas ampliar tu radio de búsqueda en el mismo punto.</p>
                <button
                  type="button"
                  className="map-zero-results-action"
                  onClick={() => {
                    const discounted = expansionQualifies(customCenter, suggestedExpandRadius);
                    trackAmplitudeEvent('Radius Expansion Clicked', {
                      from_radius_meters: zeroResultsSearch.radius,
                      to_radius_meters: suggestedExpandRadius,
                      discounted,
                      credit_cost: discounted ? searchExpandCost : 'standard',
                    });
                    void search({ radiusOverride: suggestedExpandRadius, expand: true });
                  }}>
                  <Search size={16} aria-hidden="true" />
                  Ampliar a {suggestedExpandRadius} m · {expansionQualifies(customCenter, suggestedExpandRadius) ? searchExpandCost : searchCost} créditos
                </button>
                <p className="map-zero-results-howto">
                  <Info size={13} aria-hidden="true" />
                  <span>También puedes mover «Radio de análisis» (abajo a la izquierda) a un valor mayor y tocar «Actualizar datos en el mapa». En el mismo punto y con el mismo rubro, ampliar cuesta {searchExpandCost} créditos.</span>
                </p>
              </>
            ) : (
              <p>Ya estás en el radio máximo ({MAX_ANALYSIS_RADIUS_METERS} m). Prueba mover el punto en el mapa a otra zona o cambiar el rubro o los socios.</p>
            )}
          </div>
        )}
        <MarketMapStage
          center={customCenter}
          setCenter={moveCenterFromMap}
          basemap={mapBasemap}
          partnerHeatPlaces={visiblePartnerHeatPlaces}
          includePop={includePop}
          footTraffic={footTraffic}
          radius={radius}
          setRadius={setRadius}
          places={visiblePlaces}
          partnerPointPlaces={visiblePartnerPointPlaces}
          rentals={visibleRentals}
          zones={visibleZones}
          showZones={visibleMapLayers.zones}
          bestZones={bestZoneData?.zones}
          selectedPointLocation={selectedPointLocation}
          getColorForCategory={getColorForCategory}
          translateCategory={translateCategory}
        />
        <aside className="best-zone-tip" id="tour-best-zone">
          <p className="best-zone-tip-kicker">Tip</p>
          <strong>Mejor zona para tu local</strong>
          <p>Busca el punto con gente cerca y poca competencia.</p>
          <button
            type="button"
            onClick={searchBestZone}
            disabled={loadingBestZone || loading || !query.trim()}
            className="best-zone-tip-button"
          >
            {loadingBestZone ? <div className="loader-spinner" style={{width: '16px', height: '16px', borderTopColor: 'white'}}></div> : <MapPin size={16} />}
            {loadingBestZone ? 'Analizando...' : `Mejor zona · ${searchCost} créditos`}
          </button>
        </aside>
        </div>
      {bestZoneData && (
          <div className="card best-zone-card">
            <div className="card-head rail-section-head">
              <div>
                <h2>Mejores zonas</h2>
                <small>Más socios, menos competencia</small>
              </div>
              <span className="rail-count">{bestZoneData.zones.length}</span>
            </div>
            <p className="best-zone-meta">
              Radio {bestZoneData.radiusUsed}m · {bestZoneData.totalComps} competidores · {bestZoneData.totalPartners} socios
            </p>
            {bestZoneData.zones.length === 0 && (
              <p className="best-zone-empty">No encontramos zonas destacadas en este radio. Prueba ampliando el radio o moviendo el centro.</p>
            )}
            <div className="best-zone-list">
              {bestZoneData.zones.map((bz: any, i: number) => {
                const ratio = zoneRatioLabel(bz.partners, bz.comps);
                const tone = zoneInsightTone(bz.reason);
                return (
                  <div
                    key={i}
                    className={[
                      'best-zone-item',
                      i === 0 ? 'best-zone-item-lead' : '',
                      activeBestZone === i ? 'is-active' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <div className="best-zone-item-head">
                      <strong><em>{i + 1}</em>Zona {i + 1}</strong>
                      <span>{bz.score}</span>
                    </div>
                    <div className="best-zone-item-stats">
                      <span>{bz.comps} competidores</span>
                      <span>{bz.partners} socios</span>
                      {ratio && <span className="is-ratio">{ratio}</span>}
                      <button
                        type="button"
                        className="best-zone-go"
                        onClick={() => {
                          setActiveBestZone(i);
                          setCustomCenter([bz.lat, bz.lng]);
                        }}
                      >
                        Ir a esta zona
                      </button>
                    </div>
                    <p>
                      <span className={`best-zone-tone is-${tone}`} aria-hidden="true" />
                      <span>{zoneInsightText(bz.reason)}</span>
                    </p>
                  </div>
                );
              })}
            </div>
            {bestZoneData.districtRecs && bestZoneData.districtRecs.length > 0 && (
              <div className="district-recs">
                <div className="rail-section-head">
                  <h3>Otros distritos</h3>
                  <span className="rail-count is-navy">{bestZoneData.districtRecs.length}</span>
                </div>
                <div className="district-rec-list">
                  {bestZoneData.districtRecs.map((rec: any, i: number) => (
                    <DistrictRecRow key={`${rec.district}-${i}`} rec={rec} />
                  ))}
                </div>
              </div>
            )}
          </div>
      )}
      </section>
      </div>
    ) : (
      <LandingPreview
        movedCenter={landingCenterMoved || locations.some(location => location.source !== 'example') ? customCenter : null}
        onMoveCenter={pos => {
          moveCenterFromMap(pos);
          if (!landingCenterMoved) trackAmplitudeEvent('Landing Preview Center Dragged', {});
          setLandingCenterMoved(true);
        }}
        onUseExample={() => {
          setLandingCenterMoved(false);
          applySearchExample(SEARCH_EXAMPLES[0], 'landing_preview');
        }}
      />
    )}
    </div>

    <React.Suspense fallback={null}>
      {evalFlow && evalConfig?.enabled && (
        <EvaluateLocalFlow
          deps={evalDeps}
          config={evalConfig}
          start={evalFlow.start}
          evaluationId={evalFlow.evaluationId}
          onClose={closeEvaluateFlow}
        />
      )}
      {evalShareToken && !evalFlow && (
        <SharedEvaluationPage
          token={evalShareToken}
          apiFetch={apiFetch}
          onClose={() => { setEvalShareToken(''); clearEvalParam('evaluacion'); }}
          onEvaluateOwn={() => {
            setEvalShareToken('');
            clearEvalParam('evaluacion');
            if (evalConfig?.enabled) openEvaluateFlow({ source: 'compartido' });
            else setLandingCase('evaluate');
          }}
        />
      )}
      {evalOutcomeId && !evalFlow && (
        <OutcomeSurvey evaluationId={evalOutcomeId} deps={evalDeps} onClose={() => { setEvalOutcomeId(''); clearEvalParam('evaluacion_resultado'); }} />
      )}
    </React.Suspense>

    {(err || featureNotice) && (
      <div className={`${err ? 'error' : 'notice'} app-toast`} role={err ? 'alert' : 'status'}>
        {err ? <AlertTriangle size={18}/> : <Info size={16} aria-hidden="true" />}
        {err ? humanizeUserError(err) : featureNotice}
      </div>
    )}
    {warningMessages.length ? <div className="warning"><AlertTriangle size={18}/>{warningMessages.join(' | ')}</div> : null}
    {layerNotice ? <div className="notice"><Info size={16} aria-hidden="true"/>{layerNotice}</div> : null}

    {hasSearched && !showDiscoveryPanel && (
      <>
        {superReport && (
          <SuperSearchReportCard
            report={superReport}
            onClose={() => setSuperReport(null)}
            onDownload={() => void exportPdf()}
            exportingPdf={exportingPdf}
            exportPdfCost={exportPdfCost}
          />
        )}
        <section className="metrics">
          <Metric icon={<Building2/>} label="Establecimientos" value={summary?.totalCompetitors ?? 0}/>
          <Metric icon={<MapPin/>} label="Socios comerciales" value={summary?.totalPartners ?? 0}/>
          {(summary?.avgRating ?? 0) > 0 && <Metric icon={<Flame/>} label="Rating promedio" value={summary?.avgRating ?? 0}/>}
          {(summary?.avgReviews ?? 0) > 0 && <Metric icon={<Target/>} label="Reviews promedio" value={summary?.avgReviews ?? 0}/>}
          {footTrafficReady && footTraffic?.summary && (
            <>
              <Metric icon={<Flame/>} label="Calles alta afluencia peatonal" value={footTraffic.summary.high}/>
              {footTraffic.summary.busiestWindow && <Metric icon={<Target/>} label="Hora pico estimada" value={footTraffic.summary.busiestWindow}/>}
              {footTraffic.summary.averageScore > 0 && <Metric icon={<Target/>} label="Score peatonal prom." value={footTraffic.summary.averageScore.toFixed(0)}/>}
            </>
          )}
          {footTrafficReady && populationStats && (
            <>
              {populationStats.residentialBuildings > 0 && <Metric icon={<Building2/>} label="Edificios residenciales" value={populationStats.residentialBuildings}/>}
              {populationStats.apartmentBuildings > 0 && <Metric icon={<Building2/>} label="Deptos / multifamiliar" value={populationStats.apartmentBuildings}/>}
              {populationStats.houses > 0 && <Metric icon={<MapPin/>} label="Casas" value={populationStats.houses}/>}
              {populationStats.floorsKnown > 0 && <Metric icon={<Target/>} label="Pisos promedio OSM" value={populationStats.averageKnownFloors.toFixed(1)}/>}
            </>
          )}
        </section>

        <div className="legend-card">
          <div className="legend-header">
            <strong><Layers size={15}/> Capas del mapa</strong>
            <span className="legend-help" title="Puedes activar o desactivar cada capa para filtrar lo que ves en el mapa.">
              <Info size={14}/> Haz clic para filtrar o agregar información al mapa
            </span>
          </div>
          <div className="map-layer-controls" role="group" aria-label="Capas del mapa">
            {mapLayerOptions.map(layer => {
              const isActive = visibleMapLayers[layer.key];
              const isDisabled = layer.count === 0;
              return (
                <button
                  key={layer.key}
                  type="button"
                  className={`map-layer-toggle ${isActive ? 'map-layer-toggle-active' : ''}`}
                  aria-pressed={isActive}
                  disabled={isDisabled}
                  title={isActive ? `Ocultar ${layer.label}` : `Mostrar ${layer.label}`}
                  onClick={() => toggleMapLayer(layer.key)}>
                  {isActive ? <Eye size={14}/> : <EyeOff size={14}/>}
                  <span>{layer.label}</span>
                  <b>{layer.count}</b>
                </button>
              );
            })}
          </div>

          {uniqueCategories.length > 0 && (
            <>
              <div className="legend-subhead">Rubros visibles</div>
              <div className="legend-items">
                {uniqueCategories.map(cat => {
                  const isHidden = hiddenCategories.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      className={`legend-category-toggle ${isHidden ? 'legend-category-hidden' : ''}`}
                      aria-pressed={!isHidden}
                      disabled={!visibleMapLayers.competitors}
                      title={isHidden ? `Mostrar ${translateCategory(cat)}` : `Ocultar ${translateCategory(cat)}`}
                      onClick={() => toggleCategoryVisibility(cat)}
                    >
                      <span className="color-dot" style={{backgroundColor: getColorForCategory(cat)}}></span>
                      {isHidden ? <EyeOff size={13}/> : <Eye size={13}/>}
                      <span>{translateCategory(cat)}</span>
                    </button>
                  );
                })}
                <div className="legend-item"><span className="color-dot" style={{backgroundColor: '#2563eb'}}></span>Socio Comercial</div>
                <div className="legend-item">
                  <span style={{background:'#f59e0b', width:'14px', height:'14px', borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', marginRight:'4px', border:'2px solid white', boxShadow:'0 1px 3px rgba(0,0,0,0.3)', color:'white', fontSize:'9px', fontWeight:'bold'}}>$</span>
                  Alquileres / Locales (IA)
                </div>
                {includePop && (
                  <div className="legend-item">
                    <span className="color-dot" style={{background: 'linear-gradient(to right, #22c55e, #f59e0b, #ef4444)', width: '28px', borderRadius: '4px'}}></span>
                    Calles: baja / media / alta afluencia peatonal
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {lastSearchContext && (
          <section className={`search-feedback ${feedbackStatus === 'sent' ? 'search-feedback-complete' : ''}`}>
            <div className="search-feedback-copy">
              <strong>¿Qué tan buena estuvo esta búsqueda?</strong>
              <span>Nos ayuda a ajustar mejor los resultados.</span>
            </div>
            <div className="search-feedback-actions" role="group" aria-label="Calificar búsqueda">
              <button
                type="button"
                className={feedbackRating === 'good' ? 'feedback-choice feedback-choice-active' : 'feedback-choice'}
                aria-pressed={feedbackRating === 'good'}
                disabled={feedbackStatus === 'sending' || feedbackStatus === 'sent'}
                onClick={() => { setFeedbackRating('good'); setFeedbackStatus('idle'); }}>
                <ThumbsUp size={15}/> Bien
              </button>
              <button
                type="button"
                className={feedbackRating === 'bad' ? 'feedback-choice feedback-choice-active feedback-choice-bad' : 'feedback-choice'}
                aria-pressed={feedbackRating === 'bad'}
                disabled={feedbackStatus === 'sending' || feedbackStatus === 'sent'}
                onClick={() => { setFeedbackRating('bad'); setFeedbackStatus('idle'); }}>
                <ThumbsDown size={15}/> Mal
              </button>
            </div>
            {feedbackRating && feedbackStatus !== 'sent' && (
              <form className="search-feedback-form" onSubmit={submitSearchFeedback}>
                <textarea
                  id="search-feedback-comment"
                  name="comment"
                  aria-label="Comentario opcional sobre esta búsqueda"
                  value={feedbackComment}
                  onChange={event => setFeedbackComment(event.target.value)}
                  placeholder="Comentario opcional"
                  rows={1}
                  maxLength={2000}
                />
                <button type="submit" disabled={feedbackStatus === 'sending'}>
                  {feedbackStatus === 'sending' ? 'Guardando...' : 'Enviar'}
                </button>
              </form>
            )}
            {feedbackStatus === 'sent' && <small className="feedback-note">Gracias, quedó guardado.</small>}
            {feedbackStatus === 'error' && <small className="feedback-note feedback-note-error">No se pudo guardar ahora.</small>}
          </section>
        )}
      </>
    )}

    {!showLanding && (
    <main className="grid is-rail">
      <aside className="side">
        <div className="card insights-card">
          <div className="card-head">
            <h2>Insights con IA</h2>
            <small>{zones.length ? `${zones.length} zona${zones.length === 1 ? '' : 's'}` : 'Sin análisis'}</small>
          </div>
          {!zones.length && (
            <p className="insights-empty">Haz una búsqueda para ver NSE, alquiler estimado y una recomendación por zona.</p>
          )}
          {footTrafficReady && populationStats && (
            <div className="residential-summary">
              <strong>Modelo de afluencia peatonal</strong>
              {footTraffic?.summary && (
                <>
                  <div className="traffic-summary-row">
                    <span className="traffic-dot traffic-high"></span>{footTraffic.summary.high} alta
                    <span className="traffic-dot traffic-mid"></span>{footTraffic.summary.medium} media
                    <span className="traffic-dot traffic-low"></span>{footTraffic.summary.low} baja
                  </div>
                  <div className="traffic-advanced-summary">
                    {footTraffic.summary.busiestWindow && <span><b>{footTraffic.summary.busiestWindow}</b> hora pico</span>}
                    {footTraffic.summary.dominantPattern && <span><b>{footTraffic.summary.dominantPattern}</b> patrón dominante</span>}
                    {footTraffic.summary.peakHours?.length ? <span><b>{footTraffic.summary.peakHours.join(' / ')}</b> picos secundarios</span> : null}
                  </div>
                  <div className="traffic-signal-mix">
                    {footTraffic.summary.residentialSignals > 0 && <div><b>{footTraffic.summary.residentialSignals}</b><span>vivienda</span></div>}
                    {footTraffic.summary.commercialSignals > 0 && <div><b>{footTraffic.summary.commercialSignals}</b><span>comercio</span></div>}
                    {footTraffic.summary.transitSignals > 0 && <div><b>{footTraffic.summary.transitSignals}</b><span>paraderos</span></div>}
                    {footTraffic.summary.pedestrianSignals > 0 && <div><b>{footTraffic.summary.pedestrianSignals}</b><span>cruces/accesos</span></div>}
                  </div>
                </>
              )}
              <div className="residential-grid">
                {populationStats.residentialBuildings > 0 && <span><b>{populationStats.residentialBuildings}</b> señales residenciales</span>}
                {populationStats.apartmentBuildings > 0 && <span><b>{populationStats.apartmentBuildings}</b> deptos etiquetados</span>}
                {populationStats.houses > 0 && <span><b>{populationStats.houses}</b> casas etiquetadas</span>}
                {populationStats.floorsKnown > 0 && <span><b>{populationStats.floorsKnown}</b> con pisos OSM</span>}
              </div>
              {populationStats.floorsKnown > 0 && (
              <small>
                {populationStats.totalKnownFloors} pisos conocidos en total · promedio {populationStats.averageKnownFloors.toFixed(1)} pisos.
              </small>
              )}
            </div>
          )}
          {zones.map(z => {
            const hasAi = Boolean(z.purchasingPower || z.commercialRent || z.recommendation || z.populationDensity);
            return (
              <article className="insight-zone" key={z.id}>
                <header className="insight-zone-head">
                  <div>
                    <strong>{z.name}</strong>
                    <small>{z.district}</small>
                  </div>
                  <span className={z.score >= 80 ? 'score-pill good' : 'score-pill mid'}>{z.score}</span>
                </header>
                <div className="insight-metrics">
                  <span><b>{z.competitorCount}</b> competidores</span>
                  <span><b>{z.partnerCount}</b> socios</span>
                </div>
                {hasAi ? (
                  <dl className="insight-facts">
                    {z.purchasingPower && <div><dt>NSE</dt><dd>{z.purchasingPower}</dd></div>}
                    {z.commercialRent && <div><dt>Alquiler</dt><dd>{z.commercialRent}</dd></div>}
                    {z.populationDensity && <div><dt>Densidad</dt><dd>{z.populationDensity}</dd></div>}
                    {z.recommendation && <div className="is-reco"><dt>Recomendación</dt><dd>{z.recommendation}</dd></div>}
                  </dl>
                ) : (
                  <p className="insight-pending">El detalle de IA no alcanzó a completar. El score y el mapa sí están listos.</p>
                )}
                {z.availableRentals && z.availableRentals.length > 0 && (
                  <div className="insight-rentals">
                    <span>Locales en alquiler</span>
                    {z.availableRentals.map((rental, i) => (
                      <a key={`${z.id}-rental-${i}`} href={rental.url} target="_blank" rel="noreferrer">
                        {rental.source || 'Ver listado'}
                        <small>{[rental.price, rental.area].filter(Boolean).join(' · ')}</small>
                      </a>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </aside>
    </main>
    )}

    {showLanding && <LocationStats />}

    {hasSearched && (
    <section className="table-card">
      <div className="card-head">
        <h2>Puntos encontrados</h2>
        <div className="table-exports">
          <button type="button" className="table-export" onClick={exportCsv}><Download size={15}/> Exportar CSV · {exportCsvCost} créditos</button>
          {!superReport && (
            <button type="button" className="table-export" onClick={exportPdf} disabled={!hasSearchResults || exportingPdf}>
              {exportingPdf
                ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
                : <FileText size={15}/>}
              {exportingPdf ? 'Generando informe...' : `Descargar informe · ${exportPdfCost} créditos`}
            </button>
          )}
        </div>
      </div>
      <div className="table-wrap"><table><thead><tr><th>Nombre</th><th>Tipo</th><th>Rating</th><th>Reviews</th><th>Afluencia Est.</th><th>Links</th></tr></thead><tbody>{places.map(p=><tr key={p.id}><td>{p.name}</td><td>Competidor</td><td>{p.rating || ''}</td><td>{p.reviews || ''}</td><td>{p.estimatedTrafficScore || ''}</td><td><a href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" style={{color:'#166534'}}>Maps</a></td></tr>)}{partnersData.map(p=><tr key={p.id}><td>{p.name}</td><td>Socio Comercial</td><td>{p.rating || ''}</td><td>{p.reviews || ''}</td><td>{p.estimatedTrafficScore || ''}</td><td><a href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" style={{color:'#166534'}}>Maps</a></td></tr>)}</tbody></table></div></section>
    )}

    {exportingPdf && (
      <div className="export-overlay" role="status" aria-live="polite">
        <div className="loader-spinner" />
        <span className="loading-message">Generando tu informe PDF...</span>
        <small>Espera a que se descargue el archivo. No cierres esta pestaña.</small>
      </div>
    )}

    {showCreditsPaywall && (
      <CreditsPaywallModal
        status={billing}
        loading={billingLoading}
        onBuyCredits={requestBuyCredits}
        onClose={() => setShowCreditsPaywall(false)}
        subtitle="0 disponibles · compra créditos para continuar"
      />
    )}
    {superSearching && <SuperSearchLoadingOverlay />}
    {showSuperConfirm && billing && (
      <SuperSearchConfirmModal
        cost={superSearchCost}
        balance={billing.creditsBalance}
        district={districtForSearch}
        onConfirm={() => void runSuperSearch()}
        onCancel={() => {
          setShowSuperConfirm(false);
          trackAmplitudeEvent('Super Search Confirm Cancelled');
        }}
      />
    )}
    {showDiscoverConfirm && billing && (
      <RubroDiscoveryConfirmModal
        cost={pendingDiscoverSuper ? discoverRubroSuperCost : discoverRubroCost}
        balance={billing.creditsBalance}
        district={districtForSearch}
        radius={discoveryRadius}
        isSuper={pendingDiscoverSuper}
        onConfirm={() => void runRubroDiscovery()}
        onCancel={() => {
          setShowDiscoverConfirm(false);
          trackAmplitudeEvent('Rubro Discovery Confirm Cancelled', { super: pendingDiscoverSuper });
        }}
      />
    )}
    {showHalfCreditsNotice && !showCreditsPaywall && !showBuyCredits && billing && (
      <CreditsPaywallModal
        status={billing}
        loading={billingLoading}
        onBuyCredits={requestBuyCredits}
        onClose={() => setShowHalfCreditsNotice(false)}
        title="Hay paquetes de pago"
        subtitle={`Tienes ${billing.creditsBalance} créditos. Si quieres seguir analizando, cada paquete se paga una sola vez.`}
        offer
      />
    )}
    {showBuyCredits && !showCreditsPaywall && (
      <CreditsPaywallModal
        status={billing}
        loading={billingLoading}
        onBuyCredits={requestBuyCredits}
        onClose={() => setShowBuyCredits(false)}
        title="Comprar más créditos"
      />
    )}
    {pendingCreditPack && (
      <ConfirmCreditPurchaseModal
        pack={pendingCreditPack}
        currency={billing?.planCurrency || 'PEN'}
        loading={billingLoading}
        onConfirm={() => {
          const packCode = pendingCreditPack.code;
          setPendingCreditPack(null);
          void buyCredits(packCode);
        }}
        onClose={() => setPendingCreditPack(null)}
      />
    )}

    {purchaseSummary && (
      <PurchaseSummaryModal
        summary={purchaseSummary}
        onClose={closePurchaseSummary}
      />
    )}

    {/* Legal Modal */}
    {legalModal && (
      <div role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" style={{position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'}} onClick={() => setLegalModal(null)}>
        <div style={{position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(4px)'}} />
        <div style={{position: 'relative', background: 'white', borderRadius: '16px', maxWidth: legalModal === 'complaints' ? '980px' : '720px', width: '100%', maxHeight: '80vh', overflow: 'auto', padding: '32px', boxShadow: '0 25px 60px rgba(0,0,0,0.3)'}} onClick={e => e.stopPropagation()}>
          <button type="button" onClick={() => setLegalModal(null)} aria-label="Cerrar" style={{position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#334155'}}>✕</button>
          {legalModal === 'terms' && (<div><h2 id="legal-modal-title" style={{color: '#1e293b', marginBottom: '16px'}}>Términos y Condiciones</h2><div style={{fontSize: '14px', lineHeight: '1.8', color: '#475569'}}><p><strong>1. Aceptación de los Términos</strong><br/>Al acceder y utilizar RadarBI, usted acepta estos términos y condiciones en su totalidad. Si no está de acuerdo, le rogamos no utilizar la Plataforma.</p><p style={{marginTop:'12px'}}><strong>2. Descripción del Servicio</strong><br/>RadarBI es una herramienta de información de mercado. Reúne datos públicos y análisis geográfico para que el usuario compare zonas y locales en Perú. No es un servicio de predicción, asesoría financiera ni garantía de resultados.</p><p style={{marginTop:'12px'}}><strong>3. Uso de la Información</strong><br/>La información es referencial y de apoyo a tu criterio. RadarBI no predice el futuro, no estima con certeza ventas ni rentabilidad, y no sustituye una visita al local, tu experiencia ni asesoría profesional. Los datos provienen de fuentes públicas como Google Places y OpenStreetMap.</p><p style={{marginTop:'12px'}}><strong>4. Precisión de los Datos</strong><br/>Nos esforzamos por usar datos actualizados, pero no garantizamos exactitud, completitud ni vigencia. Los análisis son estimaciones a partir de la información disponible al momento de la consulta, no una predicción del desempeño futuro de un negocio.</p><p style={{marginTop:'12px'}}><strong>5. Propiedad Intelectual</strong><br/>Todo el contenido, diseño, código, algoritmos y marca de RadarBI son propiedad exclusiva de sus creadores. Queda prohibida la reproducción o uso comercial sin autorización.</p><p style={{marginTop:'12px'}}><strong>6. Limitación de Responsabilidad</strong><br/>Tú decides si alquilas, abres o inviertes. RadarBI no será responsable de decisiones comerciales, pérdidas financieras o daños que resulten del uso de la información proporcionada por la Plataforma.</p><p style={{marginTop:'12px'}}><strong>7. Modificaciones</strong><br/>Nos reservamos el derecho de modificar estos términos en cualquier momento.</p><p style={{marginTop:'12px'}}><em>Última actualización: {new Date().toLocaleDateString('es-PE', {year:'numeric',month:'long',day:'numeric'})}</em></p></div></div>)}
          {legalModal === 'privacy' && (
            <div>
              <h2 id="legal-modal-title" style={{color: '#1e293b', marginBottom: '16px'}}>Política de Privacidad</h2>
              <div style={{fontSize: '14px', lineHeight: '1.8', color: '#475569'}}>
                <p><strong>1. Información que Recopilamos</strong><br/>RadarBI puede recopilar información limitada: ubicación geográfica proporcionada voluntariamente, rubros y categorías de búsqueda, y preferencias almacenadas localmente en su navegador.</p>
                <p style={{marginTop:'12px'}}><strong>2. Uso de la Información</strong><br/>Usamos la información para prestar el servicio, generar análisis de mercado, medir conversiones y mejorar nuestras campañas. No vendemos datos personales.</p>
                <p style={{marginTop:'12px'}}><strong>3. Almacenamiento Local</strong><br/>RadarBI utiliza localStorage para guardar preferencias, incluida su elección sobre medición y publicidad de Google.</p>
                <p style={{marginTop:'12px'}}><strong>4. Servicios de Terceros</strong><br/>Utilizamos Google Places API, OpenStreetMap, servicios de IA y Google Ads, sujetos a sus respectivas políticas de privacidad.</p>
                <p style={{marginTop:'12px'}}><strong>5. Cookies y medición</strong><br/>Google Consent Mode v2 mantiene denegado por defecto el almacenamiento publicitario, los datos de usuario para anuncios, la personalización y el almacenamiento analítico. Solo se habilitan si usted pulsa “Aceptar”. Puede rechazar o cambiar su decisión en cualquier momento desde “Configurar privacidad” en el pie de página.</p>
                <p style={{marginTop:'12px'}}><strong>6. Derechos del Usuario</strong><br/>De acuerdo con la Ley N° 29733 (Ley de Protección de Datos Personales del Perú), usted tiene derecho a acceder, rectificar, cancelar y oponerse al tratamiento de sus datos.</p>
                <p style={{marginTop:'12px'}}><em>Última actualización: {new Date().toLocaleDateString('es-PE', {year:'numeric',month:'long',day:'numeric'})}</em></p>
              </div>
            </div>
          )}
          {legalModal === 'complaints' && (
            <div className="complaints-book">
              <div className="complaints-head">
                <div>
                  <span>Hoja de Reclamación Virtual</span>
                  <h2 id="legal-modal-title">Libro de Reclamaciones</h2>
                </div>
                <button type="button" className="complaints-reset" onClick={resetComplaintForm}>Nueva hoja</button>
              </div>

              <div className="complaints-provider">
                <div><b>Proveedor</b><span>{COMPLAINTS_PROVIDER_NAME}</span></div>
                <div><b>RUC</b><span>{COMPLAINTS_PROVIDER_RUC}</span></div>
                <div><b>Email</b><span>{COMPLAINTS_PROVIDER_EMAIL}</span></div>
                <div><b>Dirección</b><span>{COMPLAINTS_PROVIDER_ADDRESS || 'Canal virtual'}</span></div>
              </div>

              <form className="complaint-tracking" onSubmit={trackComplaint}>
                <div className="complaint-tracking-head">
                  <h3>Seguimiento de hoja</h3>
                  {complaintTrackingStatus === 'found' && complaintTracking && <span>{complaintTracking.status}</span>}
                </div>
                <div className="complaint-tracking-row">
                  <label>Número de reclamo
                    <input value={complaintLookupCode} onChange={e => setComplaintLookupCode(e.target.value.toUpperCase())} placeholder="LDR-2026-000001" required />
                  </label>
                  <label>Correo registrado
                    <input type="email" value={complaintLookupEmail} onChange={e => setComplaintLookupEmail(e.target.value)} required />
                  </label>
                  <button type="submit" disabled={complaintTrackingStatus === 'loading'}>
                    {complaintTrackingStatus === 'loading' ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={15} />}
                    Consultar
                  </button>
                </div>
                {complaintTrackingStatus === 'error' && <div className="complaint-error">{complaintTrackingError}</div>}
                {complaintTrackingStatus === 'found' && complaintTracking && (
                  <div className="complaint-tracking-result">
                    <div>
                      <b>{complaintTracking.code}</b>
                      <span>{complaintTracking.complaintType} sobre {complaintTracking.productType}</span>
                    </div>
                    <p>Registrado: {new Date(complaintTracking.submittedAt).toLocaleDateString('es-PE', { dateStyle: 'medium' })}</p>
                    <p>Fecha máxima de respuesta: {new Date(complaintTracking.responseDueAt).toLocaleDateString('es-PE', { dateStyle: 'long' })}</p>
                    {complaintTracking.responseText && <p>Respuesta: {complaintTracking.responseText}</p>}
                    {complaintTracking.events.length > 0 && (
                      <ul>
                        {complaintTracking.events.slice(-4).map((event, index) => (
                          <li key={`${event.eventType}-${event.createdAt}-${index}`}>
                            <span>{new Date(event.createdAt).toLocaleDateString('es-PE', { dateStyle: 'medium' })}</span>
                            {event.message || event.eventType}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </form>

              {complaintStatus === 'sent' && complaintReceipt ? (
                <div className="complaint-receipt">
                  <CheckCircle2 size={30} />
                  <div>
                    <h3>Hoja registrada</h3>
                    <p>Tu código de seguimiento es <strong>{complaintReceipt.code}</strong>.</p>
                    <p>Fecha máxima de respuesta: <strong>{new Date(complaintReceipt.responseDueAt).toLocaleDateString('es-PE', { dateStyle: 'long' })}</strong>.</p>
                    {complaintReceipt.receiptEmailStatus === 'sent' && <p>Enviamos la constancia al correo registrado.</p>}
                    {complaintReceipt.receiptEmailStatus === 'failed' && <p>Guarda este código: la constancia por correo no pudo enviarse en este momento.</p>}
                    {complaintReceipt.receiptEmailStatus === 'skipped' && <p>Guarda este código: el correo de constancia está pendiente de configuración.</p>}
                    <button type="button" className="complaint-print" onClick={() => window.print()}>
                      <Printer size={15} />
                      Imprimir constancia
                    </button>
                  </div>
                </div>
              ) : (
                <form className="complaint-form" onSubmit={submitComplaint}>
                  <section>
                    <h3>1. Identificación del consumidor</h3>
                    <div className="complaint-grid">
                      <label>Nombre completo
                        <input value={complaintForm.consumerName} onChange={e => updateComplaintField('consumerName', e.target.value)} required />
                      </label>
                      <label>Tipo de documento
                        <select value={complaintForm.consumerDocumentType} onChange={e => updateComplaintField('consumerDocumentType', e.target.value)}>
                          <option value="dni">DNI</option>
                          <option value="ce">Carné de extranjería</option>
                          <option value="pasaporte">Pasaporte</option>
                          <option value="ruc">RUC</option>
                        </select>
                      </label>
                      <label>Número de documento
                        <input value={complaintForm.consumerDocumentNumber} onChange={e => updateComplaintField('consumerDocumentNumber', e.target.value)} required />
                      </label>
                      <label>Teléfono
                        <input value={complaintForm.consumerPhone} onChange={e => updateComplaintField('consumerPhone', e.target.value)} required />
                      </label>
                      <label>Correo electrónico
                        <input type="email" value={complaintForm.consumerEmail} onChange={e => updateComplaintField('consumerEmail', e.target.value)} required />
                      </label>
                      <label className="complaint-wide">Domicilio
                        <input value={complaintForm.consumerAddress} onChange={e => updateComplaintField('consumerAddress', e.target.value)} required />
                      </label>
                    </div>
                    <label className="complaint-check">
                      <input type="checkbox" checked={complaintForm.isMinor} onChange={e => updateComplaintField('isMinor', e.target.checked)} />
                      El consumidor es menor de edad
                    </label>
                    {complaintForm.isMinor && (
                      <div className="complaint-grid">
                        <label>Padre, madre o representante
                          <input value={complaintForm.guardianName} onChange={e => updateComplaintField('guardianName', e.target.value)} required />
                        </label>
                        <label>Tipo de documento
                          <select value={complaintForm.guardianDocumentType} onChange={e => updateComplaintField('guardianDocumentType', e.target.value)}>
                            <option value="dni">DNI</option>
                            <option value="ce">Carné de extranjería</option>
                            <option value="pasaporte">Pasaporte</option>
                          </select>
                        </label>
                        <label>Número de documento
                          <input value={complaintForm.guardianDocumentNumber} onChange={e => updateComplaintField('guardianDocumentNumber', e.target.value)} required />
                        </label>
                      </div>
                    )}
                  </section>

                  <section>
                    <h3>2. Bien contratado</h3>
                    <div className="complaint-grid">
                      <label>Tipo
                        <select value={complaintForm.productType} onChange={e => updateComplaintField('productType', e.target.value as ComplaintFormState['productType'])}>
                          <option value="servicio">Servicio</option>
                          <option value="producto">Producto</option>
                        </select>
                      </label>
                      <label>Monto reclamado (S/)
                        <input type="number" min="0" step="0.01" value={complaintForm.claimedAmount} onChange={e => updateComplaintField('claimedAmount', e.target.value)} />
                      </label>
                      <label className="complaint-wide">Descripción del producto o servicio
                        <textarea value={complaintForm.productDescription} onChange={e => updateComplaintField('productDescription', e.target.value)} required />
                      </label>
                    </div>
                  </section>

                  <section>
                    <h3>3. Detalle de la reclamación</h3>
                    <div className="complaint-type-row">
                      <label className={complaintForm.complaintType === 'reclamo' ? 'complaint-choice complaint-choice-active' : 'complaint-choice'}>
                        <input type="radio" checked={complaintForm.complaintType === 'reclamo'} onChange={() => updateComplaintField('complaintType', 'reclamo')} />
                        <span><b>Reclamo</b>Disconformidad relacionada al producto o servicio.</span>
                      </label>
                      <label className={complaintForm.complaintType === 'queja' ? 'complaint-choice complaint-choice-active' : 'complaint-choice'}>
                        <input type="radio" checked={complaintForm.complaintType === 'queja'} onChange={() => updateComplaintField('complaintType', 'queja')} />
                        <span><b>Queja</b>Disconformidad no relacionada directamente al producto o servicio.</span>
                      </label>
                    </div>
                    <label>Detalle
                      <textarea value={complaintForm.detail} onChange={e => updateComplaintField('detail', e.target.value)} required />
                    </label>
                    <label>Pedido del consumidor
                      <textarea value={complaintForm.requestedAction} onChange={e => updateComplaintField('requestedAction', e.target.value)} required />
                    </label>
                    <label>Canal de respuesta
                      <select value={complaintForm.responseChannel} onChange={e => updateComplaintField('responseChannel', e.target.value as ComplaintFormState['responseChannel'])}>
                        <option value="email">Correo electrónico</option>
                        <option value="phone">Teléfono</option>
                        <option value="address">Domicilio</option>
                      </select>
                    </label>
                  </section>

                  <label className="complaint-check">
                    <input type="checkbox" checked={complaintForm.acceptsTruth} onChange={e => updateComplaintField('acceptsTruth', e.target.checked)} required />
                    Declaro que la información registrada es verdadera y autorizo su uso para atender esta hoja.
                  </label>

                  {complaintStatus === 'error' && <div className="complaint-error">{complaintError}</div>}

                  <div className="complaint-actions">
                    <small>El proveedor debe responder en un plazo máximo de 15 días hábiles.</small>
                    <button type="submit" disabled={complaintStatus === 'sending'}>
                      {complaintStatus === 'sending' ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                      {complaintStatus === 'sending' ? 'Registrando...' : 'Registrar hoja'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    )}

    </div>

    <footer className="site-footer">
      <div className="site-footer-grid">
        <div>
          <h2>RadarBI</h2>
          <p>
            Estudio de mercado para quien va a <strong>abrir un local</strong> o aún no sabe <strong>qué negocio poner</strong> en Perú.
            Mira la competencia y quién te trae clientes. <strong>100 créditos gratis</strong> para probar. Paquetes desde {PAID_PACKS_FROM}.
          </p>
        </div>
        <div>
          <h3>Soluciones</h3>
          <ul>
            <li>Estudio de mercado con inteligencia artificial</li>
            <li>Competencia y quién te trae clientes</li>
            <li>Abrir un local con más certeza</li>
            <li>Descubrir qué rubro conviene en una zona</li>
            <li>100 créditos gratis al crear tu cuenta</li>
            <li>Paquetes de créditos desde {PAID_PACKS_FROM}</li>
            <li>Mapa comercial de Lima y todo el Perú</li>
          </ul>
        </div>
        <div>
          <h3>Legal</h3>
          <ul>
            <li><button type="button" className="footer-link" onClick={() => setLegalModal('terms')}>Términos y Condiciones</button></li>
            <li><button type="button" className="footer-link" onClick={() => setLegalModal('privacy')}>Política de Privacidad</button></li>
            <li><button type="button" className="footer-link" onClick={() => window.dispatchEvent(new Event(GOOGLE_CONSENT_OPEN_EVENT))}>Configurar privacidad</button></li>
            <li><button type="button" className="footer-link footer-link-accent" onClick={() => setLegalModal('complaints')}>Libro de Reclamaciones</button></li>
          </ul>
        </div>
      </div>
      <div className="site-footer-bottom">
        <p>© {new Date().getFullYear()} RadarBI — Encuentra dónde poner tu negocio: competencia y quién te trae clientes.</p>
        <p>Estudio de mercado · Abrir un local · Otra sede · 100 créditos gratis · Paquetes desde {PAID_PACKS_FROM}</p>
      </div>
    </footer>
  </div>;
}
function Metric({ icon, label, value }: {icon:React.ReactNode; label:string; value:number|string}) { return <div className="metric"><div aria-hidden="true">{icon}</div><span>{label}</span><b>{value}</b></div>; }
// Set Google Client ID from env (Vite exposes VITE_ prefixed vars)
(window as any).__RADARBI_GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <AuthGate>
    {(user, logout, requireAuth) => <App authUser={user} onLogout={logout} requireAuth={requireAuth} />}
  </AuthGate>
);
